import { Plural, Trans, useLingui } from "@lingui/react/macro";
import { ApiError, apiFetch } from "@pontimed/api-client";
import {
  COUNTRY_FLAGS,
  COUNTRY_NAMES_EN,
  NEIGHBOR_COUNTRIES,
  SUPPORTED_COUNTRIES,
  isSupportedCountry,
} from "@pontimed/types";
import { cn } from "@pontimed/ui";
import { BeamFrame, Odometer, usePointerSpotlight } from "@pontimed/ui/puls";
import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import { AnimatePresence, type PanInfo, m } from "framer-motion";
import { createPortal } from "react-dom";
import {
  Calendar as CalendarIcon,
  CalendarDays,
  CalendarRange,
  Check,
  ChevronDown,
  Home as HomeIcon,
  Infinity as InfinityIcon,
  List as ListIcon,
  Loader2,
  Map as MapIcon,
  MapPin as MapPinIcon,
  Search as SearchIcon,
  SlidersHorizontal,
  Star as StarIcon,
  Wand2,
  X as XIcon,
  Zap,
} from "lucide-react";
import {
  Suspense,
  lazy,
  useCallback,
  useDeferredValue,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { useAuthModal } from "@/components/auth/auth-modal-context";
import { AppHeader } from "@/components/layout/app-header";
import { BottomTabBar } from "@/components/layout/bottom-tab-bar";
import { Footer } from "@/components/layout/footer";
import { ActiveFilterPills, type ActiveFilterPill } from "@/components/search/active-filter-pills";
import { useWhenCopy } from "@/components/search/guided-search/steps/step-when";
import { FavoritesOnlyToggle } from "@/components/search/favorites-only-toggle";
import {
  FacilityProfileModal,
  extractFacilityId,
} from "@/components/facility/facility-profile-modal";
import { ProviderProfileModal } from "@/components/provider/provider-profile-modal";
import { extractProviderId } from "@pontimed/provider-ui";
import { SavedSearchesDropdown } from "@/components/search/saved-searches-dropdown";
import { GeoBoundsChip } from "@/components/search/geo-bounds-chip";
import { GroupToggle, type GroupMode } from "@/components/search/group-toggle";
import { CompareBar } from "@/components/search/compare-bar";
import { CompareModal } from "@/components/search/compare-modal";
import { AiRouteChips } from "@/components/search/ai-route-chips";
import { EmptyStateSuggestions } from "@/components/search/empty-state-suggestions";
import {
  GroupedProviderCard,
  groupProviderResults,
} from "@/components/search/grouped-provider-card";
import { ResultCard } from "@/components/search/result-card";
import { ResultCardSkeletonList } from "@/components/search/result-card-skeleton";
import {
  createThreadHoverChannel,
  ResultPinThread,
  type ThreadMap,
} from "@/components/search/result-pin-thread";
import { SaveSearchModal } from "@/components/search/save-search-modal";
import {
  HeroChoiceChip,
  SectionPill,
  SidebarFilterSection,
} from "@/components/search/_find/sidebar-primitives";
import { SidebarMoreFilters } from "@/components/search/_find/sidebar-more-filters";
import { MobileSearchHero } from "@/components/search/_find/mobile-search-hero";
import { SearchAreaButton } from "@/components/search/_find/search-area-button";
import { ModeSwitch } from "@/components/search/_find/mode-switch";
import {
  SearchFiltersModal,
  type InsuranceFilter,
  type SearchFiltersState,
} from "@/components/search/search-filters-modal";
import { SortDropdown, type SortOption } from "@/components/search/sort-dropdown";
import {
  PromoBanner,
  PromoBannerTestGrid,
  bumpPromoVisitCount,
} from "@/components/search/promo-banner";
import { capture } from "@pontimed/analytics/client";
import { trackSearchAppearances } from "@/lib/analytics/use-track";
import {
  type AiSuggestion,
  UNIVERSAL_SEARCH_KEEP_OPEN_ATTR,
  UniversalSearch,
  type UniversalSearchHandle,
} from "@/components/search/universal-search";
import { ViewSwipeHandle } from "@/components/search/view-swipe-handle";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { CountryPickerSheet } from "@/components/country/country-picker-sheet";
import { useCountriesQuery } from "@/components/country/use-countries-query";
import { useCompareSelection } from "@/lib/hooks/use-compare-selection";
import { useCountriesGeoJson } from "@/lib/hooks/use-country-geojson";
import { useCountryRouteSync } from "@/lib/hooks/use-country-route-sync";
import { useEmptyStateSuggestions } from "@/lib/hooks/use-empty-state-suggestions";
import { useFavoriteProviderIds } from "@/lib/hooks/use-favorite-providers";
import { useMediaQuery } from "@/lib/hooks/use-media-query";
import { useTheme } from "@/lib/theme/theme-provider";
import { showToast } from "@/lib/ui/toast";
import {
  type CityOption,
  cityNameFromSlug,
  getCityDisplayName,
  parseCitySlug,
  toCitySlug,
  useCities,
} from "@/lib/hooks/use-cities";
import { saveFindSearch } from "@/lib/hooks/use-find-url-memory";
import { useResultsKeyboardNav } from "@/lib/hooks/use-results-keyboard-nav";
import {
  Slider,
  SliderControl,
  SliderIndicator,
  SliderThumb,
  SliderTrack,
} from "@/components/ui/slider";
import { LOCALE_FLAG } from "@/components/layout/locale-switcher";
import { PRIMARY_LOCALES } from "@/i18n/i18n";
import { readBrowsingCountryFromCookie } from "@/state/country";
import { getServiceDisplayName, useServices } from "@/lib/hooks/use-services";
import {
  getSpecialtyDisplayName,
  type SpecialtyOption,
  useSpecialties,
} from "@/lib/hooks/use-specialties";
import { preprocessQuery } from "@/lib/search/query-preprocessor";
import type { ConsultationType } from "@/lib/search/wizard-types";
import type { NextSlot, SearchApiResponse, SearchResult } from "@/lib/search/types";

/* ────────── Lazy map (defers the map bundle until view=map) ────────── */
const SearchMap = lazy(() => import("@/components/search/search-map"));
import type { MapBounds } from "@/components/search/search-map";
import type { Feature } from "geojson";

/* ────────── URL search-param contract ────────── */

/**
 * Cross-border scope for the search.
 *
 *   local  → only the current country (default; matches /api/search mode=country)
 *   cross  → current country + supported neighbors (NEIGHBOR_COUNTRIES); maps
 *            to /api/search mode=nearby&countries=CSV
 *   global → no country filter, hasVideo=true forced — only telemed providers
 *            anywhere in the world. Maps to /api/search mode=online (no country).
 */
type SearchScope = "local" | "cross" | "global";

interface SearchSearchParams {
  q?: string;
  /** ISO alpha-2 country code (uppercase). Defaults to "RO". */
  country?: string;
  /** Single specialty code. MVP single-select; V1.1 multi via ?specialties=CSV. */
  specialty?: string;
  /** City slug `geonameId__name` or bare UUID/geonameId. */
  city?: string;
  /** Single service code. MVP single-select. */
  service?: string;
  insurance?: InsuranceFilter;
  /** Consultation type — accepts both "in_person" (filter modal / guided
   *  wizard contract) and "in_office" (legacy / Meilisearch facet value).
   *  apps/api/search.ts normalizes "in_person" → "in_office" before building
   *  the filter, so both work end-to-end. */
  consultationType?: "in_office" | "in_person" | "video" | "home_visit";
  /** Min average rating. */
  ratingMin?: "3.5" | "4.0" | "4.5";
  /** Sort key. Maps to /api/search `sort` param. "recommended" = no `sort` sent. */
  sort?: SortOption;
  /** Default "list" — view=map activates desktop split + mobile map overlay. */
  view?: "list" | "map";
  /** Provider sheet deep-link. Opens `<ProviderProfileModal>` over the
   *  results list. Format: bare UUID, Meili composite, or pretty slug. */
  provider?: string;
  /** Facility sheet deep-link. Opens `<FacilityProfileModal>` over the
   *  results list. Format: bare UUID or pretty slug `name-<uuid>`. */
  facility?: string;
  /** Auto-open the BookSheet inside the provider modal as soon as the
   *  profile data resolves. Used by rebook / book-follow-up CTAs so the
   *  user lands directly inside the booking flow. Implies scroll-to-#booking
   *  for the parent profile sheet beneath it. */
  book?: "1";
  /** Facility context for the auto-opened BookSheet — pre-selects this
   *  facility in the BookingFlow when `book=1` is set. */
  facilityId?: string;
  /** Cross-border scope. Defaults to "local". */
  scope?: SearchScope;
  /** CSV of selected neighbor countries (UPPERCASE ISO). Only meaningful when
   *  `scope === "cross"`. Empty/null = no neighbors selected → behaves like local. */
  countries?: string;
  /** Group mode. "grouped" collapses multi-facility providers; "facility" splits them. */
  group?: GroupMode;
  /** Favorites-only toggle. "1" filters results to user-favorited entries. */
  fav?: "1";
  /** Map bounding box — written by "Search this area" (Pas 3). */
  north?: string;
  south?: string;
  east?: string;
  west?: string;
  /** L2 boolean filters extracted by preprocessQuery. Forwarded to /api/search
   *  verbatim so they survive as URL state across reloads. UI for toggling them
   *  ships in Pas 6 (filter modal extras). */
  acceptsCas?: "true";
  is24h?: "true";
  isOpenNow?: "true";
  hasParking?: "true";
  hasWheelchairAccess?: "true";
  hasElevator?: "true";
  hasAccessibleRestroom?: "true";
  hasAirConditioning?: "true";
  hasWifi?: "true";
  hasChildrenArea?: "true";
  hasBreastfeedingArea?: "true";
  hasPublicTransport?: "true";
  hasHomeCollection?: "true";
  hasDelivery?: "true";
  isVerified?: "true";
  emergencyServices?: "true";
  /** Provider gender filter. */
  gender?: "F" | "M";
  /** Spoken-language filter (ISO short code, single — set by preprocessQuery). */
  language?: string;
  /** Multi-language filter (CSV "en,fr,de"). */
  languages?: string;
  /** Max consultation fee (numeric string in URL). 0 = "free". */
  consultationFee?: string;
  /** Multi-specialty (CSV "CAR,DER"). Legacy supports both `specialty` (single)
   *  and `specialties` (CSV); /api/search forwards either as-is. */
  specialties?: string;
  /** Multi-service (CSV). */
  services?: string;
  /** Patient-audience tags (CSV — see filter modal AUDIENCE_OPTIONS). */
  audience?: string;
  /** Min/max consultation price filters from the L2 wizard. */
  minPrice?: string;
  maxPrice?: string;
  /** Result-tab filter — derives providerType + subtype on /api/search.
   *  Values: "all" | "provider" | "hospital" | "clinic" | "medical_center" |
   *  "dental_clinic" | "laboratory" | "pharmacy" | "imaging" | ... */
  tab?: string;
  /** Lift the default "facility with 0 providers is hidden" filter. Set by
   *  the Servicii Medicale tiles (hospital/clinic/polyclinic/dental_clinic +
   *  pharmacy/lab/...) so directory-imported rows surface even before any
   *  doctor affiliates with them. */
  includeEmpty?: "true";
  /** Near-me geo search — when nearMe=true, lat/lng/distance kick in and
   *  override city. Distance is km (default 10 in legacy). */
  nearMe?: "true";
  lat?: string;
  lng?: string;
  distance?: string;
  /** Home-visit address — used by the filter modal's home-visit picker so
   *  existing URLs round-trip. `homeCity`/`homeVisitCity` are forwarded to
   *  /api/search verbatim. `hasHomeVisit` flips the consultationType branch.
   *  `homeTag` is the full formatted address (street + number + city) shown
   *  in filter pills + the picker input — `homeCity` is just the city
   *  fragment kept around for the API contract. */
  hasHomeVisit?: "true";
  homeCity?: string;
  homeVisitCity?: string;
  homeTag?: string;
  /** Guided-search wizard URL state. `wizard=1` opens the modal sheet,
   *  `step=N` selects the step (0..5). Source of truth for the wizard so
   *  it survives reload + browser back. */
  wizard?: "1";
  step?: string;
  /** Wizard-emitted forwarded params (kept loose — they round-trip into the
   *  filter modal once the wizard navigates here). */
  kind?: string;
  category?: string;
  hasVideo?: "true";
  mode?: string;
  type?: string;
  availability?: string;
  dateFrom?: string;
  dateTo?: string;
  /** Time-of-day quick-pick — mirrors the guided wizard step 4 contract.
   *  `custom` activates the date-range branch; `dateFrom`/`dateTo` only carry
   *  meaning while `when === "custom"`. Backend filtering lands when the
   *  booking-availability data ships into Meilisearch; the URL state is
   *  already authoritative so the UI keeps it round-tripping through reloads. */
  when?: "now" | "today" | "tomorrow" | "this_week" | "custom";
}

const DEFAULT_COUNTRY = "RO";
const VALID_SCOPES: SearchScope[] = ["local", "cross", "global"];

/** Sidebar + modal price slider upper bound. Matches the modal's
 *  `priceCeiling` default so summary / pill labels round-trip identically. */
const PRICE_CEILING = 500;

/** Geocode response row used by the sidebar's home-visit address picker.
 *  Mirrors the wizard's `GeoResult` shape — keep in sync with /api/geocode. */
interface HomeAddrResult {
  formattedAddress: string;
  lat: number;
  lng: number;
  city?: string;
  county?: string;
}

/** /api/ai/symptom-router suggestion row — mirrors the modal's local
 *  `Suggestion` interface. Keep in sync with the AI endpoint contract. */
interface SymptomSuggestion {
  specialtyCode: string;
  specialtyName: string;
  confidence: number;
  reason: string;
}

/** Capital coordinates for the supported countries (CF middleware list).
 *  Used as the map fallback center when the user has not picked a city —
 *  switching country in the header otherwise leaves the map stuck on the
 *  prior country's capital. */
const COUNTRY_CENTERS: Record<string, [number, number]> = {
  RO: [44.4268, 26.1025], // Bucharest
  BG: [42.6977, 23.3219], // Sofia
  DE: [52.52, 13.405], // Berlin
  HU: [47.4979, 19.0402], // Budapest
  RS: [44.7866, 20.4489], // Belgrade
  HR: [45.815, 15.9819], // Zagreb
  CZ: [50.0755, 14.4378], // Prague
  SK: [48.1486, 17.1077], // Bratislava
  SI: [46.0569, 14.5058], // Ljubljana
  AT: [48.2082, 16.3738], // Vienna
  CH: [46.948, 7.4474], // Bern
  NL: [52.3676, 4.9041], // Amsterdam
  BE: [50.8503, 4.3517], // Brussels
  PL: [52.2297, 21.0122], // Warsaw
  GR: [37.9838, 23.7275], // Athens
  IE: [53.3498, -6.2603], // Dublin
  FR: [48.8566, 2.3522], // Paris
  IT: [41.9028, 12.4964], // Rome
  ES: [40.4168, -3.7038], // Madrid
  LV: [56.9496, 24.1052], // Riga
  LT: [54.6872, 25.2797], // Vilnius
};

const VALID_INSURANCE: InsuranceFilter[] = ["all", "cas", "private", "direct"];
const VALID_CONSULT: NonNullable<SearchSearchParams["consultationType"]>[] = [
  "in_office",
  "in_person",
  "video",
  "home_visit",
];
const VALID_RATINGS: NonNullable<SearchSearchParams["ratingMin"]>[] = ["3.5", "4.0", "4.5"];
const VALID_SORTS: SortOption[] = [
  "recommended",
  "distance:asc",
  "ratingAvg:desc",
  "consultationFee:asc",
];
const VALID_GROUP: GroupMode[] = ["grouped", "facility"];
const VALID_GENDER: NonNullable<SearchSearchParams["gender"]>[] = ["F", "M"];

/** L2 boolean filter URL keys — kept as `"true"` string in URL when active. */
const L2_BOOLEAN_KEYS = [
  "acceptsCas",
  "is24h",
  "isOpenNow",
  "hasParking",
  "hasWheelchairAccess",
  "hasElevator",
  "hasAccessibleRestroom",
  "hasAirConditioning",
  "hasWifi",
  "hasChildrenArea",
  "hasBreastfeedingArea",
  "hasPublicTransport",
  "hasHomeCollection",
  "hasDelivery",
  "isVerified",
  "emergencyServices",
] as const;

function pickEnum<T extends string>(value: unknown, allowed: readonly T[]): T | undefined {
  return typeof value === "string" && allowed.includes(value as T) ? (value as T) : undefined;
}

function pickBoolString(value: unknown): "true" | undefined {
  return value === "true" || value === true ? "true" : undefined;
}

/**
 * Servicii Medicale verticals — facility/business types that map to
 * `?tab=` + `?category=` + `?includeEmpty=true` URL state. Mirrors the
 * subset of WizardVertical (lib/search/wizard-types.ts) that hits facility
 * branches of the providers index OR the businesses index.
 *
 * Order = render order in the sidebar Tip filter chips.
 *  - `tab` is the URL param that drives the search tab (sometimes diverges
 *    from key, e.g. polyclinic → tab=medical_center, lab → tab=laboratory).
 *  - `category` is the facility category (also sometimes differs, e.g.
 *    equipment → category=equipment_supplier).
 */
const SERVICII_VERTICALS = [
  { key: "hospital", tab: "hospital", category: "hospital", kind: "providers" },
  { key: "clinic", tab: "clinic", category: "clinic", kind: "providers" },
  {
    key: "polyclinic",
    tab: "medical_center",
    category: "medical_center",
    kind: "providers",
  },
  {
    key: "dental_clinic",
    tab: "dental_clinic",
    category: "dental_clinic",
    kind: "providers",
  },
  { key: "pharmacy", tab: "pharmacy", category: "pharmacy", kind: "businesses" },
  { key: "lab", tab: "laboratory", category: "laboratory", kind: "businesses" },
  { key: "imaging", tab: "imaging", category: "imaging", kind: "businesses" },
  { key: "optical", tab: "optical", category: "optical", kind: "businesses" },
  { key: "homecare", tab: "homecare", category: "homecare", kind: "businesses" },
  {
    key: "medical_transport",
    tab: "medical_transport",
    category: "medical_transport",
    kind: "businesses",
  },
  {
    key: "equipment",
    tab: "equipment",
    category: "equipment_supplier",
    kind: "businesses",
  },
] as const;

type ServiciiVertical = (typeof SERVICII_VERTICALS)[number];

const SERVICII_TABS: readonly string[] = SERVICII_VERTICALS.map((v) => v.tab);

/** True when the current `?tab=` matches any Servicii Medicale vertical. */
function isServiciiTab(tab: string | undefined): boolean {
  return !!tab && SERVICII_TABS.includes(tab);
}

/* ─────────── Saved home address (localStorage) ───────────
   Persists the last picked home-visit address across sessions so that
   switching to consultationType=home_visit auto-restores it instead of
   making the user re-search Google for the same place each time. Cleared
   only via the explicit "X" on the address input (clearHomeAddress).
   Storage shape is forward-compatible: plain JSON object, parsed
   defensively. */
const SAVED_HOME_ADDRESS_KEY = "pontimed.find.savedHomeAddress.v1";

interface SavedHomeAddress {
  lat: number;
  lng: number;
  city: string | null;
  formattedAddress: string;
}

function loadSavedHomeAddress(): SavedHomeAddress | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(SAVED_HOME_ADDRESS_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<SavedHomeAddress>;
    if (
      typeof parsed?.lat !== "number" ||
      typeof parsed?.lng !== "number" ||
      typeof parsed?.formattedAddress !== "string"
    ) {
      return null;
    }
    return {
      lat: parsed.lat,
      lng: parsed.lng,
      city: typeof parsed.city === "string" ? parsed.city : null,
      formattedAddress: parsed.formattedAddress,
    };
  } catch {
    return null;
  }
}

function saveHomeAddress(addr: SavedHomeAddress): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(SAVED_HOME_ADDRESS_KEY, JSON.stringify(addr));
  } catch {
    /* storage unavailable — skip silently */
  }
}

function clearSavedHomeAddress(): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(SAVED_HOME_ADDRESS_KEY);
  } catch {
    /* storage unavailable — skip silently */
  }
}

export const Route = createFileRoute("/_anon/find")({
  validateSearch: (search: Record<string, unknown>): SearchSearchParams => ({
    q: typeof search.q === "string" ? search.q : undefined,
    country: typeof search.country === "string" ? search.country.toUpperCase() : undefined,
    specialty: typeof search.specialty === "string" ? search.specialty : undefined,
    city: typeof search.city === "string" ? search.city : undefined,
    service: typeof search.service === "string" ? search.service : undefined,
    insurance: pickEnum(search.insurance, VALID_INSURANCE),
    consultationType: pickEnum(search.consultationType, VALID_CONSULT),
    ratingMin: pickEnum(search.ratingMin, VALID_RATINGS),
    sort: pickEnum(search.sort, VALID_SORTS),
    view: search.view === "map" ? "map" : search.view === "list" ? "list" : undefined,
    provider: typeof search.provider === "string" ? search.provider : undefined,
    facility: typeof search.facility === "string" ? search.facility : undefined,
    book: search.book === "1" ? "1" : undefined,
    facilityId: typeof search.facilityId === "string" ? search.facilityId : undefined,
    scope: pickEnum(search.scope, VALID_SCOPES),
    countries: typeof search.countries === "string" ? search.countries : undefined,
    group: pickEnum(search.group, VALID_GROUP),
    fav: search.fav === "1" ? "1" : undefined,
    acceptsCas: pickBoolString(search.acceptsCas),
    is24h: pickBoolString(search.is24h),
    isOpenNow: pickBoolString(search.isOpenNow),
    hasParking: pickBoolString(search.hasParking),
    hasWheelchairAccess: pickBoolString(search.hasWheelchairAccess),
    hasElevator: pickBoolString(search.hasElevator),
    hasAccessibleRestroom: pickBoolString(search.hasAccessibleRestroom),
    hasAirConditioning: pickBoolString(search.hasAirConditioning),
    hasWifi: pickBoolString(search.hasWifi),
    hasChildrenArea: pickBoolString(search.hasChildrenArea),
    hasBreastfeedingArea: pickBoolString(search.hasBreastfeedingArea),
    hasPublicTransport: pickBoolString(search.hasPublicTransport),
    hasHomeCollection: pickBoolString(search.hasHomeCollection),
    hasDelivery: pickBoolString(search.hasDelivery),
    isVerified: pickBoolString(search.isVerified),
    emergencyServices: pickBoolString(search.emergencyServices),
    gender: pickEnum(search.gender, VALID_GENDER),
    language: typeof search.language === "string" ? search.language : undefined,
    languages: typeof search.languages === "string" ? search.languages : undefined,
    consultationFee:
      typeof search.consultationFee === "string"
        ? search.consultationFee
        : typeof search.consultationFee === "number"
          ? String(search.consultationFee)
          : undefined,
    specialties: typeof search.specialties === "string" ? search.specialties : undefined,
    services: typeof search.services === "string" ? search.services : undefined,
    audience: typeof search.audience === "string" ? search.audience : undefined,
    minPrice:
      typeof search.minPrice === "string"
        ? search.minPrice
        : typeof search.minPrice === "number"
          ? String(search.minPrice)
          : undefined,
    maxPrice:
      typeof search.maxPrice === "string"
        ? search.maxPrice
        : typeof search.maxPrice === "number"
          ? String(search.maxPrice)
          : undefined,
    tab: typeof search.tab === "string" ? search.tab : undefined,
    includeEmpty: pickBoolString(search.includeEmpty),
    nearMe: pickBoolString(search.nearMe),
    lat:
      typeof search.lat === "string"
        ? search.lat
        : typeof search.lat === "number"
          ? String(search.lat)
          : undefined,
    lng:
      typeof search.lng === "string"
        ? search.lng
        : typeof search.lng === "number"
          ? String(search.lng)
          : undefined,
    distance:
      typeof search.distance === "string"
        ? search.distance
        : typeof search.distance === "number"
          ? String(search.distance)
          : undefined,
    hasHomeVisit: pickBoolString(search.hasHomeVisit),
    homeCity: typeof search.homeCity === "string" ? search.homeCity : undefined,
    homeTag: typeof search.homeTag === "string" ? search.homeTag : undefined,
    homeVisitCity: typeof search.homeVisitCity === "string" ? search.homeVisitCity : undefined,
    north: typeof search.north === "string" ? search.north : undefined,
    south: typeof search.south === "string" ? search.south : undefined,
    east: typeof search.east === "string" ? search.east : undefined,
    west: typeof search.west === "string" ? search.west : undefined,
    // TanStack Router's default `parseSearch` is JSON-aware: `?wizard=1`
    // arrives as `{ wizard: 1 }` (number, not string). Accept both forms so
    // the wizard's URL→state binding (`String(search.wizard) === "1"`) sees
    // a stable value and the param survives validateSearch's strip pass.
    wizard:
      search.wizard === "1" || search.wizard === 1 || search.wizard === true ? "1" : undefined,
    step:
      typeof search.step === "string"
        ? search.step
        : typeof search.step === "number"
          ? String(search.step)
          : undefined,
    kind: typeof search.kind === "string" ? search.kind : undefined,
    category: typeof search.category === "string" ? search.category : undefined,
    hasVideo: pickBoolString(search.hasVideo),
    mode: typeof search.mode === "string" ? search.mode : undefined,
    type: typeof search.type === "string" ? search.type : undefined,
    availability: typeof search.availability === "string" ? search.availability : undefined,
    dateFrom: typeof search.dateFrom === "string" ? search.dateFrom : undefined,
    dateTo: typeof search.dateTo === "string" ? search.dateTo : undefined,
  }),
  // Guarantee `?country=` is in the URL before the component mounts and the
  // first /api/search call fires. Cascade: URL → cookie (set by CF Pages
  // middleware from cf-ipcountry, or a previous header-switcher pick) →
  // DEFAULT_COUNTRY. Without this redirect the initial search runs against
  // the route fallback while the URL stays naked, which makes shared links
  // ambiguous and breaks the atom→URL sync's "stale URL" detection.
  beforeLoad: ({ search }) => {
    if (search.country) return;
    const fromCookie = readBrowsingCountryFromCookie();
    const country = (fromCookie ?? DEFAULT_COUNTRY).toUpperCase();
    throw redirect({
      to: "/find",
      search: { ...search, country },
      replace: true,
    });
  },
  component: SearchPage,
});

/* ────────── Data hook ────────── */

interface UseSearchDataResult {
  results: SearchResult[];
  total: number;
  loading: boolean;
  error: string | null;
  /** Per-filter result counts when each filter is dropped — drives empty-state
   *  suggestions ("any specialty → 412 results"). Empty until first response. */
  disjunctiveTotals: Record<string, number>;
  /** Per-city counts when city filter is dropped — drives "try nearby city"
   *  suggestions in the empty state. */
  cityFacet?: Record<string, number>;
}

function useSearchData(params: SearchSearchParams): UseSearchDataResult {
  const [results, setResults] = useState<SearchResult[]>([]);
  const [total, setTotal] = useState(0);
  const [disjunctiveTotals, setDisjunctiveTotals] = useState<Record<string, number>>({});
  const [cityFacet, setCityFacet] = useState<Record<string, number> | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  // Sent to /api/search so provider specialties come back in the UI language
  // (the index holds them in Romanian); part of the key so a switch refetches.
  const { i18n } = useLingui();
  const locale = i18n.locale;

  // Stable key so unrelated URL changes (e.g. opening provider sheet) don't
  // refetch. Excludes `view`, `provider`, `group`, `fav` (UI-only). Includes
  // L2 boolean filters + gender/language/consultationFee so preprocessQuery
  // intent extraction round-trips end-to-end.
  const l2Snapshot = L2_BOOLEAN_KEYS.map((k) => params[k] ?? "").join("|");
  const queryKey = useMemo(() => {
    const k: Record<string, string | undefined> = {
      q: params.q,
      country: params.country ?? DEFAULT_COUNTRY,
      specialty: params.specialty,
      specialties: params.specialties,
      city: params.city,
      service: params.service,
      services: params.services,
      insurance: params.insurance,
      consultationType: params.consultationType,
      ratingMin: params.ratingMin,
      sort: params.sort,
      scope: params.scope ?? "local",
      countries: params.countries,
      gender: params.gender,
      language: params.language,
      languages: params.languages,
      audience: params.audience,
      consultationFee: params.consultationFee,
      minPrice: params.minPrice,
      maxPrice: params.maxPrice,
      tab: params.tab,
      includeEmpty: params.includeEmpty,
      nearMe: params.nearMe,
      lat: params.lat,
      lng: params.lng,
      distance: params.distance,
      homeCity: params.homeCity,
      homeVisitCity: params.homeVisitCity,
      north: params.north,
      south: params.south,
      east: params.east,
      west: params.west,
      l2: l2Snapshot,
      locale,
    };
    return JSON.stringify(k);
  }, [
    params.q,
    params.country,
    params.specialty,
    params.specialties,
    params.city,
    params.service,
    params.services,
    params.insurance,
    params.consultationType,
    params.ratingMin,
    params.sort,
    params.scope,
    params.countries,
    params.gender,
    params.language,
    params.languages,
    params.audience,
    params.consultationFee,
    params.minPrice,
    params.maxPrice,
    params.tab,
    params.nearMe,
    params.lat,
    params.lng,
    params.distance,
    params.homeCity,
    params.homeVisitCity,
    params.north,
    params.south,
    params.east,
    params.west,
    l2Snapshot,
    locale,
  ]);

  useEffect(() => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    const country = (params.country ?? DEFAULT_COUNTRY).toUpperCase();
    const scope: SearchScope = params.scope ?? "local";

    const sp = new URLSearchParams();
    // Match legacy 1:1 — see src/app/[locale]/[country]/search/search-client.tsx
    // doSearch (lines 755-875). Limit + includeNextSlots are required for the
    // FacilityBlock slot chips ("Soonest"/today/tomorrow) to render.
    sp.set("limit", "100");
    sp.set("includeNextSlots", "true");

    if (scope === "global") {
      sp.set("mode", "online");
    } else if (scope === "cross") {
      // URL `countries` CSV holds the user-selected neighbors (per-chip toggle
      // in the hero popover). Empty CSV → just the current country, same as
      // local — chip mode is purely UI in that edge case.
      const fromUrl = params.countries
        ?.split(",")
        .map((s) => s.trim().toUpperCase())
        .filter(Boolean);
      const codes = [country, ...(fromUrl ?? [])];
      sp.set("mode", "nearby");
      sp.set("countries", codes.join(","));
    } else {
      sp.set("mode", "country");
      sp.set("country", country);
    }

    if (params.q) sp.set("q", params.q);
    // `kind` switches the index searched server-side: "businesses" routes the
    // query to the BUSINESSES_INDEX (Business + BusinessBranch docs covering
    // pharmacy / lab / imaging / optical chains). Without this forward, the
    // pharmacy tab silently fell back to PROVIDERS_INDEX which never contains
    // pharmacy rows → 0 hits even after a fresh scrape + Meili resync.
    if (params.kind) sp.set("kind", params.kind);
    // Forward category for the businesses branch — without this the pharmacy
    // tab and the lab tab returned the same rows because the API saw no
    // category filter and the businesses search ran wide-open. (Providers
    // branch derives category internally from `tab`/`subtype`, so this is
    // mostly a no-op there.)
    if (params.category) sp.set("category", params.category);
    // Single-specialty `specialty` + multi-CSV `specialties` BOTH supported by
    // /api/search — forward whichever is present (legacy line 803).
    if (params.specialty) sp.set("specialty", params.specialty);
    if (params.specialties) sp.set("specialties", params.specialties);
    sp.set("locale", locale);
    if (params.service) sp.set("service", params.service);
    if (params.services) sp.set("services", params.services);
    if (params.insurance && params.insurance !== "all") sp.set("insurance", params.insurance);
    if (params.consultationType) sp.set("consultationType", params.consultationType);
    if (params.consultationType === "video" || scope === "global") sp.set("hasVideo", "true");
    if (params.ratingMin) sp.set("ratingMin", params.ratingMin);
    if (params.sort && params.sort !== "recommended") sp.set("sort", params.sort);
    if (params.gender) sp.set("gender", params.gender);
    if (params.language) sp.set("language", params.language);
    if (params.languages) sp.set("languages", params.languages);
    if (params.audience) sp.set("audience", params.audience);
    if (params.consultationFee !== undefined) sp.set("consultationFee", params.consultationFee);
    if (params.minPrice) sp.set("minPrice", params.minPrice);
    if (params.maxPrice) sp.set("maxPrice", params.maxPrice);
    if (params.homeCity) sp.set("homeCity", params.homeCity);
    if (params.homeVisitCity) sp.set("homeVisitCity", params.homeVisitCity);
    for (const key of L2_BOOLEAN_KEYS) {
      if (params[key] === "true") sp.set(key, "true");
    }

    // Tab → providerType + subtype (legacy line 845-855). When the URL lacks
    // an explicit providerType but `tab` is set, derive: "provider" → solo
    // doctor; anything else → facility with subtype = tab value.
    if (params.tab) {
      if (params.tab === "provider") {
        sp.set("providerType", "provider");
      } else if (params.tab !== "all") {
        sp.set("providerType", "facility");
        // The index stores medical_center facilities as subtype "polyclinic"
        // (mapCategoryToSubtype in apps/api/src/lib/search/sync-providers.ts
        // and apps/workers/src/meili/sync-facility.ts); the tab keeps the DB
        // category name, so the polyclinic vertical matched nothing.
        sp.set("subtype", params.tab === "medical_center" ? "polyclinic" : params.tab);
      }
    }

    // includeEmpty → forwarded to /api/search so directory-imported facilities
    // (providerCount = 0) surface in the Servicii Medicale tiles. Without this
    // forward the API's default `(providerType != "facility" OR providerCount > 0)`
    // filter would hide them.
    if (params.includeEmpty === "true") sp.set("includeEmpty", "true");

    // Geo: near-me overrides city. Bounds are independent (sent alongside
    // anything else when "Search this area" wrote them to the URL).
    if (params.nearMe === "true" && params.lat && params.lng) {
      sp.set("lat", params.lat);
      sp.set("lng", params.lng);
      sp.set("radiusKm", params.distance ?? "10");
    } else if (params.city) {
      sp.set("cityId", parseCitySlug(params.city));
    }
    if (params.north && params.south && params.east && params.west) {
      sp.set("north", params.north);
      sp.set("south", params.south);
      sp.set("east", params.east);
      sp.set("west", params.west);
    }

    setLoading(true);
    setError(null);

    apiFetch
      .get<SearchApiResponse>(`/api/search?${sp.toString()}`, { signal: controller.signal })
      .then((data) => {
        setResults(data.hits ?? []);
        setTotal(data.total ?? 0);
        setDisjunctiveTotals((data.disjunctiveTotals ?? {}) as Record<string, number>);
        const dCity = data.disjunctiveFacets?.city;
        setCityFacet(dCity && Object.keys(dCity).length > 0 ? dCity : undefined);
      })
      .catch((err: unknown) => {
        if (err instanceof DOMException && err.name === "AbortError") return;
        if (err instanceof ApiError) {
          setError(`HTTP ${err.status}`);
        } else {
          setError(err instanceof Error ? err.message : "Search failed");
        }
        setResults([]);
        setTotal(0);
        setDisjunctiveTotals({});
        setCityFacet(undefined);
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });

    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryKey]);

  return { results, total, loading, error, disjunctiveTotals, cityFacet };
}

/* ────────── Hero headings — random per session, mix of "search" / "book" /
     "brand" intents so the page reads differently across visits without
     drifting too far from the core value prop. Picked once on mount via
     `useState` initializer (stable per session, no re-shuffle on re-render). */
const HERO_HEADINGS = [
  <Trans>Find the best doctor in your city.</Trans>,
  <Trans>Find the right doctor in seconds.</Trans>,
  <Trans>The right specialist, closer than you think.</Trans>,
  <Trans>Book the best doctor in your city.</Trans>,
  <Trans>Book a consultation today.</Trans>,
  <Trans>Reserve a consultation in 30 seconds.</Trans>,
  <Trans>Book when it suits you, online or in person.</Trans>,
  <Trans>Your health, hassle-free.</Trans>,
  <Trans>From symptom to appointment, in 1 minute.</Trans>,
  <Trans>Verified doctors. Instant bookings. Zero phone calls.</Trans>,
  <Trans>The right doctor, when you need one.</Trans>,
];

/** Result-list entrance stagger (Puls): 40ms steps, first 8 cards only. The
 *  delay feeds the cards' mount fade/slide — their `layout` reorder timing
 *  and the list's AnimatePresence behaviour are untouched. */
const ENTER_STAGGER_S = 0.04;
const ENTER_STAGGER_MAX = 8;
function enterDelayFor(index: number): number {
  return index < ENTER_STAGGER_MAX ? index * ENTER_STAGGER_S : 0;
}

/* ────────── Main page ────────── */

function SearchPage() {
  const { t, i18n } = useLingui();
  const whenCopy = useWhenCopy();
  const search = Route.useSearch();
  const navigate = useNavigate();

  // Random hero heading — picked once at mount so it stays stable through
  // re-renders / filter updates / route param writes. Same node is rendered
  // on both mobile and desktop for visual consistency within the session.
  const [heroHeadingIdx] = useState(() => Math.floor(Math.random() * HERO_HEADINGS.length));
  const heroHeading = HERO_HEADINGS[heroHeadingIdx];

  // Bump the cross-promo visit counter exactly once per /find mount. The
  // banner reads this counter to gate visibility (visit 1 + every 3rd after).
  // Doing it here (not inside PromoBanner) avoids double-counting when
  // multiple banner instances render on the same page.
  useEffect(() => {
    bumpPromoVisitCount();
  }, []);

  // Cold-load deep-link guard — when the user lands directly on
  // `/find?provider=<id>` or `/find?facility=<id>` (external link, share,
  // universal link from web SSR), make sure the browser back-button takes
  // them to a clean `/find` (closing the modal) instead of bouncing them
  // out of the app entirely. We detect "fresh" loads via empty referrer or
  // single-entry history and rewrite the history stack to:
  //   [clean /find, deep-link /find?...]
  // TanStack Router parses the URL on each render but doesn't observe
  // silent `replaceState`/`pushState`, so we can do this without triggering
  // a re-render or duplicate fetch. Browser back → popstate → cursor moves
  // to the clean entry → router re-reads URL → modal closes naturally.
  const coldLoadHandledRef = useRef(false);
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (coldLoadHandledRef.current) return;
    coldLoadHandledRef.current = true;
    const params = new URLSearchParams(window.location.search);
    if (!params.has("provider") && !params.has("facility")) return;
    const sameOrigin =
      document.referrer !== "" && document.referrer.startsWith(window.location.origin);
    const looksFresh = window.history.length <= 1 || !sameOrigin;
    if (!looksFresh) return;
    const dirtyUrl = window.location.pathname + window.location.search + window.location.hash;
    const clean = new URLSearchParams(params);
    clean.delete("provider");
    clean.delete("facility");
    const cleanQs = clean.toString();
    const cleanUrl =
      window.location.pathname + (cleanQs ? `?${cleanQs}` : "") + window.location.hash;
    window.history.replaceState(window.history.state, "", cleanUrl);
    window.history.pushState(window.history.state, "", dirtyUrl);
  }, []);

  // Memoize the live /find search state in sessionStorage so a tab away to
  // /account/* and back lands on the same filters (read by the Find link
  // in the sidenav, mobile bottom-tab bar, dashboard tile). Wizard entry
  // state is skipped inside `saveFindSearch` — see use-find-url-memory.ts.
  useEffect(() => {
    saveFindSearch(search);
  }, [search]);
  // Resolved theme drives the Leaflet tile layer (CARTO dark_all vs light_all)
  // so the map matches the rest of the chrome on theme change.
  const { resolvedTheme } = useTheme();
  const mapIsDark = resolvedTheme === "dark";
  // Keep the URL `?country` and the persisted browsing-country atom in sync.
  // Header switcher updates the atom → this hook patches the URL with replace.
  useCountryRouteSync();
  const country = search.country ?? DEFAULT_COUNTRY;

  // Promo banner test mode — `?promoTest=1` short-circuits the page to render
  // every PromoBanner variant (4 categories × 4 taglines) at the top so the
  // full copy matrix can be reviewed visually. Read once at mount; flip the
  // flag by changing the URL.
  const [promoTest] = useState(
    () =>
      typeof window !== "undefined" &&
      new URLSearchParams(window.location.search).get("promoTest") === "1",
  );

  /** Inject the cross-promo banner into a flat list of result nodes:
   *   - one after the 4th result (so it sits below the user's first scan),
   *   - a second after the 12th result for long lists (repeat exposure on
   *     scroll without dominating sparse pages).
   *  Each instance picks an independent random category at mount thanks to
   *  the unique React keys + per-mount `useState` initializer. */
  const withPromos = useCallback(
    (nodes: React.ReactNode[]): React.ReactNode[] =>
      nodes.flatMap((node, i) => {
        if (i === 3 && nodes.length > 4) {
          return [node, <PromoBanner key="promo-1" locale={i18n.locale} country={country} />];
        }
        if (i === 11 && nodes.length > 12) {
          return [node, <PromoBanner key="promo-2" locale={i18n.locale} country={country} />];
        }
        return [node];
      }),
    [country, i18n.locale],
  );

  /* ────────── View state (URL-driven) ────────── */
  const viewIsMap = search.view === "map";
  const mobileView: "list" | "map" = viewIsMap ? "map" : "list";
  const desktopMapOpen = viewIsMap;

  // Reactive viewport flag — `md:hidden` Tailwind breakpoint is 768px, so
  // mobile = strictly below. Drives mobile-only conditionals that need to
  // re-render on resize (filter modal hideVertical/hideSpecialty, etc.).
  const isMobile = useMediaQuery("(max-width: 767px)");

  // Map drawer is irrelevant for video / home-visit consultations — no facility
  // address to plot. Hide the toggle and force-collapse any active map view.
  const hideMapToggle =
    search.consultationType === "video" || search.consultationType === "home_visit";

  /* ────────── URL writers ────────── */
  // `resetScroll: false` so toggling a filter on /find doesn't yank the
  // user back to the top of the page — desktop users frequently scroll
  // mid-list, tap a filter, and expect the same row to stay in view.
  // TanStack Router defaults to true (good for cross-page navigation,
  // wrong for in-page state updates).
  const updateParams = useCallback(
    (updates: Record<string, string | null>) => {
      // PostHog `filter_applied` — fired ONCE per `updateParams` call,
      // with all changed keys as a property. We capture the high-signal
      // filter keys only (skip noise like `view`, `q`, etc.). Empty
      // change set = no event. Multi-key updates (e.g. clear-all) appear
      // as a single event with `filter_keys: [...]` so funnels can
      // distinguish "user toggled one filter" vs. "reset everything".
      const TRACKED_FILTER_KEYS = new Set([
        "specialty",
        "specialties",
        "service",
        "city",
        "country",
        "distance",
        "consultationType",
        "insurance",
        "audience",
        "tab",
        "group",
        "sort",
        "video_only",
        "favoritesOnly",
      ]);
      const trackedChanges: Record<string, string | null> = {};
      for (const [key, value] of Object.entries(updates)) {
        if (TRACKED_FILTER_KEYS.has(key)) trackedChanges[key] = value;
      }
      const trackedKeys = Object.keys(trackedChanges);
      if (trackedKeys.length > 0) {
        capture("filter_applied", {
          filter_keys: trackedKeys,
          // Single-key updates carry their value in `filter_value` for
          // easy slicing in PostHog (e.g. top-N specialty codes). Multi-
          // key calls leave it null because there's no single value.
          filter_type: trackedKeys.length === 1 ? trackedKeys[0] : null,
          filter_value:
            trackedKeys.length === 1 ? (trackedChanges[trackedKeys[0]] ?? "cleared") : null,
          country,
        });
      }
      void navigate({
        to: "/find",
        search: (prev: unknown) => {
          const next: Record<string, unknown> = { ...(prev as Record<string, unknown>) };
          for (const [key, value] of Object.entries(updates)) {
            if (value === null || value === "") {
              delete next[key];
            } else {
              next[key] = value;
            }
          }
          return next as SearchSearchParams;
        },
        replace: false,
        resetScroll: false,
      });
    },
    [navigate, country],
  );

  const setView = useCallback(
    (next: "list" | "map") => {
      void navigate({
        to: "/find",
        search: (prev: unknown) => {
          const out: Record<string, unknown> = { ...(prev as Record<string, unknown>) };
          if (next === "list") delete out.view;
          else out.view = "map";
          return out as SearchSearchParams;
        },
        replace: false,
        resetScroll: false,
      });
    },
    [navigate],
  );

  const setSort = useCallback(
    (sort: SortOption) => updateParams({ sort: sort === "recommended" ? null : sort }),
    [updateParams],
  );

  const setGroup = useCallback(
    (next: GroupMode) => updateParams({ group: next === "facility" ? "facility" : null }),
    [updateParams],
  );

  /* ────────── Favorites — auth-gated, client-side filter when fav=1 ────────── */
  const { ids: favoriteIds, authed: favoritesAuthed } = useFavoriteProviderIds();
  const auth = useAuthModal();

  const setFavoritesOnly = useCallback(
    (next: boolean) => {
      // Auth-gate the toggle ON. The Set is empty for guests; flipping fav=1
      // would zero out results — open the auth modal instead.
      if (next && !favoritesAuthed) {
        auth.open({ intent: "favorite" });
        return;
      }
      updateParams({ fav: next ? "1" : null });
    },
    [auth, favoritesAuthed, updateParams],
  );

  /**
   * Smart submit — runs preprocessQuery on the raw text, applies extracted
   * filters to the URL, sets q to the cleaned query. Mirrors the legacy
   * `executeSearch` flow inside `UniversalSearch.onSearch` (search-client.tsx
   * lines 2211-2225).
   *
   *   Emergency trigger ("urgenta", "urgenta dentara"...) — wipes filters and
   *   sets `emergencyServices=true`. Pas 6 wires the emergency UI on top.
   *
   *   Otherwise — maps preprocessQuery filterKeys to find.tsx URL keys:
   *     hasVideo: true        → consultationType=video
   *     acceptsCas: true      → keep as ?acceptsCas=true (still surfaced as a
   *                              pill, hits /api/search Meilisearch filter)
   *     boolean L2 keys       → ?<key>=true verbatim
   *     gender, language      → ?gender=, ?language=
   *     consultationFee=0     → ?consultationFee=0 ("free")
   *     sortOverride          → sort=
   */
  const executeSmartSearch = useCallback(
    (rawQuery: string) => {
      const { cleanQuery, extractedFilters, sortOverride, trigger } = preprocessQuery(rawQuery);

      if (trigger === "emergency") {
        updateParams({
          q: cleanQuery || null,
          emergencyServices: "true",
          // Reset narrow filters — emergency routes to all-types.
          consultationType: null,
          insurance: null,
          ratingMin: null,
        });
        return;
      }

      const updates: Record<string, string | null> = { q: cleanQuery || null };

      for (const [key, value] of Object.entries(extractedFilters)) {
        // Map Meilisearch-style keys onto the find.tsx URL contract.
        if (key === "hasVideo" && value === true) {
          updates.consultationType = "video";
          continue;
        }
        // Insurance enum: cas | private | direct (canonical, not the legacy
        // `acceptsCas=true` boolean which duplicates this and serializes
        // poorly via TanStack Router).
        if (key === "insurance" && (value === "cas" || value === "private" || value === "direct")) {
          updates.insurance = value;
          continue;
        }
        // Boolean L2 keys (is24h, hasParking, ...) — pass through.
        if ((L2_BOOLEAN_KEYS as readonly string[]).includes(key)) {
          updates[key] = value === true || value === "true" ? "true" : null;
          continue;
        }
        if (key === "gender" && (value === "F" || value === "M")) {
          updates.gender = value;
          continue;
        }
        if (key === "language" && typeof value === "string") {
          updates.language = value;
          continue;
        }
        if (key === "consultationFee" && typeof value === "number") {
          updates.consultationFee = String(value);
          continue;
        }
      }

      if (sortOverride) updates.sort = sortOverride;

      updateParams(updates);
    },
    [updateParams],
  );

  const scope: SearchScope = search.scope ?? "local";
  const groupMode: GroupMode = search.group === "facility" ? "facility" : "grouped";
  const favoritesOnly = search.fav === "1";

  // Auto-collapse map when user switches to video / home-visit (matches legacy).
  useEffect(() => {
    if (hideMapToggle && viewIsMap) setView("list");
  }, [hideMapToggle, viewIsMap, setView]);

  /* ────────── Modals ──────────
     Filter modal is opt-in only — opens via the Refine CTA in the hero.
     The previous lazy initializer auto-opened it on first mount whenever
     the URL had no active filters, which surprised users arriving via
     other flows (e.g. "Continue without account" → /find). */
  const [filtersOpen, setFiltersOpen] = useState<boolean>(false);
  const [saveSearchOpen, setSaveSearchOpen] = useState(false);

  // Smart-search overlay (UniversalSearch in mobile-fullscreen mode). Driven
  // by the magnifier icon button in the mobile hero — separated from the
  // filter modal so users get a focused free-text experience instead of
  // nesting it inside a generic filter sheet.
  const [smartSearchOpen, setSmartSearchOpen] = useState(false);

  /* ────────── Puls command bar (desktop hero) ──────────
     `universalSearchRef` lets the hero "Search" button run the exact Enter
     path of the bar; `aiBusy` speeds the beam up while the symptom router
     thinks; `aiRoute` mirrors the router's latest suggestions as chips under
     the bar (the dropdown keeps its own AI section unchanged). */
  const universalSearchRef = useRef<UniversalSearchHandle>(null);
  const [aiBusy, setAiBusy] = useState(false);
  const [aiRoute, setAiRoute] = useState<{
    query: string;
    suggestions: AiSuggestion[];
  } | null>(null);
  const handleAiSuggestions = useCallback((suggestions: AiSuggestion[], query: string) => {
    setAiRoute(suggestions.length > 0 ? { query, suggestions } : null);
  }, []);

  // Notify-me CTA on the empty-state — same flow as the bookmark icon.
  // Guest tap → stamp the post-auth action and open the auth modal;
  // authed tap → open the SaveSearchModal directly.
  const handleNotifyMe = useCallback(() => {
    if (!favoritesAuthed) {
      try {
        window.sessionStorage.setItem("pontimed_post_auth_action", "save-search");
      } catch {
        /* private mode etc — degrade to plain sign-in flow */
      }
      auth.open({ intent: "save-search" });
      return;
    }
    setSaveSearchOpen(true);
  }, [auth, favoritesAuthed]);

  // Empty-state escape hatch — opens the guided-search wizard at step 0 so
  // users who can't refine their filters into matches can restart with a
  // wizard-driven flow. Mirrors `<GuidedSearchHero>` (?wizard=1&step=0).
  const handleStartGuided = useCallback(() => {
    void navigate({
      to: "/find",
      search: ((prev: Record<string, unknown>) => ({
        ...prev,
        wizard: "1",
        step: "0",
      })) as never,
      replace: false,
    });
  }, [navigate]);

  // Post-auth handoff: when the bookmark-icon flow opens the auth modal for a
  // guest, it stamps `pontimed_post_auth_action=save-search` into
  // sessionStorage. Once the session flips to authenticated, consume the flag
  // and pop the SaveSearchModal — what the user originally intended to do.
  useEffect(() => {
    if (!favoritesAuthed) return;
    let action: string | null = null;
    try {
      action = window.sessionStorage.getItem("pontimed_post_auth_action");
    } catch {
      return;
    }
    if (action !== "save-search") return;
    try {
      window.sessionStorage.removeItem("pontimed_post_auth_action");
    } catch {
      /* ignore */
    }
    setSaveSearchOpen(true);
  }, [favoritesAuthed]);

  // "Reset all" — clears search INTENT in a single URL write. Modal is NEVER
  // auto-opened from here (neither mobile nor desktop): the sidebar/mobile
  // sheet stays in whatever state the user left it.
  // Preserves user CONTEXT (city, language(s), mode/countries for cross-border):
  // re-open would re-hydrate them from localStorage anyway, so wiping is
  // redundant and annoying. Also clears the secondary filter flags hosted
  // inside the "More filters" disclosure (rating, accessibility, amenities)
  // and the Servicii quick toggles so both the sidebar "Clear all" and the
  // empty-state "Reset all filters" perform an identical reset.
  const resetFilters = useCallback(() => {
    updateParams({
      specialty: null,
      specialties: null,
      service: null,
      services: null,
      insurance: null,
      consultationType: null,
      ratingMin: null,
      hasVideo: null,
      gender: null,
      minPrice: null,
      maxPrice: null,
      consultationFee: null,
      isVerified: null,
      audience: null,
      tab: null,
      // Accessibility flags
      hasWheelchairAccess: null,
      hasElevator: null,
      hasAccessibleRestroom: null,
      hasParking: null,
      // Amenity flags
      hasWifi: null,
      hasAirConditioning: null,
      hasChildrenArea: null,
      hasBreastfeedingArea: null,
      hasPublicTransport: null,
      hasHomeCollection: null,
      hasDelivery: null,
      // Servicii quick toggles
      isOpenNow: null,
      is24h: null,
    });
  }, [updateParams]);

  /* ────────── Top bar height (drives --search-header-h CSS var) ──────────
     The mobile sticky search header was removed, so this is always 0 now.
     Kept as a CSS var because desktop `--chrome-h` references it for
     sticky-sidebar offsets. */
  const topBarHeight = 0;

  /* ────────── Viewport tier + persisted list/map split ──────────
     Desktop tier (≥1200px) renders the always-mounted map in a draggable
     split panel; tablet (md..1199) hides the map column entirely. */
  const tier = useViewportTier();
  const [listSplitPct, setListSplitPct] = useListMapSplit();
  const showDesktopMapColumn = tier === "desktop" && !hideMapToggle;
  const gridContainerRef = useRef<HTMLDivElement>(null);

  /* ────────── Lazy-load map only when first requested ────────── */
  // Desktop always shows the map in the sticky right column — mount eagerly on
  // first render so the user never sees the loading skeleton at md+. On
  // mobile we also mount immediately when the URL explicitly requests
  // `view=map` (deep link / sessionStorage restore back from /account) —
  // otherwise the user would stare at an empty grey area for up to 3s
  // until the scroll/touch fallback timer activates the map.
  const isDesktop = typeof window !== "undefined" && window.innerWidth >= 768;
  const [mapReady, setMapReady] = useState(isDesktop || search.view === "map");
  useEffect(() => {
    if (mapReady) return;
    const activate = () => {
      setMapReady(true);
      cleanup();
    };
    const timer = setTimeout(activate, 3000);
    const cleanup = () => {
      clearTimeout(timer);
      window.removeEventListener("scroll", activate);
      window.removeEventListener("touchstart", activate);
    };
    window.addEventListener("scroll", activate, { once: true, passive: true });
    window.addEventListener("touchstart", activate, { once: true, passive: true });
    return cleanup;
  }, [mapReady]);

  /* ────────── Reference data ────────── */
  // Live country list — drives the video country picker chip set. Cached 5min.
  const { data: countriesData } = useCountriesQuery();

  // City search scope:
  //  · in_person / null  → current country only. Going to a doctor in person
  //    is geographically constrained, so the city list must not mix Romanian
  //    + Bulgarian + … cities. The country pill above the city picker has a
  //    "Schimbă" affordance that opens the country sheet for explicit swaps.
  //  · video / others    → legacy scope-driven (local · cross+selected · global).
  const cityScopeCountries = useMemo<string | string[]>(() => {
    const ct = search.consultationType;
    if (ct === "in_person" || ct === "in_office" || !ct) {
      return country;
    }
    if (scope === "global") return [...SUPPORTED_COUNTRIES];
    if (scope === "cross") {
      const selectedLower = (search.countries ?? "")
        .split(",")
        .map((s) => s.trim().toLowerCase())
        .filter(Boolean);
      if (selectedLower.length === 0) return country;
      return Array.from(new Set([country.toLowerCase(), ...selectedLower]));
    }
    return country;
  }, [scope, search.countries, search.consultationType, country]);

  const {
    topCities,
    searchResults: citySearchResults,
    searchCities: searchCitiesFn,
  } = useCities(cityScopeCountries);
  const {
    specialties,
    searchSpecialties,
    searchResults: specSearchResults,
    isSearching: isSpecSearching,
  } = useSpecialties(100, country);
  const {
    services,
    searchServices,
    searchResults: svcSearchResults,
    isSearching: isSvcSearching,
  } = useServices(country, 100);

  /* ────────── Search ────────── */
  const {
    results: rawResults,
    total: rawTotal,
    loading,
    disjunctiveTotals,
    cityFacet,
  } = useSearchData(search);
  // Stays true a frame longer than `loading` so the overlay hides AFTER new
  // results paint (mirrors legacy useDeferredValue trick).
  const loadingDeferred = useDeferredValue(loading);
  const showLoadingOverlay = loading || loadingDeferred;

  // Cold-load gate — true only during the FIRST fetch (before any results
  // have ever arrived). Subsequent refetches (filter changes) rely on the
  // global <NavigationProgress> bar in the header instead of a local
  // skeleton, since results are still on screen.
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  useEffect(() => {
    if (!loading && !hasLoadedOnce) setHasLoadedOnce(true);
  }, [loading, hasLoadedOnce]);
  const isColdLoad = showLoadingOverlay && !hasLoadedOnce;

  /* ────────── Favorites filter (client-side, 1:1 legacy) ────────── */
  // The /api/search hit ids for providers are composite `<providerId>__<facilityId>`,
  // while /api/account/favorites/ids returns bare provider UUIDs. Match using
  // `r.providerId ?? r.id` — same key the legacy search-client uses (line 1769).
  // Facilities are filtered out (favorites only apply to providers in legacy).
  const favoritesInResults = useMemo(() => {
    if (favoriteIds.size === 0) return 0;
    let n = 0;
    for (const r of rawResults) {
      if (r.type !== "provider") continue;
      const key = r.providerId ?? r.id;
      if (favoriteIds.has(key)) n++;
    }
    return n;
  }, [rawResults, favoriteIds]);

  /* ────────── Pinned hit (just-viewed card sticky at top of list) ──────────
     When a provider/facility modal is opened, we remember which hit it was
     so the corresponding card jumps to the first position in the list
     behind. Stays pinned after the user closes the modal so they land on
     "where they were" — until they change a filter or search context, at
     which point the pin is wiped further down. Separate from
     `highlightedId` because the highlight may shift via keyboard nav but
     the pin should reflect only the actual modal target. */
  const [pinnedHitId, setPinnedHitId] = useState<string | null>(null);

  const results = useMemo(() => {
    const base = favoritesOnly
      ? rawResults.filter((r) => r.type === "provider" && favoriteIds.has(r.providerId ?? r.id))
      : rawResults;
    // Pinned reorder — the hit corresponding to the open profile modal (or
    // the last-opened one in this search context) jumps to index 0 so on
    // modal close the user lands on it as the first card. No-op when the
    // hit isn't in the current filtered set.
    if (!pinnedHitId || base.length < 2) return base;
    const idx = base.findIndex((r) => r.id === pinnedHitId);
    if (idx <= 0) return base;
    return [base[idx], ...base.slice(0, idx), ...base.slice(idx + 1)];
  }, [favoritesOnly, rawResults, favoriteIds, pinnedHitId]);
  const total = favoritesOnly ? results.length : rawTotal;

  // Custom DB tracking — `search_appearance` per result. Increments
  // Provider/Facility.searchAppearances counter (used by search ranking)
  // + writes AnalyticsEvent rows. Per-id dedup ref so re-renders inside
  // the same result set don't double-count; new ids fire as the user
  // paginates / filters. Mirror of legacy `trackSearchAppearances` in
  // src/lib/analytics/use-track.ts.
  const trackedResultIdsRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (results.length === 0) return;
    const fresh = results.filter((r) => !trackedResultIdsRef.current.has(r.id));
    if (fresh.length === 0) return;
    for (const r of fresh) trackedResultIdsRef.current.add(r.id);
    trackSearchAppearances(
      fresh
        .filter((r) => r.type === "provider" || r.type === "facility")
        .map((r) => {
          // Provider hits expose composite `doc_<providerId>_<facilityId>`
          // ids (see apps/workers/src/meili/sync-provider.ts) — send the
          // bare provider UUID since `analytics_events.target_id` is
          // `@db.Uuid`. Facility hits already use the bare facility UUID.
          const id = r.type === "provider" ? (r.providerId ?? r.id) : r.id;
          return { id, type: r.type };
        }),
    );
  }, [results]);

  /* ────────── Grouping (collapse multi-facility providers) ────────── */
  const groupedResults = useMemo(
    () => (groupMode === "grouped" ? groupProviderResults(results) : null),
    [groupMode, results],
  );

  /* ────────── Empty-state suggestions (driven by disjunctive facets) ────────── */
  const emptyStateSuggestions = useEmptyStateSuggestions({
    totalResults: total,
    activeFilters: {
      specialty: search.specialty ?? null,
      city: search.city ?? null,
      service: search.service ?? null,
      insurance: search.insurance ?? null,
    },
    disjunctiveTotals,
    topCities,
    cityFacet,
    locale: i18n.locale,
    onApply: updateParams,
    // Empty-state "Reset all filters" must do the SAME thing as the
    // sidebar "Clear all" — same param set, no modal open.
    onResetAll: resetFilters,
  });

  /* ────────── UI state (highlight + keyboard nav) ────────── */
  const [highlightedId, setHighlightedId] = useState<string | null>(null);
  // Pointer-following border light on the result cards (Puls) — mouse/pen
  // only; the list containers carry `.puls-spot-group`.
  const handleSpotlight = usePointerSpotlight();

  /* ────────── Card click → open ProviderProfileModal ────────── */
  // Modal owns its own provider data fetch and resolves the id (Meili
  // composite, bare UUID, or pretty slug) internally. We only mirror the
  // selection in the URL (`?provider=<id>`) so deep links + browser back
  // restore the open state without us tracking a separate slug state.
  const openProviderSheet = useCallback(
    (id: string) => {
      void navigate({
        to: "/find",
        search: (prev: unknown) => ({
          ...(prev as Record<string, unknown>),
          provider: id,
        }),
        replace: false,
        resetScroll: false,
      });
    },
    [navigate],
  );

  const closeProviderSheet = useCallback(() => {
    void navigate({
      to: "/find",
      search: (prev: unknown) => {
        const next: Record<string, unknown> = { ...(prev as Record<string, unknown>) };
        delete next.provider;
        return next as SearchSearchParams;
      },
      replace: false,
      resetScroll: false,
    });
  }, [navigate]);

  const openFacilitySheet = useCallback(
    (id: string) => {
      void navigate({
        to: "/find",
        search: (prev: unknown) => ({
          ...(prev as Record<string, unknown>),
          facility: id,
        }),
        replace: false,
        resetScroll: false,
      });
    },
    [navigate],
  );

  // Variant (b) URL back-fill — when the user cold-loads `/find?provider=<id>`
  // or `/find?facility=<id>` WITHOUT explicit filter context (country, city,
  // specialty, tab, q), extract that context from the modal's profile data
  // and push it into the URL so the list behind the modal is populated
  // with relevant alternatives instead of being empty. Only fires for the
  // first profile resolved per session — subsequent in-app modal opens
  // already have user-driven filters and shouldn't override them.
  //
  // `replace: true` because we're filling in implicit state the user
  // already meant when they arrived at this URL, not adding a new history
  // entry the back button should rewind through.
  const hasExplicitFilters =
    Boolean(search.q) ||
    Boolean(search.country) ||
    Boolean(search.city) ||
    Boolean(search.specialty) ||
    Boolean(search.specialties) ||
    Boolean(search.tab);

  const handleProviderProfileResolved = useCallback(
    (ctx: { country: string | null; city: string | null; specialty: string | null }) => {
      if (hasExplicitFilters) return;
      const updates: Record<string, string> = {};
      if (ctx.country) updates.country = ctx.country.toUpperCase();
      if (ctx.city) updates.city = ctx.city;
      if (ctx.specialty) updates.specialty = ctx.specialty;
      if (Object.keys(updates).length === 0) return;
      void navigate({
        to: "/find",
        search: (prev: unknown) => ({
          ...(prev as Record<string, unknown>),
          ...updates,
        }),
        replace: true,
        resetScroll: false,
      });
    },
    [hasExplicitFilters, navigate],
  );

  const handleFacilityProfileResolved = useCallback(
    (ctx: { country: string | null; city: string | null; category: string | null }) => {
      if (hasExplicitFilters) return;
      const updates: Record<string, string> = {};
      if (ctx.country) updates.country = ctx.country.toUpperCase();
      if (ctx.city) updates.city = ctx.city;
      // Facility category maps 1:1 onto the `tab` filter values
      // (clinic, hospital, pharmacy, laboratory, imaging, dental) — see
      // /api/search providerType/subtype derivation.
      if (ctx.category) updates.tab = ctx.category;
      if (Object.keys(updates).length === 0) return;
      void navigate({
        to: "/find",
        search: (prev: unknown) => ({
          ...(prev as Record<string, unknown>),
          ...updates,
        }),
        replace: true,
        resetScroll: false,
      });
    },
    [hasExplicitFilters, navigate],
  );

  const closeFacilitySheet = useCallback(() => {
    void navigate({
      to: "/find",
      search: (prev: unknown) => {
        const next: Record<string, unknown> = { ...(prev as Record<string, unknown>) };
        delete next.facility;
        return next as SearchSearchParams;
      },
      replace: false,
      resetScroll: false,
    });
  }, [navigate]);

  // Sync highlight + pin with URL deep-link (provider/facility param).
  // Whenever a modal is open via URL — either via in-app click or
  // cold-load — pin the corresponding card so it jumps to the top of the
  // list and stays there past the modal close. Pin persists across modal
  // close so the user lands on "where they were" when they dismiss the
  // sheet. Card matches by raw id (Meili composite or facility UUID),
  // provider UUID extracted from a canonical slug, or facility UUID
  // extracted from a pretty slug. Match against `rawResults` (not the
  // post-reorder `results`) so setting the pin doesn't depend on a memo
  // that itself depends on the pin.
  useEffect(() => {
    const target = search.provider ?? search.facility;
    if (!target || rawResults.length === 0) return;
    const hit = rawResults.find((r) => {
      if (r.id === target) return true;
      if (r.type === "provider") {
        const extracted = extractProviderId(target) ?? target;
        return r.providerId === extracted;
      }
      if (r.type === "facility") {
        const extracted = extractFacilityId(target);
        return r.id === extracted;
      }
      return false;
    });
    if (hit) {
      setHighlightedId(hit.id);
      setPinnedHitId(hit.id);
      const el = document.getElementById(`result-${hit.id}`);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [search.provider, search.facility, rawResults]);

  // Wipe pin when search context changes. Keeping a "just-viewed" card
  // pinned across an unrelated filter change would surface a result that
  // may no longer match the query at all. Triggered by any user-driven
  // filter mutation; URL-state writes that don't change the meaningful
  // context (e.g. opening the modal itself) do NOT reset the pin because
  // they leave these specific params untouched.
  useEffect(() => {
    setPinnedHitId(null);
  }, [
    search.q,
    search.specialty,
    search.specialties,
    search.service,
    search.services,
    search.city,
    search.tab,
    search.consultationType,
    search.country,
    search.insurance,
    search.ratingMin,
    search.gender,
    search.languages,
    search.nearMe,
    search.fav,
    search.scope,
  ]);

  // Card click: highlight + scroll into view AND open the quick sheet.
  // Provider hits open `<ProviderProfileModal>`, facility hits open
  // `<FacilityProfileModal>` — both URL-driven via `?provider=` / `?facility=`.
  const handleCardClick = useCallback(
    (id: string) => {
      setHighlightedId(id);
      const el = document.getElementById(`result-${id}`);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
      const hit = results.find((r) => r.id === id);
      if (hit?.type === "provider") openProviderSheet(id);
      else if (hit?.type === "facility") openFacilitySheet(id);
    },
    [openProviderSheet, openFacilitySheet, results],
  );

  const handleBookClick = useCallback(
    (id: string, _initialSlot?: NextSlot) => {
      // For now, "Book" opens the same quick sheet — Pas 4.1 wires inline
      // booking via initialSlot. The sheet's Book CTA links to /provider/$slug?book=1.
      handleCardClick(id);
    },
    [handleCardClick],
  );

  // Hover-to-highlight only active in desktop split view (mobile switches
  // between list and map, so hovering would highlight an unseen marker).
  // Puls thread: the hovered card reaches <ResultPinThread> through a
  // store, not state — the highlight gate below is unchanged and the page
  // doesn't re-render on every card hover.
  const [threadHover] = useState(createThreadHoverChannel);
  const handleCardHover = useCallback(
    (id: string | null) => {
      threadHover.set(id);
      if (!isDesktop || !desktopMapOpen) return;
      setHighlightedId(id);
    },
    [isDesktop, desktopMapOpen, threadHover],
  );

  useResultsKeyboardNav({
    results,
    highlightedId,
    setHighlightedId,
    onActivate: handleCardClick,
    enabled: !filtersOpen,
  });

  // Sidebar accordion: only one filter section open at a time. `null` =
  // all collapsed. Toggle helper closes the previously-open one when a new
  // one is clicked, and re-clicking the open section closes it.
  const [openSidebarSection, setOpenSidebarSection] = useState<string | null>(null);

  // "More filters" disclosure — gates the secondary sidebar cards
  // (Rating, Accessibility, Amenities, Audience, Provider gender). Hidden
  // by default to keep the sidebar short; user expands to reveal them all.
  const [showMoreFilters, setShowMoreFilters] = useState<boolean>(false);

  /* ────────── Sidebar mode: Consult Medical vs Servicii Medicale ──────────
     Switch pill pair at the top of the desktop sidebar drives the binary
     mental model.

     Mode resolution (priority order):
       1. URL `?tab=` matches a Servicii vertical → "servicii" (deep-link wins)
       2. Local explicit override from the user clicking the switch → that
       3. Default → "consult"

     Local override is required because the user can switch to Servicii and
     pick "All" — at that point `?tab=` is unset and a pure URL-derived mode
     would snap back to Consult, making the switch click look broken. The
     override sticks until the user clicks Consult or until URL `?tab=`
     changes to a non-Servicii value (e.g. wizard rewrites the URL). */
  const [explicitSidebarMode, setExplicitSidebarMode] = useState<"consult" | "servicii" | null>(
    null,
  );
  const sidebarMode: "consult" | "servicii" =
    isServiciiTab(search.tab) || search.kind === "businesses"
      ? "servicii"
      : (explicitSidebarMode ?? "consult");
  const currentServiciiVertical: ServiciiVertical | undefined = SERVICII_VERTICALS.find(
    (v) => v.tab === search.tab,
  );

  const setSidebarMode = useCallback(
    (next: "consult" | "servicii") => {
      setExplicitSidebarMode(next);
      // Always open the relevant first card on switch so the user sees the
      // filter immediately (Specialty for Consult, Tip for Servicii). Helps
      // make the switch click feel actionable even when the URL change is
      // subtle (Servicii with no Tip writes only kind=businesses).
      setOpenSidebarSection(next === "consult" ? "specialty" : "serviciiTip");
      if (next === "consult") {
        // Mirror the modal's generic "provider" vertical setVertical case
        // (search-filters-modal.tsx ~line 473): explicit kind=providers +
        // tab=provider so /api/search hits the providers index, not the
        // mixed default. category/includeEmpty/type stay null (those are
        // facility-only). consultationType + hasVideo survive — user may
        // want to keep an in_person/video preference when flipping back.
        updateParams({
          tab: "provider",
          kind: "providers",
          category: null,
          includeEmpty: null,
          type: null,
        });
      } else {
        // Switch to Servicii ("All" by default) — drop Consult-only params
        // AND any prior Servicii vertical state. Sets kind=businesses as
        // the sensible default for "All Servicii" so /api/search hits the
        // businesses index (pharmacy/lab/imaging/optical/homecare/medical
        // _transport/equipment) by default. Picking a Tip vertical
        // overrides kind to providers for hospital/clinic/polyclinic/
        // dental_clinic. Deep links with `?kind=businesses` round-trip
        // back to Servicii mode via the derivation above.
        updateParams({
          specialty: null,
          specialties: null,
          hasVideo: null,
          consultationType: null,
          tab: null,
          kind: "businesses",
          category: null,
          includeEmpty: null,
          type: null,
        });
      }
    },
    [updateParams],
  );

  const setServiciiVertical = useCallback(
    (vertical: ServiciiVertical | null) => {
      if (!vertical) {
        // "Toate" — drop tab/category/includeEmpty/type, but keep
        // kind=businesses so the Servicii context survives in the URL
        // (matches the switch-to-Servicii default). Mode derivation also
        // treats kind=businesses as a Servicii signal.
        updateParams({
          tab: null,
          kind: "businesses",
          category: null,
          includeEmpty: null,
          type: null,
        });
        return;
      }
      // 1:1 with SearchFiltersModal#setVertical() for parity (search-filters
      // -modal.tsx ~line 444): write tab + kind + category + includeEmpty,
      // clear subtype, clear specialty pickers, clear consult-only filters.
      updateParams({
        tab: vertical.tab,
        kind: vertical.kind,
        category: vertical.category,
        includeEmpty: "true",
        type: null,
        specialty: null,
        specialties: null,
        hasVideo: null,
        consultationType: null,
      });
      // Collapse the Tip card. Open Where ONLY if location isn't already
      // set (city / near-me) — if user already has Bucharest as city, no
      // point making them confirm Where again. The selected Tip is visible
      // as a red pill in the collapsed Tip card header.
      const whereDone = !!search.city || (search.nearMe === "true" && !!search.lat && !!search.lng);
      setOpenSidebarSection(whereDone ? null : "where");
    },
    [updateParams, search.city, search.nearMe, search.lat, search.lng],
  );

  /* True when the Where filter already has a meaningful value for the given
     consultation type. Used by the consultation-type pick handlers to skip
     the auto-open if the user has already set a location. */
  const isWhereSatisfiedForType = useCallback(
    (type: "in_person" | "video" | "home_visit"): boolean => {
      switch (type) {
        case "video":
          // Video defaults to scope=local = current country (always set).
          // Cross-border explicitly needs at least one neighbor; global is
          // self-satisfied (no narrowing required).
          if (search.scope === "global") return true;
          if (search.scope === "cross") return !!search.countries;
          return true;
        case "home_visit":
          return !!search.homeTag || (!!search.lat && !!search.lng);
        case "in_person":
        default:
          return !!search.city || (search.nearMe === "true" && !!search.lat && !!search.lng);
      }
    },
    [
      search.scope,
      search.countries,
      search.homeTag,
      search.lat,
      search.lng,
      search.city,
      search.nearMe,
    ],
  );

  /* Consultation type pick handler — wraps updateParams + the smart
     auto-open-Where logic. For home_visit, also tries to restore the saved
     home address from localStorage if the URL has none, so a fresh visit
     doesn't force a re-search of the same place every time.

     Cross-border cleanup: scope/countries are video-only concepts ("All
     countries" globally licensed, or a hand-picked cross-border set).
     When switching to in_person or home_visit those filters stop making
     sense (both are inherently geographic to ONE country), so we clear
     them — otherwise the active "All countries" pill survives the switch
     and the result set looks broken. */
  const pickConsultationType = useCallback(
    (type: "in_person" | "video" | "home_visit") => {
      if (type === "home_visit") {
        const hasUrlAddress = !!search.homeTag || (!!search.lat && !!search.lng);
        if (!hasUrlAddress) {
          const saved = loadSavedHomeAddress();
          if (saved) {
            updateParams({
              consultationType: "home_visit",
              hasHomeVisit: "true",
              lat: String(saved.lat),
              lng: String(saved.lng),
              homeCity: saved.city,
              homeTag: saved.formattedAddress,
              // Same competing-filter cleanup as pickHomeAddress.
              nearMe: null,
              distance: null,
              city: null,
              north: null,
              south: null,
              east: null,
              west: null,
              // Drop cross-border / global scope — see comment above.
              scope: null,
              countries: null,
            });
            setHomeAddrQuery(saved.formattedAddress);
            // Address restored — Where is satisfied, don't auto-open.
            setOpenSidebarSection(null);
            return;
          }
        }
        updateParams({
          consultationType: "home_visit",
          scope: null,
          countries: null,
        });
        setOpenSidebarSection(hasUrlAddress ? null : "where");
        return;
      }
      updateParams({
        consultationType: type,
        // Only video keeps scope/countries; in_person/in_office drop them
        // (in-person is inherently single-country, cross-border is video-only).
        ...(type !== "video" && { scope: null, countries: null }),
      });
      setOpenSidebarSection(isWhereSatisfiedForType(type) ? null : "where");
    },
    [updateParams, isWhereSatisfiedForType, search.homeTag, search.lat, search.lng],
  );
  const toggleSidebarSection = useCallback(
    (key: string) => setOpenSidebarSection((cur) => (cur === key ? null : key)),
    [],
  );

  // Sidebar Specialty search input — debounced via `searchSpecialties`. When
  // the query is empty we fall back to the top-N reference set.
  const [sidebarSpecialtyQuery, setSidebarSpecialtyQuery] = useState("");
  useEffect(() => {
    if (sidebarSpecialtyQuery.trim().length > 0) searchSpecialties(sidebarSpecialtyQuery);
  }, [sidebarSpecialtyQuery, searchSpecialties]);

  // Sidebar Services search input — mirrors the Specialty search above.
  // Debounced via `searchServices`; empty query falls back to the
  // category-scoped reference set (`visibleServiceOptions`).
  const [sidebarServicesQuery, setSidebarServicesQuery] = useState("");
  useEffect(() => {
    if (sidebarServicesQuery.trim().length > 0) searchServices(sidebarServicesQuery);
  }, [sidebarServicesQuery, searchServices]);

  // Sidebar symptom-router (inline equivalent of the modal's "Help me pick").
  // Toggles into a mode where the input is treated as a symptom description
  // and a Find-specialty button calls /api/ai/symptom-router. Reuses the
  // same URL query input by repurposing it; chip-list is suppressed in this
  // mode so the user isn't tempted to scan-pick.
  const [symptomMode, setSymptomMode] = useState(false);
  const [symptomSuggestions, setSymptomSuggestions] = useState<SymptomSuggestion[]>([]);
  const [symptomLoading, setSymptomLoading] = useState(false);
  const [symptomError, setSymptomError] = useState<string | null>(null);
  const sidebarSpecialtyInputRef = useRef<HTMLInputElement | null>(null);
  const sidebarSpecialtyTextareaRef = useRef<HTMLTextAreaElement | null>(null);
  const enterSymptomMode = useCallback(() => {
    setSymptomMode(true);
    setSidebarSpecialtyQuery("");
    setSymptomSuggestions([]);
    setSymptomError(null);
    // Defer focus until the textarea has rendered.
    requestAnimationFrame(() => sidebarSpecialtyTextareaRef.current?.focus());
  }, []);
  const runSymptomSearch = useCallback(async () => {
    const q = sidebarSpecialtyQuery.trim();
    if (q.length < 3) return;
    setSymptomLoading(true);
    setSymptomError(null);
    setSymptomSuggestions([]);
    try {
      const data = await apiFetch.post<{ suggestions?: SymptomSuggestion[]; error?: string }>(
        "/api/ai/symptom-router",
        { symptoms: q, locale: i18n.locale, country },
      );
      if (data.suggestions && data.suggestions.length > 0) {
        setSymptomSuggestions(data.suggestions);
      } else {
        setSymptomError(t`No suggestions for these symptoms.`);
      }
    } catch {
      setSymptomError(t`Symptom analysis failed. Try again.`);
    } finally {
      setSymptomLoading(false);
    }
  }, [sidebarSpecialtyQuery, i18n.locale, country, t]);

  /* ────────── Map fitKey + center ────────── */
  const [fitKey, setFitKey] = useState(0);
  useEffect(() => {
    if (!loading) setFitKey((k) => k + 1);
  }, [loading, results.length]);

  const mapCenter = useMemo<[number, number]>(() => {
    if (search.city) {
      const cityId = parseCitySlug(search.city);
      const match = topCities.find((c) => c.id === cityId || String(c.geonameId) === cityId);
      if (match) return [match.lat, match.lng];
    }
    return COUNTRY_CENTERS[country.toUpperCase()] ?? COUNTRY_CENTERS.RO;
  }, [search.city, topCities, country]);

  const mapZoom = search.city ? 13 : 12;

  /* ────────── Map bounds — URL state + live emitter ────────── */
  const urlMapBounds = useMemo<MapBounds | null>(() => {
    const n = Number(search.north);
    const s = Number(search.south);
    const e = Number(search.east);
    const w = Number(search.west);
    if (![n, s, e, w].every(Number.isFinite)) return null;
    return { north: n, south: s, east: e, west: w };
  }, [search.north, search.south, search.east, search.west]);

  const hasMapBounds = urlMapBounds !== null;
  const [liveBounds, setLiveBounds] = useState<MapBounds | null>(null);

  // Puls map extras: `radarKey` replays the one-shot radar sweep over the
  // map ("Search this area" committed / near-me located); `desktopMap` is the
  // desktop column's map, for the card ↔ pin thread.
  const [radarKey, setRadarKey] = useState(0);
  const [desktopMap, setDesktopMap] = useState<ThreadMap | null>(null);

  const handleBoundsChange = useCallback((b: MapBounds) => {
    setLiveBounds(b);
  }, []);

  const commitCurrentBounds = useCallback(() => {
    if (!liveBounds) return;
    void navigate({
      to: "/find",
      search: (prev: unknown) => {
        const next: Record<string, unknown> = { ...(prev as Record<string, unknown>) };
        next.north = String(liveBounds.north);
        next.south = String(liveBounds.south);
        next.east = String(liveBounds.east);
        next.west = String(liveBounds.west);
        // "Search this area" replaces city / nearMe — viewport bbox wins.
        delete next.city;
        delete next.nearMe;
        delete next.lat;
        delete next.lng;
        delete next.distance;
        return next as SearchSearchParams;
      },
      replace: false,
      resetScroll: false,
    });
    setRadarKey((k) => k + 1);
  }, [liveBounds, navigate]);

  /** True when liveBounds are within ~1e-3 deg of urlMapBounds (≈100m). */
  const boundsMatchUrl = useMemo(() => {
    if (!liveBounds || !urlMapBounds) return false;
    const tol = 0.001;
    return (
      Math.abs(liveBounds.north - urlMapBounds.north) < tol &&
      Math.abs(liveBounds.south - urlMapBounds.south) < tol &&
      Math.abs(liveBounds.east - urlMapBounds.east) < tol &&
      Math.abs(liveBounds.west - urlMapBounds.west) < tol
    );
  }, [liveBounds, urlMapBounds]);

  /* ────────── Near-me + flyTo ────────── */
  const [flyTarget, setFlyTarget] = useState<[number, number] | null>(null);
  const [flyKey, setFlyKey] = useState(0);
  const [isLocating, setIsLocating] = useState(false);

  // Fly the map to the new country center on country switch. Leaflet's
  // MapContainer captures `center` only at init, so prop changes don't pan
  // it; FlyToCenter is the explicit hook. Always fires on country change —
  // the previous country's city / nearMe / bounds become stale and the
  // results refire often returns 0 hits (so MapBoundsUpdater's fitBounds
  // bails on `valid.length === 0` and the map stays put). Overriding the
  // map view to the new country's capital is the user-visible signal that
  // the context changed.
  const prevCountryRef = useRef(country);
  useEffect(() => {
    if (prevCountryRef.current === country) return;
    prevCountryRef.current = country;
    const center = COUNTRY_CENTERS[country.toUpperCase()] ?? COUNTRY_CENTERS.RO;
    setFlyTarget(center);
    setFlyKey((k) => k + 1);
  }, [country]);

  // When the UI locale changes (footer / header switcher) and an active
  // `languages` filter exists that doesn't include it, append the locale.
  // Rationale: the user just signaled which language they want to read in;
  // doctors who speak that language are now relevant. Empty filter means
  // "any language" — leave it alone so we don't accidentally narrow the
  // result set on a locale switch.
  const prevLocaleRef = useRef(i18n.locale);
  useEffect(() => {
    if (prevLocaleRef.current === i18n.locale) return;
    prevLocaleRef.current = i18n.locale;
    if (!search.languages) return;
    const lower = i18n.locale.toLowerCase();
    const langs = search.languages
      .split(",")
      .map((s) => s.trim().toLowerCase())
      .filter(Boolean);
    if (langs.includes(lower)) return;
    updateParams({ languages: [...langs, lower].join(",") });
    // updateParams is stable; search.languages tracked so we don't fire on
    // every URL tweak.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n.locale]);

  const locateViaIp = useCallback(async (): Promise<[number, number] | null> => {
    try {
      const data = await apiFetch.get<{
        lat: number | null;
        lng: number | null;
        city: string | null;
      }>("/api/geoip");
      if (Number.isFinite(data.lat) && Number.isFinite(data.lng)) {
        showToast(
          data.city
            ? t`Approximate location: ${data.city} (based on IP).`
            : t`Approximate location based on IP.`,
          "info",
        );
        return [data.lat as number, data.lng as number];
      }
      // API responded but didn't include coordinates — usually means apps/api
      // is running an older build without the lat/lng fields on /api/geoip.
      console.warn("[locateViaIp] /api/geoip missing lat/lng:", data);
    } catch (e) {
      console.warn("[locateViaIp] /api/geoip request failed:", e);
    }
    return null;
  }, [t]);

  /* IP-based city pre-fill on first visit to /find.
     When the URL carries no location signal (no city, no near-me, no
     home-visit address, no scope=global override), we ask /api/geoip
     once per tab and try to map the IP city name to an entry in
     `topCities`. Match → write `?city=<slug>` + a short toast so the
     user knows where the result set came from. Match miss → silently
     skip; the search just falls back to "any city in $country" like
     before. ipapi.co fallback on the server already covers regions
     where Fly doesn't see CF headers, so this works in dev + prod. */
  const geoIpCityAttemptedRef = useRef(false);
  useEffect(() => {
    if (geoIpCityAttemptedRef.current) return;
    if (search.city) return;
    if (search.nearMe === "true") return;
    if (search.homeTag || search.lat || search.lng) return;
    if (search.scope === "global") return;
    if (search.consultationType === "home_visit") return;
    if (topCities.length === 0) return;

    geoIpCityAttemptedRef.current = true;
    void (async () => {
      try {
        const data = await apiFetch.get<{ city: string | null }>("/api/geoip");
        const ipCity = data.city?.trim();
        if (!ipCity) return;
        const lower = ipCity.toLowerCase();
        const match = topCities.find((c) => c.asciiName.toLowerCase() === lower);
        if (!match) return;
        // Re-check the URL right before writing — the user may have
        // picked a city between effect fire and API response (unlikely
        // at ~200-400ms but cheap to guard).
        if (window.location.search.includes("city=")) return;
        updateParams({ city: toCitySlug(match) });
        showToast(t`Showing results for ${ipCity} (based on your location).`, "info");
      } catch {
        /* silent — same posture as locateViaIp on failure */
      }
    })();
  }, [
    search.city,
    search.nearMe,
    search.homeTag,
    search.lat,
    search.lng,
    search.scope,
    search.consultationType,
    topCities,
    updateParams,
    t,
  ]);

  const handleNearMe = useCallback(
    async (km: number) => {
      setIsLocating(true);

      // Reverse-geocode coords → country code so a "Near me" trigger that lands
      // the user in a different country than the one currently in the URL also
      // updates `?country=`. Same cascade as the country picker (URL → inbound
      // sync → atom → cookie) so the rest of the SPA stays consistent.
      const reverseGeocodeCountry = async (lat: number, lng: number): Promise<string | null> => {
        try {
          const body = await apiFetch.get<{ countryISO?: string }>(
            `/api/geocode?lat=${encodeURIComponent(lat)}&lng=${encodeURIComponent(lng)}`,
          );
          const cc = body.countryISO?.toLowerCase();
          return cc && isSupportedCountry(cc) ? cc : null;
        } catch {
          return null;
        }
      };

      // Commit URL only on success — caller passes the desired radius (km) and
      // we append `lat`/`lng` once we actually have them, plus `country` if the
      // detected location lies in a supported country other than the current
      // URL's. Failure paths leave the URL untouched so the popover chip falls
      // back to "Choose city" and no zero-result search fires.
      const commitSuccess = async (coords: [number, number]) => {
        const detectedCC = await reverseGeocodeCountry(coords[0], coords[1]);
        // Near me always hard-pins the search to the detected location's country
        // alone — clear cross-border scope + neighbor selection so prior context
        // doesn't leak into a fresh "where I am right now" search.
        const updates: Record<string, string | null> = {
          nearMe: "true",
          distance: String(km),
          lat: String(coords[0]),
          lng: String(coords[1]),
          city: null,
          north: null,
          south: null,
          east: null,
          west: null,
          scope: null,
          countries: null,
        };
        if (detectedCC && detectedCC.toUpperCase() !== country.toUpperCase()) {
          updates.country = detectedCC.toUpperCase();
        }
        updateParams(updates);
        setFlyTarget(coords);
        setFlyKey((k) => k + 1);
        setRadarKey((k) => k + 1);
      };

      // Browsers/WebViews where the API is missing or blocked at platform level.
      if (typeof navigator === "undefined" || !navigator.geolocation) {
        const ipCoords = await locateViaIp();
        if (ipCoords) {
          await commitSuccess(ipCoords);
          setIsLocating(false);
        } else {
          setIsLocating(false);
          showToast(t`Could not determine your location.`, "error");
        }
        return;
      }

      // Wrap getCurrentPosition: the built-in `timeout` option is unreliable on
      // some browsers / WebViews (Permissions-Policy denies, iOS Safari embed
      // contexts, …) — sometimes neither callback fires. Race against an 8s
      // hard ceiling so the IP fallback is always reachable. Also catch
      // synchronous throws (rare, but happens when Permissions-Policy blocks).
      type GpsResult =
        | { ok: true; coords: [number, number] }
        | { ok: false; err: GeolocationPositionError | null };

      const HARD_TIMEOUT = 8_000;

      const gps = await new Promise<GpsResult>((resolve) => {
        let settled = false;
        const finish = (r: GpsResult) => {
          if (settled) return;
          settled = true;
          resolve(r);
        };
        const timer = setTimeout(() => finish({ ok: false, err: null }), HARD_TIMEOUT);
        try {
          navigator.geolocation.getCurrentPosition(
            (pos) => {
              clearTimeout(timer);
              finish({ ok: true, coords: [pos.coords.latitude, pos.coords.longitude] });
            },
            (err) => {
              clearTimeout(timer);
              finish({ ok: false, err });
            },
            { enableHighAccuracy: true, maximumAge: 60_000, timeout: HARD_TIMEOUT },
          );
        } catch {
          clearTimeout(timer);
          finish({ ok: false, err: null });
        }
      });

      if (gps.ok) {
        await commitSuccess(gps.coords);
        setIsLocating(false);
        return;
      }

      // Anything other than success → IP fallback. Always.
      const ipCoords = await locateViaIp();
      if (ipCoords) {
        await commitSuccess(ipCoords);
        setIsLocating(false);
        return;
      }
      setIsLocating(false);

      // GPS + IP both failed — URL stays unchanged so the popover chip remains
      // on "Choose city" and no broken nearMe search fires.
      const err = gps.err;
      const message = !err
        ? t`Could not determine your location.`
        : err.code === err.PERMISSION_DENIED
          ? t`Location access denied. Enable location in your browser settings.`
          : err.code === err.POSITION_UNAVAILABLE
            ? t`Could not determine your location. Try again outdoors or check your settings.`
            : err.code === err.TIMEOUT
              ? t`Locating timed out. Try again.`
              : t`Could not get your location.`;
      showToast(message, "error");
    },
    [t, locateViaIp, updateParams, country],
  );

  /* ────────── Compare selection (localStorage, plan-gated cap) ────────── */
  // Cap at 4 for now — Pas 6.4.1 wires plan-gating once the feature catalog
  // exposes the per-tier limit. Free legacy was 2 / priority 5; 4 is a
  // reasonable midpoint until the gate ships.
  const compare = useCompareSelection(4);
  const [compareOpen, setCompareOpen] = useState(false);

  /* ────────── Filter modal — derived inputs ────────── */
  // SearchFiltersModal expects DropdownOption[] (value/label/sublabel) — map
  // SpecialtyOption[] / ServiceOption[] to that shape using the locale-aware
  // display name helpers. Same for the live search-result variants.
  const specOptions = useMemo(
    () =>
      specialties.map((s) => ({
        value: s.code,
        label: getSpecialtyDisplayName(s, i18n.locale),
      })),
    [specialties, i18n.locale],
  );
  const specSearchOptions = useMemo(
    () =>
      (specSearchResults ?? []).map((s) => ({
        value: s.code,
        label: getSpecialtyDisplayName(s, i18n.locale),
      })),
    [specSearchResults, i18n.locale],
  );
  const serviceOptions = useMemo(
    () =>
      services.map((s) => ({
        value: s.code,
        label: getServiceDisplayName(s, i18n.locale),
        category: s.category,
      })),
    [services, i18n.locale],
  );

  /* Service-category scope for the desktop "Services" filter chip strip.
     Maps the active sidebar context (Consult vs Servicii vertical) to the
     subset of MedicalService.category values worth surfacing:
       consult                       → consultation / procedure / diagnostic
       servicii hospital/clinic/poly → all 5 (broad medical facility)
       servicii dental_clinic        → consultation + procedure
       servicii lab                  → lab
       servicii imaging              → imaging
       servicii pharmacy/optical/…   → null → hide the filter entirely
     `null` signals "no meaningful service taxonomy for this entity type" so
     the section can be skipped rather than rendering an empty / nonsensical
     picker. */
  const relevantServiceCategories = useMemo<string[] | null>(() => {
    if (sidebarMode === "consult") return ["consultation", "procedure", "diagnostic"];
    const key = currentServiciiVertical?.key;
    if (key === "hospital" || key === "clinic" || key === "polyclinic") {
      return ["consultation", "procedure", "diagnostic", "lab", "imaging"];
    }
    if (key === "dental_clinic") return ["consultation", "procedure"];
    if (key === "lab") return ["lab"];
    if (key === "imaging") return ["imaging"];
    return null;
  }, [sidebarMode, currentServiciiVertical]);

  /* Visible services row for the Services SidebarFilterSection. Filters
     the loaded country-services to the categories declared by
     `relevantServiceCategories`, then caps at 24 chips so the More filters
     disclosure stays digestible. Falls back to the full list when no
     category scope applies (shouldn't happen because the section is
     hidden when relevantServiceCategories is null, but keeps the memo
     defensive). */
  const visibleServiceOptions = useMemo(() => {
    if (!relevantServiceCategories) return [];
    const allowed = new Set(relevantServiceCategories);
    return serviceOptions
      .filter((s) => (s.category ? allowed.has(s.category) : false))
      .slice(0, 24);
  }, [serviceOptions, relevantServiceCategories]);
  const svcSearchOptions = useMemo(
    () =>
      (svcSearchResults ?? []).map((s) => ({
        value: s.code,
        label: getServiceDisplayName(s, i18n.locale),
      })),
    [svcSearchResults, i18n.locale],
  );

  // Cross-border neighbor options + currently selected codes (CSV in URL).
  // `NEIGHBOR_COUNTRIES` lists geographic neighbors (incl. unsupported ones
  // like TR/UA/MD) — filter to only platform-supported countries so the chip
  // list never renders flag-less / name-less stubs.
  const modalNeighborOptions = useMemo(() => {
    const codes = NEIGHBOR_COUNTRIES[country.toLowerCase()] ?? [];
    return codes
      .filter((code) => isSupportedCountry(code.toLowerCase()))
      .map((code) => ({
        code: code.toLowerCase(),
        flag: COUNTRY_FLAGS[code.toLowerCase()] ?? "",
        name: COUNTRY_NAMES_EN[code.toLowerCase()] ?? code.toUpperCase(),
      }));
  }, [country]);

  // Selected neighbor countries — CSV `?countries=BG,HU,...` (UPPERCASE ISO).
  // Only meaningful when scope === "cross"; per-neighbor chips toggle inclusion.
  const selectedNeighborCodes = useMemo<string[]>(() => {
    if (!search.countries) return [];
    return search.countries
      .split(",")
      .map((s) => s.trim().toUpperCase())
      .filter(Boolean);
  }, [search.countries]);

  // Countries whose Natural Earth outline gets drawn on the map. Local =
  // primary country; cross-border = primary + every selected neighbor; global
  // = none (the whole world is in scope, no point dimming anything).
  const boundaryCountryCodes = useMemo<string[]>(() => {
    if (scope === "global") return [];
    if (scope === "cross") {
      const primary = country.toUpperCase();
      const set = new Set([primary, ...selectedNeighborCodes]);
      return [...set];
    }
    return [country.toUpperCase()];
  }, [scope, country, selectedNeighborCodes]);
  const { features: countryBoundaryFeatures } = useCountriesGeoJson(boundaryCountryCodes);

  // Near-me radius mask — when active, the map shows a dimmed circle around
  // the user's GPS instead of the country outline (the two are mutually
  // exclusive in `<SearchMap>`). Coords come from `?lat`/`?lng`, radius from
  // `?distance` (km, defaults to 5 to match `applyNearMe`).
  const nearMeMapCenter = useMemo<[number, number] | null>(() => {
    if (search.nearMe !== "true") return null;
    const lat = typeof search.lat === "string" ? parseFloat(search.lat) : NaN;
    const lng = typeof search.lng === "string" ? parseFloat(search.lng) : NaN;
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
    return [lat, lng];
  }, [search.nearMe, search.lat, search.lng]);
  const nearMeRadiusKm = useMemo(() => {
    const d = typeof search.distance === "string" ? parseInt(search.distance, 10) : NaN;
    return Number.isFinite(d) && d > 0 ? d : 5;
  }, [search.distance]);

  // Final boundary features pumped into <SearchMap>. Three mutually-exclusive
  // modes (most specific wins):
  //   1. "Search this area" (URL bbox) — synthesize a Polygon from the bbox so
  //      the same mask + outline machinery dims everything outside the user's
  //      committed viewport.
  //   2. Country / cross-border — Natural Earth features (already computed).
  //   3. Near-me — handled separately via `nearMeMapCenter`; we return null
  //      here so the country mask doesn't fight the radius circle.
  const mapBoundaryFeatures = useMemo<Feature[] | null>(() => {
    if (nearMeMapCenter) return null;
    if (urlMapBounds) {
      const { north, south, east, west } = urlMapBounds;
      // Encode the bbox coords into ISO_A2 so <CountryBoundaryLayer>'s
      // cacheKey (joined ISO_A2 of every feature) changes whenever the
      // user re-commits a different viewport via "Search this area".
      // Without this, the bbox key would be a constant string and the
      // effect wouldn't re-fire when only the coordinates change.
      const bboxKey = `__BBOX__:${north.toFixed(4)},${south.toFixed(4)},${east.toFixed(4)},${west.toFixed(4)}`;
      return [
        {
          type: "Feature",
          properties: { ISO_A2: bboxKey },
          geometry: {
            type: "Polygon",
            coordinates: [
              [
                [west, south],
                [east, south],
                [east, north],
                [west, north],
                [west, south],
              ],
            ],
          },
        },
      ];
    }
    return countryBoundaryFeatures;
  }, [nearMeMapCenter, urlMapBounds, countryBoundaryFeatures]);

  // Selected cross-border country codes (UPPERCASE). Forwarded to the modal's
  // video / cross-border picker so per-neighbor chips can render as active.
  // Reads the same `search.countries` CSV the sidebar uses — single source.
  const modalSelectedCountries: string[] | null =
    selectedNeighborCodes.length > 0 ? selectedNeighborCodes : null;
  const modalSearchMode: "country" | "nearby" | "online" =
    scope === "global" ? "online" : scope === "cross" ? "nearby" : "country";
  const modalCurrentCountryName = COUNTRY_NAMES_EN[country.toLowerCase()] ?? country.toUpperCase();
  const modalCurrentCountryFlag = COUNTRY_FLAGS[country.toLowerCase()] ?? "";

  // Build the home-visit address from URL state. Only meaningful when
  // hasHomeVisit=true (set by StepWhere's geocoder picker).
  const homeAddress = useMemo<SearchFiltersState["homeAddress"]>(() => {
    if (search.hasHomeVisit !== "true") return null;
    const lat = Number(search.lat);
    const lng = Number(search.lng);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
    // `homeTag` carries the full formatted address (preferred for display).
    // Fall back to `homeCity` for legacy URLs, then to coords as last resort
    // so the picker never renders blank.
    return {
      formatted: search.homeTag ?? search.homeCity ?? `${lat.toFixed(4)}, ${lng.toFixed(4)}`,
      lat,
      lng,
      city: search.homeCity ?? "",
    };
  }, [search.hasHomeVisit, search.lat, search.lng, search.homeCity, search.homeTag]);

  // Sidebar home-visit address picker — debounced /api/geocode?q=, writes
  // `hasHomeVisit=true` + `lat/lng` + `homeCity` to URL on pick. Mirrors the
  // legacy wizard StepWhere flow so address state round-trips identically.
  const [homeAddrQuery, setHomeAddrQuery] = useState("");
  const [homeAddrSuggestions, setHomeAddrSuggestions] = useState<HomeAddrResult[]>([]);
  const [homeAddrSearching, setHomeAddrSearching] = useState(false);
  const homeAddrDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const homeAddrAbortRef = useRef<AbortController | null>(null);

  // Sync local input with URL state when user navigates / clears externally.
  useEffect(() => {
    setHomeAddrQuery(homeAddress?.formatted ?? "");
  }, [homeAddress?.formatted]);

  const searchHomeAddress = useCallback(
    (q: string) => {
      if (homeAddrDebounceRef.current) clearTimeout(homeAddrDebounceRef.current);
      if (homeAddrAbortRef.current) homeAddrAbortRef.current.abort();
      if (q.trim().length < 3) {
        setHomeAddrSuggestions([]);
        setHomeAddrSearching(false);
        return;
      }
      setHomeAddrSearching(true);
      homeAddrDebounceRef.current = setTimeout(() => {
        const controller = new AbortController();
        homeAddrAbortRef.current = controller;
        const countryParam = country ? `&country=${country}` : "";
        void apiFetch
          .get<{ results?: HomeAddrResult[] }>(
            `/api/geocode?q=${encodeURIComponent(q)}${countryParam}`,
            { signal: controller.signal },
          )
          .then((data) => {
            setHomeAddrSuggestions(data.results ?? []);
            setHomeAddrSearching(false);
          })
          .catch((err: unknown) => {
            if (err instanceof DOMException && err.name === "AbortError") return;
            setHomeAddrSearching(false);
          });
      }, 400);
    },
    [country],
  );

  useEffect(() => {
    return () => {
      if (homeAddrDebounceRef.current) clearTimeout(homeAddrDebounceRef.current);
      if (homeAddrAbortRef.current) homeAddrAbortRef.current.abort();
    };
  }, []);

  const pickHomeAddress = useCallback(
    (r: HomeAddrResult) => {
      updateParams({
        hasHomeVisit: "true",
        lat: String(r.lat),
        lng: String(r.lng),
        // `homeCity` keeps just the city for the API contract; `homeTag`
        // carries the full formatted address for pill / picker display.
        homeCity: r.city ?? null,
        homeTag: r.formattedAddress,
        // Home visit pins to a coord — clear nearMe / city / map bounds.
        nearMe: null,
        distance: null,
        city: null,
        north: null,
        south: null,
        east: null,
        west: null,
      });
      // Persist for the next session so flipping to home_visit on a fresh
      // visit auto-restores instead of forcing a re-search.
      saveHomeAddress({
        lat: r.lat,
        lng: r.lng,
        city: r.city ?? null,
        formattedAddress: r.formattedAddress,
      });
      setHomeAddrQuery(r.formattedAddress);
      setHomeAddrSuggestions([]);
    },
    [updateParams],
  );

  const clearHomeAddress = useCallback(() => {
    updateParams({
      hasHomeVisit: null,
      lat: null,
      lng: null,
      homeCity: null,
      homeTag: null,
    });
    // Explicit clear = user no longer wants the saved address — drop it
    // from localStorage too (otherwise the next home_visit pick would
    // restore the same address they just cleared).
    clearSavedHomeAddress();
    setHomeAddrQuery("");
    setHomeAddrSuggestions([]);
  }, [updateParams]);

  // Derive `where` from URL — nearMe / city / mode=online / null.
  const filterWhere: SearchFiltersState["where"] = useMemo(() => {
    if (search.nearMe === "true") return "nearby";
    if (search.city) return "city";
    if (modalSearchMode === "online") return "online";
    return null;
  }, [search.nearMe, search.city, modalSearchMode]);

  // Derive `vertical` from URL `tab` + first specialty code (mirrors legacy
  // search-client.tsx `activeVertical`).
  const filterVertical: SearchFiltersState["vertical"] = useMemo(() => {
    const tab = search.tab;
    if (tab === "hospital") return "hospital";
    if (tab === "clinic") return "clinic";
    if (tab === "medical_center") return "polyclinic";
    if (tab === "dental_clinic") return "dental_clinic";
    if (tab === "pharmacy") return "pharmacy";
    if (tab === "laboratory") return "lab";
    if (tab === "imaging") return "imaging";
    if (tab === "optical") return "optical";
    if (tab === "homecare") return "homecare";
    if (tab === "medical_transport") return "medical_transport";
    if (tab === "equipment") return "equipment";
    if (tab === "provider") {
      const specs = (search.specialties ?? search.specialty ?? "")
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);
      if (specs.includes("STO")) return "dental";
      if (specs.includes("FIZ")) return "physio";
      if (specs.includes("MOA")) return "midwife";
      if (specs.includes("NUT")) return "nutrition";
      if (specs.includes("PSI") || specs.includes("PSH")) return "mental_health";
      return "provider";
    }
    return null;
  }, [search.tab, search.specialties, search.specialty]);

  const filterModalConsultationType: ConsultationType | null = useMemo(() => {
    if (search.consultationType === "video") return "video";
    if (search.consultationType === "home_visit") return "home_visit";
    if (search.consultationType === "in_office") return "in_person";
    return null;
  }, [search.consultationType]);

  const filterModalValues: SearchFiltersState = useMemo(() => {
    const csvToArr = (v: string | undefined) =>
      v
        ? v
            .split(",")
            .map((s) => s.trim())
            .filter(Boolean)
        : [];
    return {
      vertical: filterVertical,
      consultationType: filterModalConsultationType,
      specialties: search.specialties
        ? csvToArr(search.specialties)
        : search.specialty
          ? [search.specialty]
          : [],
      services: search.services
        ? csvToArr(search.services)
        : search.service
          ? [search.service]
          : [],
      where: filterWhere,
      citySlug: search.city ?? null,
      nearbyDistanceKm: Number(search.distance) || 10,
      homeAddress,
      insurance: (search.insurance ?? "all") as SearchFiltersState["insurance"],
      ratingMin: (search.ratingMin ?? null) as SearchFiltersState["ratingMin"],
      hasVideo: search.consultationType === "video",
      isVerified: search.isVerified === "true",
      gender: (search.gender ?? null) as SearchFiltersState["gender"],
      languages: csvToArr(search.languages),
      audience: csvToArr(search.audience),
      minPrice: search.minPrice ? Number(search.minPrice) : null,
      maxPrice: search.maxPrice ? Number(search.maxPrice) : null,
      openNow: search.isOpenNow === "true",
      is24h: search.is24h === "true",
      hasDelivery: search.hasDelivery === "true",
      hasHomeDraw: search.hasHomeCollection === "true",
      hasParking: search.hasParking === "true",
      hasWheelchairAccess: search.hasWheelchairAccess === "true",
      hasElevator: search.hasElevator === "true",
      hasAccessibleRestroom: search.hasAccessibleRestroom === "true",
      hasAirConditioning: search.hasAirConditioning === "true",
      hasWifi: search.hasWifi === "true",
      hasChildrenArea: search.hasChildrenArea === "true",
      hasBreastfeedingArea: search.hasBreastfeedingArea === "true",
      hasPublicTransport: search.hasPublicTransport === "true",
      when: search.when ?? null,
      dateFrom: search.dateFrom ?? null,
      dateTo: search.dateTo ?? null,
    };
  }, [
    filterVertical,
    filterModalConsultationType,
    search.specialties,
    search.specialty,
    search.services,
    search.service,
    filterWhere,
    search.city,
    search.distance,
    homeAddress,
    search.insurance,
    search.ratingMin,
    search.consultationType,
    search.isVerified,
    search.gender,
    search.languages,
    search.audience,
    search.minPrice,
    search.maxPrice,
    search.isOpenNow,
    search.is24h,
    search.hasDelivery,
    search.hasHomeCollection,
    search.hasParking,
    search.hasWheelchairAccess,
    search.hasElevator,
    search.hasAccessibleRestroom,
    search.hasAirConditioning,
    search.hasWifi,
    search.hasChildrenArea,
    search.hasBreastfeedingArea,
    search.hasPublicTransport,
    search.when,
    search.dateFrom,
    search.dateTo,
  ]);

  /* ────────── Active filter pills + map-bounds chip ────────── */

  type SectionPillData = { id: string; label: string; onRemove: () => void };

  /* "Meaningful" filter count — mirrors the modal's `canCloseCount`
     contract: `activeCount` minus auto-seeded language(s). When this drops
     to 0 the modal auto-reopens (you can't be in a state where the search
     is unconstrained), regardless of *which* removal path triggered it
     (per-card X · catch-all pill X · "Clear all" · pill row sweep). */
  const meaningfulFilterCount =
    (filterModalValues.vertical ? 1 : 0) +
    (filterModalValues.consultationType ? 1 : 0) +
    filterModalValues.specialties.length +
    filterModalValues.services.length +
    (filterModalValues.where ? 1 : 0) +
    (filterModalValues.homeAddress ? 1 : 0) +
    (filterModalValues.insurance !== "all" ? 1 : 0) +
    (filterModalValues.ratingMin ? 1 : 0) +
    (filterModalValues.isVerified ? 1 : 0) +
    (filterModalValues.gender ? 1 : 0) +
    filterModalValues.audience.length +
    (filterModalValues.minPrice != null || filterModalValues.maxPrice != null ? 1 : 0);

  useEffect(() => {
    // Mirror: whenever the wizard opens (via the modal's promo card or any
    // other entry point), close the filter modal so the two sheets don't
    // stack. The wizard removes the URL params on its own dismiss, at
    // which point the auto-reopen effect above takes over.
    if (search.wizard === "1" && filtersOpen) {
      setFiltersOpen(false);
    }
  }, [search.wizard, filtersOpen]);

  const activePills = useActivePills({
    params: search,
    values: filterModalValues,
    specOptions,
    serviceOptions,
    topCities,
    countryCatalog: countriesData ?? undefined,
  });

  /* ────────── Tier-2 per-card pills (desktop sidebar) ──────────
     Each multi-select sidebar card hosts its own pill cluster so users see
     and clear individual selections inside the section that owns them. The
     bottom `<ActiveFilterPills>` row on desktop filters these out via
     `desktopVisiblePills` to avoid double-render. Mobile sticky pill row is
     untouched (no card structure on mobile) so it keeps `activePills`. */

  const specialtyCardPills = useMemo<SectionPillData[]>(
    () =>
      filterModalValues.specialties.map((code) => {
        const match = specOptions.find((o) => o.value === code);
        return {
          id: `specialty:${code}`,
          label: match?.label || code,
          onRemove: () => {
            const remaining = filterModalValues.specialties.filter((c) => c !== code);
            updateParams({
              specialties: remaining.length > 0 ? remaining.join(",") : null,
              specialty: null,
            });
            // Removing the last pill empties the filter — open the card so
            // the user can pick again without manually expanding it.
            if (remaining.length === 0) setOpenSidebarSection("specialty");
          },
        };
      }),
    [filterModalValues.specialties, specOptions, updateParams],
  );

  // AI route chip under the hero bar — toggles one code through the same
  // `specialties` CSV the dropdown's AI section (`onSelectAiSpecialties`)
  // and the specialty pills write.
  const toggleAiRouteSpecialty = useCallback(
    (code: string) => {
      const current = filterModalValues.specialties;
      const next = current.includes(code) ? current.filter((c) => c !== code) : [...current, code];
      updateParams({
        specialties: next.length > 0 ? next.join(",") : null,
        specialty: null,
      });
    },
    [filterModalValues.specialties, updateParams],
  );

  /* Services section pill list — mirrors specialtyCardPills. Selected
     services render as red SectionPills in the Services card header so
     the user can remove them with one click without opening the card. */
  const serviceCardPills = useMemo<SectionPillData[]>(
    () =>
      filterModalValues.services.map((code) => {
        const match = serviceOptions.find((o) => o.value === code);
        return {
          id: `service:${code}`,
          label: match?.label || code,
          onRemove: () => {
            const remaining = filterModalValues.services.filter((c) => c !== code);
            updateParams({
              services: remaining.length > 0 ? remaining.join(",") : null,
              service: null,
            });
            if (remaining.length === 0) setOpenSidebarSection("services");
          },
        };
      }),
    [filterModalValues.services, serviceOptions, updateParams],
  );

  // Localized language display name — mirrors the modal's `languageName`
  // so pills + chips read identical regardless of where the user toggled
  // the filter from. Falls back to upper-cased code if Intl.DisplayNames
  // is unavailable for the active locale.
  const languageName = useMemo(() => {
    try {
      const dn = new Intl.DisplayNames([i18n.locale], { type: "language" });
      return (code: string) => dn.of(code) ?? code.toUpperCase();
    } catch {
      return (code: string) => code.toUpperCase();
    }
  }, [i18n.locale]);

  const languageCardPills = useMemo<SectionPillData[]>(
    () =>
      filterModalValues.languages.map((lang) => ({
        id: `lang:${lang}`,
        label: languageName(lang),
        onRemove: () => {
          const remaining = filterModalValues.languages.filter((l) => l !== lang);
          updateParams({ languages: remaining.length > 0 ? remaining.join(",") : null });
          if (remaining.length === 0) setOpenSidebarSection("languages");
        },
      })),
    [filterModalValues.languages, languageName, updateParams],
  );

  // ── When (time-of-day quick picker) ──
  // URL is the source of truth; the inputs below mirror `dateFrom`/`dateTo`
  // and only commit on blur to avoid spamming router updates while typing.
  const whenChoice = (search.when ?? null) as
    | "now"
    | "today"
    | "tomorrow"
    | "this_week"
    | "custom"
    | null;
  const [customWhenFrom, setCustomWhenFrom] = useState(search.dateFrom ?? "");
  const [customWhenTo, setCustomWhenTo] = useState(search.dateTo ?? "");
  useEffect(() => {
    setCustomWhenFrom(search.dateFrom ?? "");
  }, [search.dateFrom]);
  useEffect(() => {
    setCustomWhenTo(search.dateTo ?? "");
  }, [search.dateTo]);

  const setWhen = useCallback(
    (key: "now" | "today" | "tomorrow" | "this_week" | "custom") => {
      // Tap on the active card → clear the entire When filter.
      if (whenChoice === key) {
        updateParams({ when: null, dateFrom: null, dateTo: null });
        // Toggling off in the sidebar — collapse so the cleared state mirrors
        // the rest of the accordion (no card opened with no value set).
        setOpenSidebarSection((cur) => (cur === "when" ? null : cur));
        return;
      }
      // Switching between quick-picks wipes any previous custom range.
      const updates: Record<string, string | null> = { when: key };
      if (key !== "custom") {
        updates.dateFrom = null;
        updates.dateTo = null;
      }
      updateParams(updates);
      // Quick-pick committed → collapse the sidebar When card. For "custom"
      // we keep it open because the date inputs still need to be visible.
      if (key !== "custom") {
        setOpenSidebarSection((cur) => (cur === "when" ? null : cur));
      }
    },
    [whenChoice, updateParams],
  );

  const commitCustomWhen = useCallback(() => {
    if (customWhenFrom && customWhenTo) {
      updateParams({
        when: "custom",
        dateFrom: customWhenFrom,
        dateTo: customWhenTo,
      });
      // Range committed → collapse the sidebar When card.
      setOpenSidebarSection((cur) => (cur === "when" ? null : cur));
    }
  }, [customWhenFrom, customWhenTo, updateParams]);

  // Section summary — formatted ranged for `custom`, copy label otherwise.
  const whenSummary = useMemo<string>(() => {
    if (!whenChoice) return t`Anytime`;
    if (whenChoice !== "custom") return whenCopy.label(whenChoice);
    if (search.dateFrom && search.dateTo) {
      const fmt = new Intl.DateTimeFormat(i18n.locale, {
        day: "numeric",
        month: "short",
      });
      return `${fmt.format(new Date(search.dateFrom))} – ${fmt.format(new Date(search.dateTo))}`;
    }
    return whenCopy.label("custom");
  }, [whenChoice, search.dateFrom, search.dateTo, whenCopy, t, i18n.locale]);

  const whenCardPills = useMemo<SectionPillData[]>(() => {
    if (!whenChoice) return [];
    return [
      {
        id: `when:${whenChoice}`,
        label: whenSummary,
        onRemove: () => updateParams({ when: null, dateFrom: null, dateTo: null }),
      },
    ];
  }, [whenChoice, whenSummary, updateParams]);

  const insuranceCardPills = useMemo<SectionPillData[]>(() => {
    const ins = filterModalValues.insurance;
    if (!ins || ins === "all") return [];
    // Use the same label set as the modal so pill / chip / radio all read
    // identical regardless of where the user toggled the filter from.
    const labels: Record<string, string> = {
      cas: t`Public (CAS)`,
      private: t`Private insurance`,
      direct: t`Direct payment`,
    };
    return [
      {
        id: `insurance:${ins}`,
        label: labels[ins] ?? ins,
        onRemove: () => {
          updateParams({ insurance: null });
          // Single-value filter — removal always empties → open card.
          setOpenSidebarSection("insurance");
        },
      },
    ];
  }, [filterModalValues.insurance, t, updateParams]);

  const consultationCardPills = useMemo<SectionPillData[]>(() => {
    const ct = search.consultationType;
    if (!ct) return [];
    const labels: Record<string, string> = {
      video: t`Video`,
      in_person: t`In person`,
      in_office: t`In person`,
      home_visit: t`Home visit`,
    };
    return [
      {
        id: `consultation:${ct}`,
        label: labels[ct] ?? ct,
        onRemove: () => {
          updateParams({ consultationType: null });
          setOpenSidebarSection("consultation");
        },
      },
    ];
  }, [search.consultationType, t, updateParams]);

  const priceCardPills = useMemo<SectionPillData[]>(() => {
    const lo = filterModalValues.minPrice;
    const hi = filterModalValues.maxPrice;
    if (lo == null && hi == null) return [];
    const loStr = lo ?? 0;
    const hiStr = hi == null || hi >= PRICE_CEILING ? `${PRICE_CEILING}+` : hi;
    return [
      {
        id: "price",
        label: `${loStr} – ${hiStr} RON`,
        onRemove: () => {
          updateParams({ minPrice: null, maxPrice: null });
          setOpenSidebarSection("price");
        },
      },
    ];
  }, [filterModalValues.minPrice, filterModalValues.maxPrice, updateParams]);

  /* Where-card pills — country, cross-border scope, city, near-me. Pulled
     from `activePills` (canonical source for label + remove dict) and
     reshaped to the `SectionPillData` contract (`onRemove` closure that
     calls updateParams). Hidden for home_visit consultations because those
     dimensions don't apply (the card shows the address picker instead).
     Country pill is special-cased: tapping it (or its X) opens the
     CountryPickerSheet instead of resetting to DEFAULT_COUNTRY — matches
     the mobile pill flow and user mental model ("clicking country = switch
     it, not remove it"). */
  const whereCardPills = useMemo<SectionPillData[]>(() => {
    if (search.consultationType === "home_visit") return [];
    const filtered = activePills.filter((p) => {
      if (p.id.startsWith("country:")) return true;
      if (p.id === "scope:global") return true;
      if (p.id.startsWith("cross:")) return true;
      if (p.id.startsWith("city:")) return true;
      if (p.id === "nearby") return true;
      return false;
    });
    return filtered.map((p) => ({
      id: p.id,
      label: p.label,
      onRemove: p.id.startsWith("country:")
        ? () => setCountrySwitchOpen(true)
        : () => {
            updateParams(p.remove);
            // Removing any where-pill is almost always followed by "pick a
            // different city/country" — open the Where card unconditionally so
            // the picker is back on screen with no extra click.
            setOpenSidebarSection("where");
          },
    }));
  }, [activePills, search.consultationType, updateParams]);

  /* Mobile sticky pill row — same as `activePills` minus the vertical pill
     (now conveyed by the mobile slider switch added above the search hero).
     The rest of the pills stay because mobile has no sidebar cards to host
     them inside. Country pill tap is rerouted to the country picker sheet
     instead of the default "reset to RO" remove flow — the X glyph still
     reads as removable but the user's intent on tapping the country is
     usually "switch to a different one". */
  const mobileVisiblePills = useMemo<ActiveFilterPill[]>(
    () =>
      activePills
        .filter((p) => !p.id.startsWith("vertical:"))
        .map((p) =>
          p.id.startsWith("country:") ? { ...p, onClick: () => setCountrySwitchOpen(true) } : p,
        ),
    [activePills],
  );

  // One active-filter count for every "Adjust search" entry point (hero CTA
  // and the mobile bottom-tab badge): the visible pills plus the map-area
  // bound, so both always show the same number. The country pill is left
  // out — it is browsing context (always present, switched via the country
  // picker), not a filter the user set, so it would pin the badge at ≥1.
  const activeFilterCount =
    mobileVisiblePills.filter((p) => !p.id.startsWith("country:")).length + (hasMapBounds ? 1 : 0);

  /* Tip card pill — single-pill array (or empty). Mirrors the SectionPill
     pattern used by every other inline sidebar card so the active service
     type renders as a red removable pill in the collapsed card header.
     Removal clears the vertical (back to "kind=businesses no Tip"). */
  const tipCardPills = useMemo<SectionPillData[]>(() => {
    if (!currentServiciiVertical) return [];
    const labels: Record<ServiciiVertical["key"], string> = {
      hospital: t`Hospitals`,
      clinic: t`Clinics`,
      polyclinic: t`Polyclinics`,
      dental_clinic: t`Dental offices`,
      pharmacy: t`Pharmacy`,
      lab: t`Lab`,
      imaging: t`Imaging`,
      optical: t`Optical`,
      homecare: t`Home care`,
      medical_transport: t`Medical transport`,
      equipment: t`Medical equipment`,
    };
    return [
      {
        id: `tip:${currentServiciiVertical.tab}`,
        label: labels[currentServiciiVertical.key],
        onRemove: () => {
          setServiciiVertical(null);
          // Pop the Tip card back open after clearing — user almost always
          // wants to pick a different vertical (mirrors the Specialty card's
          // auto-open-on-last-pill-remove behavior).
          setOpenSidebarSection("serviciiTip");
        },
      },
    ];
  }, [currentServiciiVertical, setServiciiVertical, t]);

  /* Mobile Servicii vertical descriptors — SERVICII_VERTICALS enriched with
     i18n'd labels so MobileSearchHero can render the Tip dropdown options
     without re-importing the Lingui macro inside the component. */
  const mobileServiciiVerticals = useMemo(() => {
    const labels: Record<ServiciiVertical["key"], string> = {
      hospital: t`Hospitals`,
      clinic: t`Clinics`,
      polyclinic: t`Polyclinics`,
      dental_clinic: t`Dental offices`,
      pharmacy: t`Pharmacy`,
      lab: t`Lab`,
      imaging: t`Imaging`,
      optical: t`Optical`,
      homecare: t`Home care`,
      medical_transport: t`Medical transport`,
      equipment: t`Medical equipment`,
    };
    return SERVICII_VERTICALS.map((v) => ({ tab: v.tab, label: labels[v.key] }));
  }, [t]);

  /* ────────── Desktop hero — section labels ──────────
     Each section in the 3-section search card surfaces the current selection
     (or a placeholder). The actual controls live in the filter modal — these
     are read-only summaries. */
  const heroLocationLabel = useMemo<string | null>(() => {
    // Where now adapts to the consultation type:
    //   home_visit → show the picked address (city fallback)
    //   video      → country only matters for licensing — show its name
    //   else       → city / near-me / cross-border (legacy in-person flow)
    if (search.consultationType === "home_visit") {
      return homeAddress?.formatted ?? homeAddress?.city ?? null;
    }
    if (search.consultationType === "video") {
      if (scope === "global") return t`All countries`;
      return modalCurrentCountryName;
    }
    if (search.nearMe === "true") return t`Near me`;
    if (search.city) {
      // Prefer the localized city name from the catalog when available so
      // diacritics + i18n labels show up; fall back to a titlecased slug
      // (e.g. "664437__turnu-magurele" → "Turnu Magurele") if the city
      // hasn't been loaded yet.
      const cityId = parseCitySlug(search.city);
      const match = topCities.find((c) => c.id === cityId || String(c.geonameId) === cityId);
      return match ? getCityDisplayName(match, i18n.locale) : cityNameFromSlug(search.city);
    }
    if (scope === "cross") return t`Cross-border`;
    return null;
  }, [
    search.nearMe,
    search.city,
    search.consultationType,
    scope,
    topCities,
    i18n.locale,
    homeAddress,
    modalCurrentCountryName,
    t,
  ]);

  /* Hero popovers (Location + Moment zones) were removed when those zones
   * moved to the sidebar. The state + click-outside effect went with them.
   * Zone 1 (UniversalSearch) manages its own focus state. The radius
   * "≤ X km" hint and the consultation-type / moment label are now
   * surfaced directly inside the sidebar Where + Consultation type cards. */

  // Location popover handlers — match the filter modal's "Where" semantics so
  // results stay consistent regardless of entry point. handleNearMe defers
  // the URL write until coords are resolved (success), so the URL doesn't
  // momentarily carry `nearMe=true` without `lat`/`lng`.
  const applyNearMe = useCallback(
    (km: number) => {
      void handleNearMe(km);
    },
    [handleNearMe],
  );
  const applyCity = useCallback(
    (slug: string, cityCountryCode?: string) => {
      const updates: Record<string, string | null> = {
        city: slug,
        nearMe: null,
        lat: null,
        lng: null,
        distance: null,
        north: null,
        south: null,
        east: null,
        west: null,
      };
      // A picked city pins the search to one specific place — drop any
      // cross/global scope + neighbor selection, and switch the URL country
      // to match the city's country if it's a supported, different one.
      const cc = cityCountryCode?.toLowerCase();
      if (cc && isSupportedCountry(cc) && cc !== country.toLowerCase()) {
        updates.country = cc.toUpperCase();
      }
      if (scope) {
        updates.scope = null;
        updates.countries = null;
      }
      updateParams(updates);
    },
    [updateParams, country, scope],
  );

  // Video country picker — multi-select chips filtered by a free-text
  // search. Excludes the current country (always shown as a fixed chip
  // above) and anything not `platformStatus = "open"`.
  const [videoCountryQuery, setVideoCountryQuery] = useState("");
  const videoCountryOptions = useMemo(() => {
    if (!countriesData) return [];
    const currentUpper = country.toUpperCase();
    const q = videoCountryQuery.trim().toLowerCase();
    return countriesData
      .filter((c) => c.platformStatus === "open" && c.code !== currentUpper)
      .map((c) => ({
        code: c.code,
        flag: c.flag,
        // LocalizedString → pick i18n locale, fallback en, fallback code.
        name: c.name[i18n.locale] || c.name.en || c.code,
      }))
      .filter((c) => !q || c.name.toLowerCase().includes(q) || c.code.toLowerCase().includes(q))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [countriesData, country, videoCountryQuery, i18n.locale]);

  // Video-mode country toggle — same shape as `toggleNeighborCountry` but
  // also flips `scope=cross` when adding the first one and clears `scope`
  // when removing the last (keeps URL minimal in the default "local-only"
  // shape). When already on `scope=global`, picking a specific country
  // demotes to `scope=cross` with just that selection.
  const toggleVideoCountry = useCallback(
    (code: string) => {
      const upper = code.toUpperCase();
      if (scope === "global") {
        updateParams({ scope: "cross", countries: upper });
        return;
      }
      const next = selectedNeighborCodes.includes(upper)
        ? selectedNeighborCodes.filter((c) => c !== upper)
        : [...selectedNeighborCodes, upper];
      updateParams({
        countries: next.length > 0 ? next.join(",") : null,
        scope: next.length > 0 ? "cross" : null,
      });
    },
    [scope, selectedNeighborCodes, updateParams],
  );

  // Country switch sheet — opened from the cabinet "Schimbă" button next to
  // the country pill. Same `<CountryPickerSheet>` the header pill uses, but
  // wired into the find URL: picking a country writes `params.country` and
  // resets city + map bounds (stale slugs would otherwise survive the swap).
  const [countrySwitchOpen, setCountrySwitchOpen] = useState(false);

  // City search inside the location popover — reuses the same `useCities`
  // hook instance already mounted above. `searchCities(q)` is debounced
  // internally; on empty input we show `topCities` as a fallback list.
  const [heroCityQuery, setHeroCityQuery] = useState("");
  useEffect(() => {
    if (heroCityQuery.trim().length > 0) searchCitiesFn(heroCityQuery);
  }, [heroCityQuery, searchCitiesFn]);
  const heroCityList: CityOption[] =
    heroCityQuery.trim().length > 0 ? citySearchResults.slice(0, 12) : topCities.slice(0, 12);

  /* ────────── Render ────────── */
  return (
    <div
      className="text-foreground relative min-h-[100dvh] w-full"
      style={
        {
          // Search-pill header height (driven by ResizeObserver on the
          // sticky search header below).
          "--search-header-h": `${topBarHeight}px`,
          // Constant AppHeader height (h-16 + safe-area top inset). Combined
          // with --search-header-h to compute the total chrome offset.
          "--app-header-h": "calc(4rem + env(safe-area-inset-top))",
          "--chrome-h": "calc(var(--app-header-h) + var(--search-header-h))",
        } as React.CSSProperties
      }
    >
      {/* ─── Top header (logo + country + locale + notifications) — same as account ─── */}
      <AppHeader />

      {/* ─── Promo banner test grid (?promoTest=1) — short-circuits the
           normal /find layout so every banner variant is visible at once.
           Dev-only utility; remove the query param to restore the page. */}
      {promoTest && (
        <section className="mx-auto w-full max-w-2xl px-4 py-6 sm:px-6">
          <PromoBannerTestGrid locale={i18n.locale} country={country} />
        </section>
      )}

      {/* ─── Mobile map overlay — pinned below sticky header so filters +
           view toggle stay reachable. Desktop has its own always-mounted map
           in the 3-col grid below, so this drawer is mobile-only now. */}
      <div
        className={cn(
          "z-20 overflow-hidden md:hidden",
          mobileView === "map"
            ? "max-md:fixed max-md:inset-x-0 max-md:top-[var(--chrome-h)] max-md:bottom-0 max-md:z-20"
            : "max-md:hidden",
        )}
      >
        <div className="relative h-full w-full">
          {mapReady ? (
            <Suspense fallback={<MapSkeleton />}>
              <SearchMap
                results={results}
                highlightedId={highlightedId}
                onMarkerHover={setHighlightedId}
                onMarkerClick={(id) => {
                  setHighlightedId(id);
                  const el = document.getElementById(`result-${id}`);
                  if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
                }}
                center={mapCenter}
                zoom={mapZoom}
                fitKey={fitKey}
                onBoundsChange={handleBoundsChange}
                flyToTarget={flyTarget}
                flyKey={flyKey}
                countryFeatures={mapBoundaryFeatures}
                nearMeFacility={nearMeMapCenter}
                nearMeRadiusKm={nearMeRadiusKm}
                skipResultsAutoFit={hasMapBounds}
                onLocate={() => applyNearMe(Number(search.distance) || 5)}
                isLocating={isLocating}
                onMapClick={() => setHighlightedId(null)}
                isDark={mapIsDark}
                radarKey={radarKey}
              />
            </Suspense>
          ) : (
            <MapSkeleton />
          )}

          {/* "Search this area" — visible whenever the user has interacted
               with the map and the live viewport bbox no longer matches URL
               bounds. Stays visible on highlight too: bottom-anchored above
               the tab bar, bumped up by ~9rem while the peek card is open. */}
          <SearchAreaButton
            visible={
              mapReady &&
              !!liveBounds &&
              !boundsMatchUrl &&
              (desktopMapOpen || mobileView === "map")
            }
            onClick={commitCurrentBounds}
            edge="bottom"
            className={cn(
              "transition-[bottom] duration-200",
              highlightedId
                ? "bottom-[calc(3.5rem+max(0.25rem,env(safe-area-inset-bottom))+9.5rem)]"
                : "bottom-[calc(3.5rem+max(0.25rem,env(safe-area-inset-bottom))+0.75rem)]",
            )}
          />
        </div>
      </div>

      {/* Mobile hero — extracted to _find/mobile-search-hero.tsx. Wraps the
           title + 2-select quickbar (specialty / city) + full-width Refine
           CTA. Smart-search input was moved into the filter modal so this
           hero stays compact. Hidden when the user swipes to the map view. */}
      {mobileView === "list" && (
        <MobileSearchHero
          heroHeading={heroHeading}
          search={search}
          updateParams={updateParams}
          sidebarMode={sidebarMode}
          setSidebarMode={setSidebarMode}
          specOptions={specOptions}
          specSearchOptions={specSearchOptions}
          searchSpecialties={searchSpecialties}
          isSpecSearching={isSpecSearching}
          serviciiVerticals={mobileServiciiVerticals}
          setServiciiVerticalByTab={(tab) => {
            if (!tab) return setServiciiVertical(null);
            const strict = SERVICII_VERTICALS.find((v) => v.tab === tab);
            if (strict) setServiciiVertical(strict);
          }}
          topCities={topCities}
          citySearchResults={citySearchResults}
          searchCities={searchCitiesFn}
          applyCity={applyCity}
          country={country}
          selectedSpecialtyCodes={filterModalValues.specialties}
          activeFilterCount={activeFilterCount}
          onOpenFilters={() => setFiltersOpen(true)}
          onOpenSmartSearch={() => setSmartSearchOpen(true)}
        />
      )}

      {/* ─── Mobile: active filter pills (red removable pills + geo bounds chip).
           Sticks below the AppHeader (mobile h-14 = 3.5rem + safe-area-inset-top)
           so active filters stay visible as the user scrolls the results. Soft
           backdrop blur lets the content show through behind it. The pills sit
           inside a tinted "contained" block so the strip reads as a single
           unit. "Edit search" link removed — the Refine CTA in the hero is
           the single entry point to the modal. */}
      {(activePills.length > 0 || hasMapBounds) && (
        <div className="bg-background/80 supports-[backdrop-filter]:bg-background/60 border-border/40 sticky top-[calc(3.5rem+env(safe-area-inset-top))] z-20 border-b px-3 py-2 backdrop-blur-xl backdrop-saturate-150 md:hidden">
          <ActiveFilterPills
            pills={mobileVisiblePills}
            leading={<GeoBoundsChip active={hasMapBounds} />}
            onRemove={updateParams}
            onClearAll={() => {
              resetFilters();
              // Mobile list view: jump back to the top so the user sees the
              // fresh result set from the first card (otherwise the page
              // stays scrolled at whatever offset they were reading at).
              if (mobileView === "list" && typeof window !== "undefined") {
                window.scrollTo({ top: 0, behavior: "instant" as ScrollBehavior });
              }
            }}
          />
        </div>
      )}

      {/* ─── Filter modal ─── */}
      <SearchFiltersModal
        open={filtersOpen}
        onClose={() => setFiltersOpen(false)}
        locale={i18n.locale}
        country={country}
        updateParams={updateParams}
        values={filterModalValues}
        specOptions={specOptions}
        specSearchOptions={specSearchOptions}
        onSpecSearch={searchSpecialties}
        isSpecSearching={isSpecSearching}
        serviceOptions={serviceOptions}
        svcSearchOptions={svcSearchOptions}
        onSvcSearch={searchServices}
        isSvcSearching={isSvcSearching}
        neighborOptions={modalNeighborOptions}
        selectedCountries={modalSelectedCountries}
        searchMode={modalSearchMode}
        currentCountryName={modalCurrentCountryName}
        currentCountryFlag={modalCurrentCountryFlag}
        hasMapBounds={hasMapBounds}
        mapBoundsValue={urlMapBounds}
        onClearMapBounds={() => updateParams({ north: null, south: null, east: null, west: null })}
        onApplyMapBounds={(b) =>
          updateParams({
            north: String(b.north),
            south: String(b.south),
            east: String(b.east),
            west: String(b.west),
            city: null,
            nearMe: null,
            lat: null,
            lng: null,
            distance: null,
          })
        }
        resultsCount={total}
        hideVertical={isMobile}
        hideSpecialty={isMobile}
      />

      {/* Smart search overlay — UniversalSearch in `overlayOnly` mode so
          only the mobile fullscreen sheet renders (no stray compact input
          on the page). Portaled to <body> so the overlay's `fixed inset-0`
          can never be trapped by an ancestor containing block. */}
      {smartSearchOpen &&
        isMobile &&
        typeof document !== "undefined" &&
        createPortal(
          <UniversalSearch
            variant="compact"
            country={country}
            autoOpen
            overlayOnly
            onClose={() => setSmartSearchOpen(false)}
            onSearch={(q) => {
              executeSmartSearch(q);
              setSmartSearchOpen(false);
            }}
            onSelectSpecialty={(code) => {
              const next = filterModalValues.specialties.includes(code)
                ? filterModalValues.specialties
                : [...filterModalValues.specialties, code];
              updateParams({
                specialties: next.length > 0 ? next.join(",") : null,
                specialty: null,
              });
              setSmartSearchOpen(false);
            }}
            onSelectAiSpecialties={(codes) => {
              const merged = [...filterModalValues.specialties];
              for (const c of codes) if (!merged.includes(c)) merged.push(c);
              updateParams({
                specialties: merged.length > 0 ? merged.join(",") : null,
                specialty: null,
              });
              setSmartSearchOpen(false);
            }}
            onSelectQuery={(q) => {
              updateParams({ q });
              setSmartSearchOpen(false);
            }}
            onSelectQuickAction={(s) => {
              updateParams(s);
              setSmartSearchOpen(false);
            }}
            selectedSpecialtyCodes={filterModalValues.specialties}
          />,
          document.body,
        )}

      {/* Cabinet country switch — same picker the header pill uses. Picking
          a country updates the URL `country` param and clears the city +
          map bounds so the previous selection doesn't carry over to a
          different geography. */}
      <CountryPickerSheet
        open={countrySwitchOpen}
        mode="dismissible"
        current={country.toLowerCase()}
        onSelect={(c) => {
          updateParams({
            country: c.toUpperCase(),
            city: null,
            lat: null,
            lng: null,
            distance: null,
            nearMe: null,
            north: null,
            south: null,
            east: null,
            west: null,
          });
          setCountrySwitchOpen(false);
        }}
        onClose={() => setCountrySwitchOpen(false)}
      />

      {/* ─── Desktop: hero — title + 3-section search bar + active pills + chips (md+ only) ─── */}
      <section className="mx-auto hidden w-full max-w-[1600px] px-4 pt-9 pb-6 sm:px-6 md:block lg:px-8">
        {/* Random heading, blur-to-sharp entrance (`.puls-reveal`, CSS only —
            static under reduced motion). */}
        <h1 className="font-display text-foreground mb-4 text-3xl font-semibold tracking-[-0.025em]">
          <span className="puls-reveal">{heroHeading}</span>
        </h1>

        {/* Puls command bar — ONE bar wrapped in the homepage beam
             (`BeamFrame`, faster while the AI symptom router is thinking):
             live search on the left, then the guided wizard as a compact
             secondary button, then the primary Search (runs the bar's own
             Enter path). AI specialty suggestions surface as chips below.
             `max-w-[1600px] mx-auto` caps the row on ultra-wide monitors
             so the search bar doesn't feel like a runway — modern-looking
             content density, similar to Linear / Notion / Stripe heroes. */}
        <div className="mx-auto mb-4 max-w-[1600px]">
          <BeamFrame className="rounded-2xl" busy={aiBusy}>
            <div className="bg-card flex items-stretch gap-2 rounded-2xl p-2 shadow-md">
              {/* Section 1 — Specialty / Symptom (live combobox, autocomplete + AI
               symptom routing + recents from `UniversalSearch`). Inline so the
               user can type immediately; the UniversalSearch dropdown anchors
               below the bar via portaled backdrop. The `idleSuffix` shows the
               currently-selected specialties as a small right-aligned hint
               (mirrors the "≤ 5 km" pattern in the Location zone) and the
               `dropdownHeader` surfaces the same specialties as red removable
               pills at the top of the dropdown when it opens. */}
              <div className="focus-within:bg-muted/40 flex flex-[2] items-center rounded-xl px-2 transition-colors">
                <UniversalSearch
                  variant="compact"
                  initialQuery={search.q ?? ""}
                  country={country}
                  onSearch={executeSmartSearch}
                  noBackdrop
                  selectedSpecialtyCodes={filterModalValues.specialties}
                  idleSuffix={
                    specialtyCardPills.length > 0 ? (
                      <div className="text-muted-foreground ml-auto flex max-w-[180px] shrink-0 flex-col items-end text-right text-[10px] leading-tight">
                        {specialtyCardPills.slice(0, 3).map((p) => (
                          <span key={p.id} className="max-w-full truncate">
                            {p.label}
                          </span>
                        ))}
                        {specialtyCardPills.length > 3 && <span aria-hidden>…</span>}
                      </div>
                    ) : undefined
                  }
                  dropdownHeader={
                    specialtyCardPills.length > 0 ? (
                      <div className="border-border/40 flex flex-wrap items-center gap-1 border-b p-3">
                        {specialtyCardPills.map((p) => (
                          <SectionPill key={p.id} label={p.label} onRemove={p.onRemove} />
                        ))}
                        {specialtyCardPills.length >= 2 && (
                          <button
                            type="button"
                            onClick={() => updateParams({ specialties: null, specialty: null })}
                            className="text-muted-foreground hover:text-foreground ml-1 text-xs underline-offset-4 hover:underline"
                          >
                            <Trans>Clear all</Trans>
                          </button>
                        )}
                      </div>
                    ) : undefined
                  }
                  onSelectSpecialty={(code) => {
                    if (filterModalValues.specialties.includes(code)) return;
                    const next = [...filterModalValues.specialties, code];
                    updateParams({ specialties: next.join(","), specialty: null });
                  }}
                  onSelectAiSpecialties={(codes) => {
                    const merged = [...filterModalValues.specialties];
                    for (const code of codes) {
                      if (!merged.includes(code)) merged.push(code);
                    }
                    updateParams({
                      specialties: merged.length > 0 ? merged.join(",") : null,
                      specialty: null,
                    });
                  }}
                  onSelectQuery={(q) => updateParams({ q })}
                  submitRef={universalSearchRef}
                  // md+ only: on phones this bar is CSS-hidden but mounted —
                  // don't re-render the page for chips nobody sees.
                  onAiLoadingChange={isDesktop ? setAiBusy : undefined}
                  onAiSuggestions={isDesktop ? handleAiSuggestions : undefined}
                  onSelectQuickAction={(s) => {
                    // Quick-action chips like "Cardiologist" carry `specialty: "CAR"`.
                    // Append to the multi-select `specialties[]` instead of writing
                    // the legacy `specialty` single-value param — keeps the picker
                    // pills + sidebar pills in sync, and lets the user stack
                    // multiple quick actions without overwriting prior picks.
                    if (typeof s.specialty === "string" && s.specialty) {
                      const code = s.specialty;
                      const rest = { ...s };
                      delete rest.specialty;
                      const merged = filterModalValues.specialties.includes(code)
                        ? filterModalValues.specialties
                        : [...filterModalValues.specialties, code];
                      updateParams({
                        ...rest,
                        specialties: merged.join(","),
                        specialty: null,
                      });
                      return;
                    }
                    updateParams(s);
                  }}
                />
              </div>

              {/* Sections 2 + 3 (Consultation type + Where) moved to the sidebar
              cards. Hero stays compact: just specialty search + a Search
              button anchor. Refs/state for those zones are kept around for
              now in case the change is reverted; the JSX is gone. */}

              {/* Guided wizard — same entry (`?wizard=1&step=0`), same labels,
              now a compact secondary button inside the bar. */}
              <button
                type="button"
                onClick={() => updateParams({ wizard: "1", step: "0" })}
                className="border-accent/30 bg-accent-soft text-accent-strong hover:border-accent/60 inline-flex min-h-[44px] shrink-0 items-center gap-2 rounded-lg border px-3.5 text-sm font-semibold transition-colors"
                title={t`Search wizard`}
              >
                <Wand2 className="size-4 shrink-0" aria-hidden />
                <Trans>Guided search</Trans>
                <span className="hidden font-mono text-[11px] font-normal opacity-75 xl:inline">
                  <Trans>Step-by-step in 30s</Trans>
                </span>
              </button>

              {/* Primary CTA — submits what's typed through UniversalSearch's
              own Enter path (`submit()`); with an empty field it focuses
              the input instead. The keep-open marker stops the dropdown's
              outside-click handler from wiping the draft on mousedown. Not
              wired to the wizard — that restarts from step 0. */}
              <button
                type="button"
                {...{ [UNIVERSAL_SEARCH_KEEP_OPEN_ATTR]: true }}
                onClick={() => universalSearchRef.current?.submit()}
                className="bg-primary text-primary-foreground hover:bg-primary/90 inline-flex min-h-[44px] shrink-0 items-center gap-1.5 rounded-lg px-5 text-sm font-semibold transition-colors"
              >
                <SearchIcon className="size-4" />
                <Trans>Search</Trans>
              </button>
            </div>
          </BeamFrame>

          <AiRouteChips
            query={aiRoute?.query ?? ""}
            suggestions={aiRoute?.suggestions ?? []}
            selectedCodes={filterModalValues.specialties}
            onToggle={toggleAiRouteSpecialty}
          />
        </div>
      </section>

      {/* ─── Desktop: responsive grid — filters | results | (map) ───
           Tablet (md..1199): 2 cols. Desktop (≥1200): 3 cols with a single
           gap between list and map; the resize handle is rendered INSIDE the
           map column (left edge) so we don't need a dedicated gutter column. */}
      <div
        ref={gridContainerRef}
        className="mx-auto hidden w-full max-w-[1600px] px-4 sm:px-6 md:grid md:gap-6 md:pb-12 lg:px-8"
        style={{
          // `minmax(0, Xfr)` instead of plain `Xfr` so the columns actually
          // honor the fr ratio even when their content has a wider intrinsic
          // min-width (long doctor names, addresses, marker pills…). Without
          // it, CSS grid silently inflates the list column past its declared
          // fr — the resize handle's percent math then drifts and feels
          // "stuck" near SPLIT_MIN/SPLIT_MAX after a window resize.
          gridTemplateColumns: showDesktopMapColumn
            ? `${FILTER_COL_PX}px minmax(0, ${listSplitPct}fr) minmax(0, ${100 - listSplitPct}fr)`
            : `${FILTER_COL_PX}px minmax(0, 1fr)`,
        }}
      >
        {/* Left: filter sidebar (5 cards opening modal) */}
        <aside className="self-start md:sticky md:top-[calc(var(--chrome-h)+1.5rem)] md:h-[calc(100dvh-var(--chrome-h)-3rem)] md:overflow-y-auto md:overscroll-contain md:px-1.5 md:[scrollbar-gutter:stable]">
          <div className="flex flex-col gap-4">
            {/* Always-visible header row above the filter cards.
                Left: "Filter" label + count badge (when ≥1 filter is active).
                Right: "Clear all" — muted/disabled when no filter is active.
                Replaces the previous dual-mode rendering (pill strip /
                tiny "N filters active" line); active filters now live as
                red SectionPills inside their own cards. */}
            <div className="flex items-center justify-between px-1">
              <div className="flex items-center gap-2">
                <span className="text-foreground text-sm font-semibold">
                  <Trans>Filter</Trans>
                </span>
                {meaningfulFilterCount > 0 && (
                  <span className="bg-primary/10 text-primary inline-flex h-5 min-w-[1.25rem] items-center justify-center rounded-full px-1.5 text-xs font-semibold tabular-nums">
                    {meaningfulFilterCount}
                  </span>
                )}
              </div>
              {meaningfulFilterCount > 0 && (
                <button
                  type="button"
                  onClick={resetFilters}
                  className="text-muted-foreground hover:text-foreground cursor-pointer text-xs underline-offset-4 transition-colors hover:underline"
                >
                  <Trans>Clear all</Trans>
                </button>
              )}
            </div>
            {/* All sections live inline in the sidebar (desktop) so the
                user never has to open a modal for the common refinements.
                The mode switch above flips the first section between
                "Specialty" (Consult Medical = providers) and "Tip serviciu"
                (Servicii Medicale = facilities + businesses). */}

            {/* Mode switch — Consult Medical vs Servicii Medicale. Sliding
                segmented control via Framer Motion `layoutId` (see
                _find/mode-switch.tsx). Suffix=desktop so the mobile copy in
                MobileSearchHero doesn't share the same layout id. */}
            <ModeSwitch mode={sidebarMode} onChange={setSidebarMode} layoutIdSuffix="desktop" />

            {sidebarMode === "servicii" ? (
              /* Servicii Medicale: Specialty card transforms into Tip
                  serviciu — chips for the 11 facility/business verticals. */
              <SidebarFilterSection
                title={t`Pick a type`}
                pills={tipCardPills}
                summary={(() => {
                  if (!currentServiciiVertical) return t`Choose a service type`;
                  switch (currentServiciiVertical.key) {
                    case "hospital":
                      return t`Hospitals`;
                    case "clinic":
                      return t`Clinics`;
                    case "polyclinic":
                      return t`Polyclinics`;
                    case "dental_clinic":
                      return t`Dental offices`;
                    case "pharmacy":
                      return t`Pharmacy`;
                    case "lab":
                      return t`Lab`;
                    case "imaging":
                      return t`Imaging`;
                    case "optical":
                      return t`Optical`;
                    case "homecare":
                      return t`Home care`;
                    case "medical_transport":
                      return t`Medical transport`;
                    case "equipment":
                      return t`Medical equipment`;
                  }
                })()}
                open={openSidebarSection === "serviciiTip"}
                onToggle={() => toggleSidebarSection("serviciiTip")}
              >
                <div className="flex flex-wrap gap-1.5">
                  {/* "All" chip dropped — user must explicitly pick a vertical
                      in Servicii mode. The Servicii switch sets
                      kind=businesses as the implicit default until a Tip is
                      picked, but the chip row only shows the 11 specific
                      verticals. */}
                  {SERVICII_VERTICALS.map((v) => {
                    const label = (() => {
                      switch (v.key) {
                        case "hospital":
                          return t`Hospitals`;
                        case "clinic":
                          return t`Clinics`;
                        case "polyclinic":
                          return t`Polyclinics`;
                        case "dental_clinic":
                          return t`Dental offices`;
                        case "pharmacy":
                          return t`Pharmacy`;
                        case "lab":
                          return t`Lab`;
                        case "imaging":
                          return t`Imaging`;
                        case "optical":
                          return t`Optical`;
                        case "homecare":
                          return t`Home care`;
                        case "medical_transport":
                          return t`Medical transport`;
                        case "equipment":
                          return t`Medical equipment`;
                      }
                    })();
                    return (
                      <HeroChoiceChip
                        key={v.key}
                        active={search.tab === v.tab}
                        onClick={() => setServiciiVertical(v)}
                      >
                        {label}
                      </HeroChoiceChip>
                    );
                  })}
                </div>
              </SidebarFilterSection>
            ) : (
              /* Consult Medical: full Specialty section (existing behavior —
                  symptom mode toggle + specialty multi-select + AI router). */
              <SidebarFilterSection
                title={t`Specialty`}
                summary={t`Any specialty`}
                pills={specialtyCardPills}
                open={openSidebarSection === "specialty"}
                onToggle={() => toggleSidebarSection("specialty")}
              >
                <div>
                  {symptomMode ? (
                    <textarea
                      ref={sidebarSpecialtyTextareaRef}
                      value={sidebarSpecialtyQuery}
                      onChange={(e) => {
                        setSidebarSpecialtyQuery(e.target.value);
                        setSymptomError(null);
                      }}
                      onKeyDown={(e) => {
                        // Enter submits, Shift+Enter inserts newline (textarea
                        // default keeps multi-line entries possible).
                        if (e.key === "Enter" && !e.shiftKey) {
                          e.preventDefault();
                          void runSymptomSearch();
                        }
                      }}
                      rows={2}
                      placeholder={t`Describe your symptoms in your own words…`}
                      className="border-border bg-background focus:border-primary/40 focus:ring-primary/20 mb-2 w-full resize-none rounded-lg border p-2 text-sm outline-none focus:ring-2"
                    />
                  ) : (
                    <input
                      ref={sidebarSpecialtyInputRef}
                      type="search"
                      value={sidebarSpecialtyQuery}
                      onChange={(e) => setSidebarSpecialtyQuery(e.target.value)}
                      placeholder={t`Search a specialty…`}
                      className="border-border bg-background focus:border-primary/40 focus:ring-primary/20 mb-2 h-9 w-full rounded-lg border px-2 text-sm outline-none focus:ring-2"
                    />
                  )}
                  {symptomMode ? (
                    <>
                      <div className="mb-2 flex items-center justify-between gap-2">
                        <Button
                          size="sm"
                          onClick={() => void runSymptomSearch()}
                          disabled={symptomLoading || sidebarSpecialtyQuery.trim().length < 3}
                        >
                          {symptomLoading ? (
                            <Trans>Analysing…</Trans>
                          ) : (
                            <Trans>Search specialties</Trans>
                          )}
                        </Button>
                        <button
                          type="button"
                          onClick={() => {
                            setSymptomMode(false);
                            setSymptomSuggestions([]);
                            setSymptomError(null);
                            setSidebarSpecialtyQuery("");
                          }}
                          className="text-muted-foreground hover:text-foreground text-xs underline-offset-2 hover:underline"
                        >
                          <Trans>Cancel</Trans>
                        </button>
                      </div>
                      {symptomError && (
                        <p className="text-destructive mb-1 text-xs">{symptomError}</p>
                      )}
                      {symptomSuggestions.length > 0 && (
                        <div className="space-y-1.5">
                          {symptomSuggestions.map((s) => {
                            const selected = filterModalValues.specialties.includes(
                              s.specialtyCode,
                            );
                            return (
                              <button
                                key={s.specialtyCode}
                                type="button"
                                onClick={() => {
                                  const next = selected
                                    ? filterModalValues.specialties.filter(
                                        (c) => c !== s.specialtyCode,
                                      )
                                    : [...filterModalValues.specialties, s.specialtyCode];
                                  updateParams({
                                    specialties: next.length > 0 ? next.join(",") : null,
                                    specialty: null,
                                  });
                                  // Collapse the Specialty card and pop open
                                  // Consultation type after add. On remove,
                                  // keep the card open so the user can keep
                                  // curating the AI suggestions.
                                  if (!selected) setOpenSidebarSection("consultation");
                                }}
                                className={cn(
                                  "flex w-full items-center gap-2 rounded-md border px-2.5 py-2 text-left text-xs transition-colors",
                                  selected
                                    ? "border-primary bg-primary/10"
                                    : "border-border bg-card hover:bg-muted/60",
                                )}
                              >
                                <div className="min-w-0 flex-1">
                                  <div className="truncate font-medium">{s.specialtyName}</div>
                                  <div className="text-muted-foreground truncate text-[11px]">
                                    {s.reason}
                                  </div>
                                </div>
                                <span className="text-muted-foreground shrink-0 text-[11px] tabular-nums">
                                  {s.confidence}%
                                </span>
                                {selected && <Check className="text-primary size-3.5" />}
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </>
                  ) : (
                    <>
                      <div className="flex flex-wrap gap-1.5">
                        {(() => {
                          const list =
                            sidebarSpecialtyQuery.trim().length > 0
                              ? specSearchOptions
                              : specOptions;
                          // Hide already-selected specialties — they live as red
                          // pills in the section header, no need to render twice.
                          const filtered = list.filter(
                            (s) => !filterModalValues.specialties.includes(s.value),
                          );
                          const visible =
                            sidebarSpecialtyQuery.trim().length > 0
                              ? filtered
                              : filtered.slice(0, 12);
                          if (visible.length === 0) {
                            return (
                              <div className="text-muted-foreground px-1 py-1 text-xs">
                                {isSpecSearching ? (
                                  <Trans>Searching…</Trans>
                                ) : (
                                  <Trans>No results</Trans>
                                )}
                              </div>
                            );
                          }
                          return visible.map((s) => {
                            return (
                              <HeroChoiceChip
                                key={s.value}
                                active={false}
                                onClick={() => {
                                  const next = [...filterModalValues.specialties, s.value];
                                  updateParams({
                                    specialties: next.length > 0 ? next.join(",") : null,
                                    specialty: null,
                                  });
                                  setOpenSidebarSection("consultation");
                                }}
                              >
                                {s.label}
                              </HeroChoiceChip>
                            );
                          });
                        })()}
                      </div>
                      <button
                        type="button"
                        onClick={enterSymptomMode}
                        className="text-primary hover:text-primary/80 mt-2 text-xs font-medium underline-offset-2 transition-colors hover:underline"
                      >
                        <Trans>Help me pick (symptoms)</Trans>
                      </button>
                    </>
                  )}
                </div>
              </SidebarFilterSection>
            )}

            {/* Consultation type — collapsible to match the rest of the
                sidebar cards. When collapsed: red pill with the active type
                (if any), no pill summary fallback. Drives the Where card's
                content (city/near-me · country · address). HIDDEN in
                Servicii mode — facility/business search doesn't have a
                video / home-visit / in-person dimension. */}
            {sidebarMode === "consult" && (
              <SidebarFilterSection
                title={t`Consultation type`}
                summary={t`Any`}
                pills={consultationCardPills}
                open={openSidebarSection === "consultation"}
                onToggle={() => toggleSidebarSection("consultation")}
              >
                {/* "Any" was dropped — clearing the filter happens via the
                  red pill X above (the section pill that shows the active
                  consultation type). Listing it as a chip duplicated the
                  affordance and made the row noisier than the rest. */}
                <div className="flex flex-wrap gap-1.5">
                  <HeroChoiceChip
                    active={
                      search.consultationType === "in_person" ||
                      search.consultationType === "in_office"
                    }
                    onClick={() => pickConsultationType("in_person")}
                  >
                    <Trans>In person</Trans>
                  </HeroChoiceChip>
                  <HeroChoiceChip
                    active={search.consultationType === "video"}
                    onClick={() => pickConsultationType("video")}
                  >
                    <Trans>Video</Trans>
                  </HeroChoiceChip>
                  <HeroChoiceChip
                    active={search.consultationType === "home_visit"}
                    onClick={() => pickConsultationType("home_visit")}
                  >
                    <Trans>Home visit</Trans>
                  </HeroChoiceChip>
                </div>
              </SidebarFilterSection>
            )}

            {/* Where — content adapts to the consultation type set above:
                  video      → country selector only (licensing context)
                  home_visit → address picker (geocoded /api/geocode)
                  else       → country (+ cross-border / global) + city /
                               near-me + radius (legacy in-person flow)
                The summary line reuses `heroLocationLabel` so the collapsed
                card mirrors what we used to show in the hero Zone 3. */}
            <SidebarFilterSection
              title={t`Where`}
              summary={
                heroLocationLabel ||
                (search.consultationType === "home_visit" ? t`Add address` : t`Anywhere`)
              }
              pills={whereCardPills}
              open={openSidebarSection === "where"}
              onToggle={() => toggleSidebarSection("where")}
            >
              {search.consultationType === "home_visit" ? (
                <div className="space-y-1.5">
                  <div className="text-muted-foreground flex items-center gap-1.5 text-[11px] font-medium">
                    <HomeIcon className="size-3.5" aria-hidden />
                    <Trans>Your home address</Trans>
                  </div>
                  <div className="relative">
                    <SearchIcon
                      className="text-muted-foreground pointer-events-none absolute top-1/2 left-2.5 size-3.5 -translate-y-1/2"
                      aria-hidden
                    />
                    <input
                      type="text"
                      value={homeAddrQuery}
                      onChange={(e) => {
                        const v = e.target.value;
                        setHomeAddrQuery(v);
                        if (homeAddress) clearHomeAddress();
                        searchHomeAddress(v);
                      }}
                      placeholder={t`Type an address…`}
                      className="border-border bg-background focus:border-primary/40 focus:ring-primary/20 h-9 w-full rounded-lg border py-1 pr-8 pl-8 text-sm outline-none focus:ring-2"
                    />
                    {homeAddrSearching && (
                      <Loader2
                        className="text-muted-foreground absolute top-1/2 right-2.5 size-3.5 -translate-y-1/2 animate-spin"
                        aria-hidden
                      />
                    )}
                    {!homeAddrSearching && homeAddrQuery && (
                      <button
                        type="button"
                        onClick={clearHomeAddress}
                        aria-label={t`Clear`}
                        className="text-muted-foreground hover:text-foreground absolute top-1/2 right-2.5 -translate-y-1/2"
                      >
                        <XIcon className="size-3.5" />
                      </button>
                    )}
                  </div>
                  {homeAddrSuggestions.length > 0 && (
                    <div className="border-border bg-background mt-1.5 max-h-56 overflow-y-auto rounded-lg border shadow-sm">
                      {homeAddrSuggestions.map((s, i) => (
                        <button
                          key={`${s.lat}-${s.lng}-${i}`}
                          type="button"
                          onClick={() => pickHomeAddress(s)}
                          className="hover:bg-muted/50 flex w-full items-start gap-2 px-2.5 py-2 text-left text-xs transition-colors"
                        >
                          <MapPinIcon
                            className="text-muted-foreground mt-0.5 size-3.5 shrink-0"
                            aria-hidden
                          />
                          <div className="min-w-0">
                            <p className="text-foreground truncate font-medium">
                              {s.formattedAddress}
                            </p>
                            {s.city && (
                              <p className="text-muted-foreground text-[11px]">
                                {s.city}
                                {s.county ? `, ${s.county}` : ""}
                              </p>
                            )}
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ) : search.consultationType === "video" ? (
                /* Video: country picker. Multi-select. Current country
                   chip is implicit (always part of the search; can't be
                   toggled off). "All countries" flips scope=global.
                   Search input filters the open-country list and toggling
                   a chip writes to `countries` + `scope=cross`. */
                <div className="space-y-2">
                  <div className="text-muted-foreground text-[11px] font-semibold tracking-[0.06em] uppercase">
                    <Trans>Choose country</Trans>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    <HeroChoiceChip active={scope !== "global"} onClick={() => {}}>
                      {modalCurrentCountryFlag && (
                        <span aria-hidden className="mr-1 text-base leading-none">
                          {modalCurrentCountryFlag}
                        </span>
                      )}
                      {modalCurrentCountryName}
                    </HeroChoiceChip>
                    <HeroChoiceChip
                      active={scope === "global"}
                      onClick={() => {
                        if (scope !== "global") {
                          updateParams({ scope: "global", countries: null });
                        } else {
                          updateParams({ scope: null, countries: null });
                        }
                      }}
                    >
                      <Trans>All countries</Trans>
                    </HeroChoiceChip>
                    {/* User-picked extras — render before the search so the
                        active selections stay visible even when the search
                        filter would hide them. */}
                    {selectedNeighborCodes.map((upper) => {
                      const meta = videoCountryOptions.find((o) => o.code === upper);
                      return (
                        <HeroChoiceChip
                          key={upper}
                          active
                          onClick={() => toggleVideoCountry(upper)}
                        >
                          {meta?.flag && (
                            <span aria-hidden className="mr-1 text-base leading-none">
                              {meta.flag}
                            </span>
                          )}
                          {meta?.name ?? upper}
                        </HeroChoiceChip>
                      );
                    })}
                  </div>
                  <input
                    type="search"
                    value={videoCountryQuery}
                    onChange={(e) => setVideoCountryQuery(e.target.value)}
                    placeholder={t`Search country…`}
                    className="border-border/60 bg-background placeholder:text-muted-foreground/60 focus:border-primary/40 focus:ring-primary/20 h-9 w-full rounded-lg border px-3 text-sm outline-none focus:ring-2"
                  />
                  {/* Default visible list of open countries (excluding current
                      + already-selected). Without typing the user sees the
                      neighbouring open markets; typing narrows the same
                      pool. Cap at 8 by default, 12 while searching so the
                      panel doesn't blow out vertically. */}
                  {(() => {
                    const searching = videoCountryQuery.trim().length > 0;
                    const list = videoCountryOptions
                      .filter((c) => !selectedNeighborCodes.includes(c.code))
                      .slice(0, searching ? 12 : 8);
                    if (list.length === 0) {
                      return searching ? (
                        <p className="text-muted-foreground px-1 text-xs">
                          <Trans>No matching country</Trans>
                        </p>
                      ) : null;
                    }
                    return (
                      <div className="flex flex-wrap gap-1.5">
                        {list.map((c) => (
                          <HeroChoiceChip
                            key={c.code}
                            active={false}
                            onClick={() => {
                              toggleVideoCountry(c.code);
                              setVideoCountryQuery("");
                            }}
                          >
                            {c.flag && (
                              <span aria-hidden className="mr-1 text-base leading-none">
                                {c.flag}
                              </span>
                            )}
                            {c.name}
                          </HeroChoiceChip>
                        ))}
                      </div>
                    );
                  })()}
                </div>
              ) : (
                /* Cabinet (in_person) and the no-selection default. The
                   country is locked to `params.country` (in-person care is
                   geographically constrained), and `cityScopeCountries`
                   only returns the current country — so the city list never
                   mixes RO + BG + … cities. The country chip itself opens
                   the CountryPickerSheet (same flow as mobile); selecting
                   a new country resets the stale city + map-bounds. */
                <div className="space-y-4">
                  <div className="space-y-1.5">
                    <div className="text-muted-foreground text-[11px] font-semibold tracking-[0.06em] uppercase">
                      <Trans>Country</Trans>
                    </div>
                    <button
                      type="button"
                      onClick={() => setCountrySwitchOpen(true)}
                      aria-label={t`Change country`}
                      className="bg-primary/10 text-primary hover:bg-primary/15 focus-visible:ring-primary/40 inline-flex cursor-pointer items-center gap-1.5 rounded-full px-2.5 py-1 text-sm transition-colors focus:outline-none focus-visible:ring-2"
                    >
                      {modalCurrentCountryFlag && (
                        <span aria-hidden className="text-base leading-none">
                          {modalCurrentCountryFlag}
                        </span>
                      )}
                      <span>{modalCurrentCountryName}</span>
                      <ChevronDown className="size-3.5 opacity-70" aria-hidden />
                    </button>
                  </div>
                  <div className="space-y-1.5">
                    <div className="text-muted-foreground text-[11px] font-semibold tracking-[0.06em] uppercase">
                      <Trans>Choose location</Trans>
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      <HeroChoiceChip
                        active={search.nearMe !== "true"}
                        onClick={() =>
                          updateParams({
                            nearMe: null,
                            lat: null,
                            lng: null,
                            distance: null,
                          })
                        }
                      >
                        <Trans>Choose city</Trans>
                      </HeroChoiceChip>
                      <HeroChoiceChip
                        active={search.nearMe === "true"}
                        loading={isLocating}
                        onClick={() => applyNearMe(Number(search.distance) || 5)}
                      >
                        {isLocating ? <Trans>Detecting...</Trans> : <Trans>Near me</Trans>}
                      </HeroChoiceChip>
                    </div>
                  </div>

                  {search.nearMe === "true" && (
                    <div className="space-y-1.5">
                      <div className="text-muted-foreground text-[11px] font-semibold tracking-[0.06em] uppercase">
                        <Trans>Radius</Trans>
                      </div>
                      <div className="flex flex-wrap gap-1.5">
                        {[1, 3, 5, 10, 25].map((km) => (
                          <HeroChoiceChip
                            key={km}
                            active={Number(search.distance) === km}
                            onClick={() => applyNearMe(km)}
                          >
                            {km} km
                          </HeroChoiceChip>
                        ))}
                      </div>
                    </div>
                  )}

                  {search.nearMe !== "true" && (
                    <div className="space-y-1.5">
                      <div className="text-muted-foreground text-[11px] font-semibold tracking-[0.06em] uppercase">
                        <Trans>City</Trans>
                      </div>
                      <input
                        type="text"
                        value={heroCityQuery}
                        onChange={(e) => setHeroCityQuery(e.target.value)}
                        placeholder={t`Search city…`}
                        className="border-border/60 bg-background placeholder:text-muted-foreground/60 focus:border-primary/40 focus:ring-primary/20 h-9 w-full rounded-lg border px-3 text-sm outline-none focus:ring-2"
                      />
                      {heroCityList.length > 0 && (
                        <div className="flex max-h-64 flex-wrap gap-1.5 overflow-y-auto py-1">
                          {heroCityList.map((c) => {
                            const slug = toCitySlug(c);
                            const active = search.city === slug;
                            return (
                              <HeroChoiceChip
                                key={c.id}
                                active={active}
                                onClick={() => {
                                  applyCity(slug, c.countryCode);
                                  setHeroCityQuery("");
                                  setOpenSidebarSection("when");
                                }}
                              >
                                {getCityDisplayName(c, i18n.locale)}
                              </HeroChoiceChip>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </SidebarFilterSection>

            {/* When — quick-pick time slots. Consult-only: facility/business
                search has no appointment-slot dimension (you don't book a
                pharmacy for 3pm). Mirrors the wizard step 4 contract. */}
            {sidebarMode === "consult" && (
              <SidebarFilterSection
                title={t`When`}
                summary={t`Anytime`}
                pills={whenCardPills}
                open={openSidebarSection === "when"}
                onToggle={() => toggleSidebarSection("when")}
              >
                <div className="flex flex-col gap-2">
                  {/* Anytime — default no-filter state. Active when no choice
                    is set; clicking explicitly clears any prior pick. */}
                  <button
                    type="button"
                    onClick={() => {
                      updateParams({ when: null, dateFrom: null, dateTo: null });
                      setOpenSidebarSection((cur) => (cur === "when" ? null : cur));
                    }}
                    className={cn(
                      "border-border bg-card hover:border-primary/50 focus-visible:ring-primary/50 flex min-h-[52px] items-center gap-3 rounded-xl border px-3 py-2 text-left transition-colors focus-visible:ring-2 focus-visible:outline-none",
                      !whenChoice && "border-primary ring-primary/20 bg-primary/5 ring-2",
                    )}
                  >
                    <div
                      className={cn(
                        "flex size-9 shrink-0 items-center justify-center rounded-lg",
                        !whenChoice
                          ? "bg-primary text-primary-foreground"
                          : "bg-muted text-foreground",
                      )}
                    >
                      <InfinityIcon className="size-4" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-foreground text-sm font-semibold">
                        <Trans>Anytime</Trans>
                      </div>
                      <div className="text-muted-foreground mt-0.5 text-[11px]">
                        <Trans>No time preference</Trans>
                      </div>
                    </div>
                  </button>

                  {[
                    { key: "now" as const, icon: Zap },
                    { key: "today" as const, icon: CalendarIcon },
                    { key: "tomorrow" as const, icon: CalendarIcon },
                    { key: "this_week" as const, icon: CalendarDays },
                  ].map(({ key, icon: Icon }) => {
                    const isActive = whenChoice === key;
                    return (
                      <button
                        key={key}
                        type="button"
                        onClick={() => setWhen(key)}
                        className={cn(
                          "border-border bg-card hover:border-primary/50 focus-visible:ring-primary/50 flex min-h-[52px] items-center gap-3 rounded-xl border px-3 py-2 text-left transition-colors focus-visible:ring-2 focus-visible:outline-none",
                          isActive && "border-primary ring-primary/20 bg-primary/5 ring-2",
                        )}
                      >
                        <div
                          className={cn(
                            "flex size-9 shrink-0 items-center justify-center rounded-lg",
                            isActive
                              ? "bg-primary text-primary-foreground"
                              : "bg-muted text-foreground",
                          )}
                        >
                          <Icon className="size-4" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="text-foreground text-sm font-semibold">
                            {whenCopy.label(key)}
                          </div>
                          <div className="text-muted-foreground mt-0.5 text-[11px]">
                            {whenCopy.description(key)}
                          </div>
                        </div>
                      </button>
                    );
                  })}

                  {/* Custom range — single card; expands two date inputs when
                    active so we don't waste vertical space when unused. */}
                  <div
                    className={cn(
                      "border-border bg-card flex flex-col gap-2 rounded-xl border px-3 py-2 transition-colors",
                      whenChoice === "custom" &&
                        "border-primary ring-primary/20 bg-primary/5 ring-2",
                    )}
                  >
                    <button
                      type="button"
                      onClick={() => setWhen("custom")}
                      className="focus-visible:ring-primary/50 flex items-center gap-3 rounded-lg text-left focus-visible:ring-2 focus-visible:outline-none"
                    >
                      <div
                        className={cn(
                          "flex size-9 shrink-0 items-center justify-center rounded-lg",
                          whenChoice === "custom"
                            ? "bg-primary text-primary-foreground"
                            : "bg-muted text-foreground",
                        )}
                      >
                        <CalendarRange className="size-4" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="text-foreground text-sm font-semibold">
                          {whenCopy.label("custom")}
                        </div>
                        <div className="text-muted-foreground mt-0.5 text-[11px]">
                          {whenCopy.description("custom")}
                        </div>
                      </div>
                    </button>
                    {whenChoice === "custom" && (
                      <div className="border-border/60 flex flex-col gap-2 border-t pt-2">
                        <label className="flex flex-col gap-1">
                          <span className="text-muted-foreground text-[11px] font-medium tracking-wide uppercase">
                            <Trans>From</Trans>
                          </span>
                          <input
                            type="date"
                            value={customWhenFrom}
                            onChange={(e) => setCustomWhenFrom(e.target.value)}
                            onBlur={commitCustomWhen}
                            className="border-border bg-background focus:border-primary/40 focus:ring-primary/20 h-9 rounded-lg border px-2.5 text-sm focus:ring-2 focus:outline-none"
                          />
                        </label>
                        <label className="flex flex-col gap-1">
                          <span className="text-muted-foreground text-[11px] font-medium tracking-wide uppercase">
                            <Trans>To</Trans>
                          </span>
                          <input
                            type="date"
                            value={customWhenTo}
                            onChange={(e) => setCustomWhenTo(e.target.value)}
                            onBlur={commitCustomWhen}
                            className="border-border bg-background focus:border-primary/40 focus:ring-primary/20 h-9 rounded-lg border px-2.5 text-sm focus:ring-2 focus:outline-none"
                          />
                        </label>
                      </div>
                    )}
                  </div>
                </div>
              </SidebarFilterSection>
            )}

            {/* Languages + Insurance — Consult-only. Facility/business search
                doesn't have a "spoken language" or "accepted CAS code"
                dimension that maps cleanly. Hidden in Servicii mode. */}
            {sidebarMode === "consult" && (
              <SidebarFilterSection
                title={t`Languages`}
                summary={t`Any language`}
                pills={languageCardPills}
                open={openSidebarSection === "languages"}
                onToggle={() => toggleSidebarSection("languages")}
              >
                <div className="flex flex-wrap gap-1.5">
                  {PRIMARY_LOCALES.filter(
                    (lang) => !filterModalValues.languages.includes(lang),
                  ).map((lang) => {
                    return (
                      <HeroChoiceChip
                        key={lang}
                        active={false}
                        onClick={() => {
                          const next = [...filterModalValues.languages, lang];
                          updateParams({ languages: next.length > 0 ? next.join(",") : null });
                        }}
                      >
                        {LOCALE_FLAG[lang] && (
                          <span aria-hidden className="text-base leading-none">
                            {LOCALE_FLAG[lang]}
                          </span>
                        )}
                        <span className="capitalize">{languageName(lang)}</span>
                      </HeroChoiceChip>
                    );
                  })}
                </div>
              </SidebarFilterSection>
            )}

            {/* Insurance — applies to both modes (CAS / private / direct
                also relevant for facilities + businesses, not just providers). */}
            <SidebarFilterSection
              title={t`Insurance`}
              summary={t`Any`}
              pills={insuranceCardPills}
              open={openSidebarSection === "insurance"}
              onToggle={() => toggleSidebarSection("insurance")}
            >
              <div className="flex flex-wrap gap-1.5">
                {(
                  [
                    { value: "all", label: t`Any` },
                    { value: "cas", label: t`Public (CAS)` },
                    { value: "private", label: t`Private insurance` },
                    { value: "direct", label: t`Direct payment` },
                  ] as const
                ).map((opt) => {
                  const active = (filterModalValues.insurance ?? "all") === opt.value;
                  return (
                    <HeroChoiceChip
                      key={opt.value}
                      active={active}
                      onClick={() =>
                        updateParams({
                          insurance: opt.value === "all" ? null : opt.value,
                        })
                      }
                    >
                      {opt.label}
                    </HeroChoiceChip>
                  );
                })}
              </div>
            </SidebarFilterSection>

            {/* Price range — applies to both modes. */}
            <SidebarFilterSection
              title={t`Price range`}
              summary={t`Any price`}
              pills={priceCardPills}
              open={openSidebarSection === "price"}
              onToggle={() => toggleSidebarSection("price")}
            >
              <div className="space-y-2">
                <div className="text-muted-foreground flex items-center justify-end text-xs tabular-nums">
                  <span>
                    {filterModalValues.minPrice ?? 0} –{" "}
                    {(filterModalValues.maxPrice ?? PRICE_CEILING) >= PRICE_CEILING
                      ? `${PRICE_CEILING}+`
                      : (filterModalValues.maxPrice ?? PRICE_CEILING)}{" "}
                    RON
                  </span>
                </div>
                <Slider
                  value={[
                    filterModalValues.minPrice ?? 0,
                    filterModalValues.maxPrice ?? PRICE_CEILING,
                  ]}
                  min={0}
                  max={PRICE_CEILING}
                  step={10}
                  onValueChange={(v) => {
                    const arr = v as number[];
                    updateParams({
                      minPrice: arr[0] > 0 ? String(arr[0]) : null,
                      maxPrice: arr[1] < PRICE_CEILING ? String(arr[1]) : null,
                    });
                  }}
                >
                  <SliderControl>
                    <SliderTrack>
                      <SliderIndicator />
                    </SliderTrack>
                    <SliderThumb />
                    <SliderThumb />
                  </SliderControl>
                </Slider>
              </div>
            </SidebarFilterSection>

            {/* Servicii-only quick toggles (Open now / 24/7 / Verified).
                Hidden in Consult — provider verified is implicit (admin-
                approved) and "open now" maps to consultation slot lookup
                handled separately in the When card. Stays outside the
                "More filters" disclosure: it IS the quick-access surface. */}
            {sidebarMode === "servicii" && (
              <SidebarFilterSection
                title={t`Quick filters`}
                summary={(() => {
                  const flags: string[] = [];
                  if (search.isOpenNow === "true") flags.push(t`Open now`);
                  if (search.is24h === "true") flags.push(t`24/7`);
                  if (search.isVerified === "true") flags.push(t`Verified`);
                  return flags.length > 0 ? flags.join(" · ") : t`None`;
                })()}
                open={openSidebarSection === "quickFilters"}
                onToggle={() => toggleSidebarSection("quickFilters")}
              >
                <div className="flex flex-wrap gap-1.5">
                  {[
                    {
                      key: "isOpenNow" as const,
                      label: t`Open now`,
                      icon: <Zap className="size-3" aria-hidden />,
                    },
                    {
                      key: "is24h" as const,
                      label: t`24/7`,
                      icon: <CalendarDays className="size-3" aria-hidden />,
                    },
                    {
                      key: "isVerified" as const,
                      label: t`Verified`,
                      icon: <Check className="size-3" aria-hidden />,
                    },
                  ].map((opt) => {
                    const isActive = search[opt.key] === "true";
                    return (
                      <HeroChoiceChip
                        key={opt.key}
                        active={isActive}
                        onClick={() => updateParams({ [opt.key]: isActive ? null : "true" })}
                      >
                        {opt.icon}
                        {opt.label}
                      </HeroChoiceChip>
                    );
                  })}
                </div>
              </SidebarFilterSection>
            )}

            {/* "More filters" disclosure — gates the secondary cards:
                Rating, Accessibility, Amenities (both modes) + Audience,
                Provider gender (consult only). Hidden by default so the
                sidebar stays short above the fold; expanding reveals the
                whole group at once. */}
            <button
              type="button"
              onClick={() => setShowMoreFilters((v) => !v)}
              aria-expanded={showMoreFilters}
              className="text-primary hover:text-primary/80 inline-flex items-center justify-center gap-1.5 self-stretch px-1 py-1 text-xs font-medium underline-offset-4 transition-colors hover:underline"
            >
              <SlidersHorizontal className="size-3.5" aria-hidden />
              {showMoreFilters ? <Trans>Less filters</Trans> : <Trans>More filters</Trans>}
              <ChevronDown
                className={cn("size-3.5 transition-transform", showMoreFilters && "rotate-180")}
                aria-hidden
              />
            </button>

            <AnimatePresence initial={false}>
              {showMoreFilters && (
                <m.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  className="flex flex-col gap-3 overflow-hidden"
                >
                  {/* Rating — applies to both modes. */}
                  <SidebarFilterSection
                    title={t`Rating`}
                    summary={search.ratingMin ? t`${search.ratingMin}+ stars` : t`Any rating`}
                    open={openSidebarSection === "ratingMin"}
                    onToggle={() => toggleSidebarSection("ratingMin")}
                  >
                    <div className="flex flex-wrap gap-1.5">
                      {(
                        [
                          { value: null, label: t`Any` },
                          { value: "3.5", label: "3.5+" },
                          { value: "4.0", label: "4.0+" },
                          { value: "4.5", label: "4.5+" },
                        ] as const
                      ).map((opt) => (
                        <HeroChoiceChip
                          key={opt.label}
                          active={
                            (search.ratingMin ?? null) ===
                            (opt.value as "3.5" | "4.0" | "4.5" | null)
                          }
                          onClick={() => updateParams({ ratingMin: opt.value })}
                        >
                          {opt.value && <StarIcon className="size-3 fill-current" aria-hidden />}
                          {opt.label}
                        </HeroChoiceChip>
                      ))}
                    </div>
                  </SidebarFilterSection>

                  {/* Accessibility — applies to both modes. */}
                  <SidebarFilterSection
                    title={t`Accessibility`}
                    summary={(() => {
                      const active: string[] = [];
                      if (search.hasWheelchairAccess === "true") active.push(t`Wheelchair`);
                      if (search.hasElevator === "true") active.push(t`Elevator`);
                      if (search.hasAccessibleRestroom === "true") active.push(t`Restroom`);
                      if (search.hasParking === "true") active.push(t`Parking`);
                      return active.length > 0 ? active.join(" · ") : t`Any`;
                    })()}
                    open={openSidebarSection === "accessibility"}
                    onToggle={() => toggleSidebarSection("accessibility")}
                  >
                    <div className="flex flex-wrap gap-1.5">
                      {(
                        [
                          { key: "hasWheelchairAccess", label: t`Wheelchair access` },
                          { key: "hasElevator", label: t`Elevator` },
                          { key: "hasAccessibleRestroom", label: t`Accessible restroom` },
                          { key: "hasParking", label: t`Parking` },
                        ] as const
                      ).map((opt) => {
                        const isActive = search[opt.key] === "true";
                        return (
                          <HeroChoiceChip
                            key={opt.key}
                            active={isActive}
                            onClick={() => updateParams({ [opt.key]: isActive ? null : "true" })}
                          >
                            {opt.label}
                          </HeroChoiceChip>
                        );
                      })}
                    </div>
                  </SidebarFilterSection>

                  {/* Amenities — applies to both modes. */}
                  <SidebarFilterSection
                    title={t`Amenities`}
                    summary={(() => {
                      const active: string[] = [];
                      if (search.hasWifi === "true") active.push(t`Wi-Fi`);
                      if (search.hasAirConditioning === "true") active.push(t`AC`);
                      if (search.hasChildrenArea === "true") active.push(t`Children area`);
                      if (search.hasBreastfeedingArea === "true") active.push(t`Breastfeeding`);
                      if (search.hasPublicTransport === "true") active.push(t`Transit`);
                      if (search.hasHomeCollection === "true") active.push(t`Home collection`);
                      if (search.hasDelivery === "true") active.push(t`Delivery`);
                      return active.length > 0 ? active.join(" · ") : t`Any`;
                    })()}
                    open={openSidebarSection === "amenities"}
                    onToggle={() => toggleSidebarSection("amenities")}
                  >
                    <div className="flex flex-wrap gap-1.5">
                      {(
                        [
                          { key: "hasWifi", label: t`Wi-Fi` },
                          { key: "hasAirConditioning", label: t`Air conditioning` },
                          { key: "hasChildrenArea", label: t`Children area` },
                          { key: "hasBreastfeedingArea", label: t`Breastfeeding area` },
                          { key: "hasPublicTransport", label: t`Public transport` },
                          { key: "hasHomeCollection", label: t`Home sample collection` },
                          { key: "hasDelivery", label: t`Delivery` },
                        ] as const
                      ).map((opt) => {
                        const isActive = search[opt.key] === "true";
                        return (
                          <HeroChoiceChip
                            key={opt.key}
                            active={isActive}
                            onClick={() => updateParams({ [opt.key]: isActive ? null : "true" })}
                          >
                            {opt.label}
                          </HeroChoiceChip>
                        );
                      })}
                    </div>
                  </SidebarFilterSection>

                  {/* Services — scope-aware picker with a debounced search
                      input (mirrors the Specialty section above). Browse
                      mode shows services whose category matches the current
                      entity type (provider in Consult, hospital/clinic/lab/…
                      vertical in Servicii); typing ≥2 chars hits the API for
                      a broader match across all categories. Section is
                      hidden when no taxonomy applies (pharmacy / optical /
                      homecare / medical_transport / equipment). */}
                  {relevantServiceCategories !== null && (
                    <SidebarFilterSection
                      title={t`Services`}
                      summary={t`Any service`}
                      pills={serviceCardPills}
                      open={openSidebarSection === "services"}
                      onToggle={() => toggleSidebarSection("services")}
                    >
                      <div>
                        <input
                          type="search"
                          value={sidebarServicesQuery}
                          onChange={(e) => setSidebarServicesQuery(e.target.value)}
                          placeholder={t`Search a service…`}
                          className="border-border bg-background focus:border-primary/40 focus:ring-primary/20 mb-2 h-9 w-full rounded-lg border px-2 text-sm outline-none focus:ring-2"
                        />
                        <div className="flex flex-wrap gap-1.5">
                          {(() => {
                            const isSearching = sidebarServicesQuery.trim().length > 0;
                            const source = isSearching ? svcSearchOptions : visibleServiceOptions;
                            const filtered = source.filter(
                              (s) => !filterModalValues.services.includes(s.value),
                            );
                            if (filtered.length === 0) {
                              return (
                                <div className="text-muted-foreground px-1 py-1 text-xs">
                                  {isSvcSearching ? (
                                    <Trans>Searching…</Trans>
                                  ) : (
                                    <Trans>No results</Trans>
                                  )}
                                </div>
                              );
                            }
                            return filtered.map((s) => (
                              <HeroChoiceChip
                                key={s.value}
                                active={false}
                                onClick={() => {
                                  const next = [...filterModalValues.services, s.value];
                                  updateParams({
                                    services: next.length > 0 ? next.join(",") : null,
                                    service: null,
                                  });
                                }}
                              >
                                {s.label}
                              </HeroChoiceChip>
                            ));
                          })()}
                        </div>
                      </div>
                    </SidebarFilterSection>
                  )}

                  {/* Audience + Provider gender — consult-only. Returns
                      null in Servicii mode, no extra cards rendered. */}
                  <SidebarMoreFilters
                    mode={sidebarMode}
                    search={search}
                    updateParams={updateParams}
                    openSidebarSection={openSidebarSection}
                    toggleSidebarSection={toggleSidebarSection}
                  />
                </m.div>
              )}
            </AnimatePresence>

            {/* Search wizard escape hatch — guided step-by-step flow for users
                who don't want to refine via filters directly. */}
            <button
              type="button"
              onClick={() => updateParams({ wizard: "1", step: "0" })}
              className="text-muted-foreground hover:text-foreground inline-flex items-center justify-center gap-1.5 self-stretch px-1 py-1 text-xs font-medium underline-offset-4 transition-colors hover:underline"
            >
              <Wand2 className="size-3.5" aria-hidden />
              <Trans>Search wizard</Trans>
            </button>
          </div>
        </aside>

        {/* Middle: results. No local loading bar / wash / skeleton — the
             global <NavigationProgress> in the header handles the loading
             affordance for the whole route. */}
        <main className="relative">
          {/* Controls bar: count + favorites + saved + group + sort + (tablet)
              map/list toggle. The toggle only renders on the tablet tier
              (768 ≤ w < 1200) where the 3rd column is hidden — desktop has
              the always-visible map column, mobile has the swipeable view. */}
          <div className="border-border/50 relative mb-3 flex items-center justify-between gap-2 border-b pb-2.5">
            <span className="text-muted-foreground inline-flex items-center gap-1 text-sm font-medium">
              {showLoadingOverlay && results.length === 0 ? (
                <Trans>Searching…</Trans>
              ) : (
                <>
                  <Odometer value={total} locale={i18n.locale} className="text-foreground" />{" "}
                  <Plural value={total} one="result" other="results" />
                </>
              )}
            </span>
            {/* Scan line along the bar's bottom border while a search is in
                flight (Puls). Decorative, never intercepts input. */}
            {showLoadingOverlay && (
              <span aria-hidden className="pointer-events-none absolute inset-x-0 -bottom-px h-0.5">
                <span className="puls-scan block h-full w-full" />
              </span>
            )}
            <div className="flex flex-wrap items-center gap-1.5">
              <FavoritesOnlyToggle
                value={favoritesOnly}
                onChange={setFavoritesOnly}
                count={favoritesInResults}
              />
              <SavedSearchesDropdown
                onApply={updateParams}
                onSaveCurrent={() => setSaveSearchOpen(true)}
              />
              <GroupToggle value={groupMode} onChange={setGroup} />
              <SortDropdown
                value={search.sort ?? "recommended"}
                onChange={setSort}
                hasFacility={Boolean(search.city)}
              />
              {tier === "tablet" && !hideMapToggle && (
                <button
                  type="button"
                  onClick={() => {
                    if (!viewIsMap && !mapReady) setMapReady(true);
                    setView(viewIsMap ? "list" : "map");
                    if (!viewIsMap) setFitKey((k) => k + 1);
                  }}
                  aria-pressed={viewIsMap}
                  className="border-border bg-card text-foreground hover:bg-muted/50 inline-flex min-h-11 cursor-pointer items-center gap-1.5 rounded-lg border px-2.5 py-1 text-xs font-semibold transition-colors"
                >
                  {viewIsMap ? (
                    <>
                      <ListIcon className="size-3.5" aria-hidden />
                      <Trans>List</Trans>
                    </>
                  ) : (
                    <>
                      <MapIcon className="size-3.5" aria-hidden />
                      <Trans>Map</Trans>
                    </>
                  )}
                </button>
              )}
            </div>
          </div>

          {/* Tablet map view — only mounts when (768 ≤ w < 1200) AND user
              flipped the controls-bar toggle to "Map". Desktop keeps the
              always-on right column; mobile uses the swipeable view stack
              below. Same prop set as the desktop column. */}
          {tier === "tablet" && viewIsMap && !hideMapToggle && (
            <div
              className="border-border bg-card relative overflow-hidden rounded-xl border"
              style={{ height: "calc(100dvh - var(--chrome-h) - 3rem)" }}
            >
              {mapReady ? (
                <Suspense fallback={<MapSkeleton />}>
                  <SearchMap
                    results={results}
                    highlightedId={highlightedId}
                    onMarkerHover={setHighlightedId}
                    onMarkerClick={(id) => {
                      setHighlightedId(id);
                      const el = document.getElementById(`result-${id}`);
                      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
                    }}
                    center={mapCenter}
                    zoom={mapZoom}
                    fitKey={fitKey}
                    onBoundsChange={handleBoundsChange}
                    flyToTarget={flyTarget}
                    flyKey={flyKey}
                    countryFeatures={mapBoundaryFeatures}
                    nearMeFacility={nearMeMapCenter}
                    nearMeRadiusKm={nearMeRadiusKm}
                    skipResultsAutoFit={hasMapBounds}
                    onLocate={() => applyNearMe(Number(search.distance) || 5)}
                    isLocating={isLocating}
                    onMapClick={() => setHighlightedId(null)}
                    isDark={mapIsDark}
                    radarKey={radarKey}
                  />
                </Suspense>
              ) : (
                <MapSkeleton />
              )}
            </div>
          )}

          {/* Results list — hidden on tablet when the user toggled to Map.
              Dims slightly while a refetch is in flight (old cards stay
              interactive); `.puls-spot-group` + handler = card spotlight. */}
          <div
            className={cn(
              "puls-spot-group space-y-3 transition-opacity duration-300",
              tier === "tablet" && viewIsMap && "hidden",
              showLoadingOverlay && results.length > 0 && "opacity-60",
            )}
            onPointerMove={handleSpotlight}
          >
            {isColdLoad && results.length === 0 ? <ResultCardSkeletonList count={5} /> : null}

            <AnimatePresence mode="popLayout">
              {results.length > 0 ? (
                withPromos(
                  groupedResults
                    ? [
                        ...groupedResults.groups.map((g, i) => (
                          <GroupedProviderCard
                            key={`grp-${g.providerId}`}
                            enterDelay={enterDelayFor(i)}
                            providerId={g.providerId}
                            facilities={g.facilities}
                            isHighlighted={g.facilities.some((f) => f.id === highlightedId)}
                            onHover={handleCardHover}
                            onClick={handleCardClick}
                            onBookClick={handleBookClick}
                          />
                        )),
                        ...groupedResults.nonProviders.map((result, i) => (
                          <ResultCard
                            key={result.id}
                            enterDelay={enterDelayFor(groupedResults.groups.length + i)}
                            result={result}
                            isHighlighted={highlightedId === result.id}
                            onHover={handleCardHover}
                            onClick={handleCardClick}
                            onBookClick={handleBookClick}
                            isInCompare={compare.ids.has(result.id)}
                            canAddToCompare={compare.canAddMore}
                            onToggleCompare={compare.toggle}
                          />
                        )),
                      ]
                    : results.map((result, i) => (
                        <ResultCard
                          key={result.id}
                          enterDelay={enterDelayFor(i)}
                          result={result}
                          isHighlighted={highlightedId === result.id}
                          onHover={handleCardHover}
                          onClick={handleCardClick}
                          onBookClick={handleBookClick}
                          isInCompare={compare.ids.has(result.id)}
                          canAddToCompare={compare.canAddMore}
                          onToggleCompare={compare.toggle}
                        />
                      )),
                )
              ) : showLoadingOverlay ? null : (
                <EmptyStateSuggestions
                  key="empty-state"
                  suggestions={emptyStateSuggestions}
                  variant="compact"
                  onNotifyMe={handleNotifyMe}
                  onStartGuided={handleStartGuided}
                />
              )}
            </AnimatePresence>
          </div>
        </main>

        {/* Right: always-mounted map (≥1200px, skipped for video / home-visit).
             The resize handle lives INSIDE this column (absolutely positioned
             on the left edge, pulled into the grid gap) so we keep a single
             gap between list and map. */}
        {showDesktopMapColumn && (
          <aside
            className="relative self-start md:sticky"
            style={{
              top: "calc(var(--chrome-h) + 1.5rem)",
              height: "calc(100dvh - var(--chrome-h) - 3rem)",
            }}
          >
            <SplitResizeHandle
              containerRef={gridContainerRef}
              onChange={setListSplitPct}
              label={t`Resize panels`}
            />
            <div className="border-border bg-card relative h-full overflow-hidden rounded-xl border">
              {mapReady ? (
                <Suspense fallback={<MapSkeleton />}>
                  <SearchMap
                    results={results}
                    highlightedId={highlightedId}
                    onMarkerHover={setHighlightedId}
                    onMarkerClick={(id) => {
                      setHighlightedId(id);
                      const el = document.getElementById(`result-${id}`);
                      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
                    }}
                    center={mapCenter}
                    zoom={mapZoom}
                    fitKey={fitKey}
                    onBoundsChange={handleBoundsChange}
                    flyToTarget={flyTarget}
                    flyKey={flyKey}
                    countryFeatures={mapBoundaryFeatures}
                    nearMeFacility={nearMeMapCenter}
                    nearMeRadiusKm={nearMeRadiusKm}
                    skipResultsAutoFit={hasMapBounds}
                    onLocate={() => applyNearMe(Number(search.distance) || 5)}
                    isLocating={isLocating}
                    onMapClick={() => setHighlightedId(null)}
                    isDark={mapIsDark}
                    radarKey={radarKey}
                    onMapReady={setDesktopMap}
                  />
                </Suspense>
              ) : (
                <MapSkeleton />
              )}

              {/* Desktop has no peek card on highlight (just marker accent) so
                  we DON'T gate on `!highlightedId` — otherwise hovering any
                  result in the list would hide the button mid-pan. The mobile
                  variant above keeps the gate because the peek sheet slides
                  up from the bottom edge and would overlap the button. */}
              <SearchAreaButton
                visible={mapReady && !!liveBounds && !boundsMatchUrl}
                onClick={commitCurrentBounds}
                edge="top"
                className="top-4"
              />
            </div>
          </aside>
        )}
      </div>

      {/* ─── Card ↔ pin light thread (Puls) — desktop map column only; the
           component itself also requires a fine pointer + motion allowed. */}
      <ResultPinThread
        highlightedId={highlightedId}
        hover={threadHover}
        map={desktopMap}
        enabled={showDesktopMapColumn}
      />

      {/* ─── Mobile: list view with m.drag-x → swipe-left commits to map ─── */}
      {mobileView === "list" && (
        <m.div
          className="relative flex flex-col md:hidden"
          drag={hideMapToggle ? false : "x"}
          dragDirectionLock
          dragConstraints={{ left: 0, right: 0 }}
          dragElastic={0.18}
          dragMomentum={false}
          onDragEnd={(_, info: PanInfo) => {
            if (hideMapToggle) return;
            if (info.offset.x < -60 || info.velocity.x < -500) {
              if (!mapReady) setMapReady(true);
              setView("map");
              setFitKey((k) => k + 1);
            }
          }}
          style={{ touchAction: "pan-y" }}
        >
          {/* No local loading bar / wash — the global <NavigationProgress>
              in the header is the single loading affordance. */}

          {/* List-view controls bar (above the first card). */}
          <div className="border-border/60 relative border-b px-4 pt-2 pb-2">
            {/* Scan line along the bar's bottom border while a search is in
                flight (Puls). Decorative, never intercepts touch. */}
            {showLoadingOverlay && (
              <span aria-hidden className="pointer-events-none absolute inset-x-0 -bottom-px h-0.5">
                <span className="puls-scan block h-full w-full" />
              </span>
            )}
            {/* `min-w-0` + a non-wrapping control group keep the row inside
                375px: the count shrinks/truncates, the 44px controls don't. */}
            <div className="flex min-w-0 items-center justify-between gap-2">
              <span className="text-muted-foreground inline-flex min-w-0 items-center gap-1 truncate text-sm font-medium">
                {showLoadingOverlay && results.length === 0 ? (
                  <Trans>Searching…</Trans>
                ) : (
                  <>
                    <Odometer value={total} locale={i18n.locale} className="text-foreground" />{" "}
                    <Plural value={total} one="result" other="results" />
                  </>
                )}
              </span>
              <div className="flex shrink-0 items-center gap-1.5">
                <FavoritesOnlyToggle
                  value={favoritesOnly}
                  onChange={setFavoritesOnly}
                  count={favoritesInResults}
                />
                <SavedSearchesDropdown
                  onApply={updateParams}
                  onSaveCurrent={() => setSaveSearchOpen(true)}
                />
                <GroupToggle value={groupMode} onChange={setGroup} />
                <SortDropdown
                  value={search.sort ?? "recommended"}
                  onChange={setSort}
                  hasFacility={Boolean(search.city)}
                />
              </div>
            </div>
          </div>

          {/* Results list */}
          <div
            className={cn(
              "puls-spot-group flex-1 space-y-3 px-3 pb-[calc(160px+env(safe-area-inset-bottom))] transition-opacity duration-300",
              showLoadingOverlay && results.length > 0 && "opacity-60",
            )}
            onPointerMove={handleSpotlight}
          >
            {isColdLoad && results.length === 0 ? <ResultCardSkeletonList count={5} /> : null}

            {results.length > 0
              ? withPromos(
                  groupedResults
                    ? [
                        ...groupedResults.groups.map((g, i) => (
                          <GroupedProviderCard
                            key={`grp-${g.providerId}`}
                            enterDelay={enterDelayFor(i)}
                            providerId={g.providerId}
                            facilities={g.facilities}
                            isHighlighted={g.facilities.some((f) => f.id === highlightedId)}
                            onHover={handleCardHover}
                            onClick={handleCardClick}
                            onBookClick={handleBookClick}
                          />
                        )),
                        ...groupedResults.nonProviders.map((result, i) => (
                          <ResultCard
                            key={result.id}
                            enterDelay={enterDelayFor(groupedResults.groups.length + i)}
                            result={result}
                            isHighlighted={highlightedId === result.id}
                            onHover={handleCardHover}
                            onClick={handleCardClick}
                            onBookClick={handleBookClick}
                            isInCompare={compare.ids.has(result.id)}
                            canAddToCompare={compare.canAddMore}
                            onToggleCompare={compare.toggle}
                          />
                        )),
                      ]
                    : results.map((result, i) => (
                        <ResultCard
                          key={result.id}
                          enterDelay={enterDelayFor(i)}
                          result={result}
                          isHighlighted={highlightedId === result.id}
                          onHover={handleCardHover}
                          onClick={handleCardClick}
                          onBookClick={handleBookClick}
                          isInCompare={compare.ids.has(result.id)}
                          canAddToCompare={compare.canAddMore}
                          onToggleCompare={compare.toggle}
                        />
                      )),
                )
              : !showLoadingOverlay && (
                  <EmptyStateSuggestions
                    suggestions={emptyStateSuggestions}
                    variant="full"
                    onNotifyMe={handleNotifyMe}
                    onStartGuided={handleStartGuided}
                  />
                )}
          </div>
        </m.div>
      )}

      {/* ─── Mobile: map view peek card ─── */}
      {mobileView === "map" && (
        <div className="md:hidden">
          <AnimatePresence>
            {highlightedId &&
              (() => {
                const peek = results.find((r) => r.id === highlightedId);
                if (!peek) return null;
                return (
                  <m.div
                    key={peek.id}
                    initial={{ y: 100, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    exit={{ y: 100, opacity: 0 }}
                    transition={{ type: "spring", damping: 25, stiffness: 300 }}
                    className="fixed right-3 left-3 z-[1001] md:hidden"
                    style={{
                      bottom: "calc(3.5rem + max(0.25rem, env(safe-area-inset-bottom)) + 0.5rem)",
                    }}
                  >
                    <ResultCard
                      result={peek}
                      isHighlighted
                      onHover={handleCardHover}
                      onClick={handleCardClick}
                      onBookClick={handleBookClick}
                    />
                  </m.div>
                );
              })()}
          </AnimatePresence>
        </div>
      )}

      {/* ─── Mobile: side swipe handle on the LIST view, anchored RIGHT ─── */}
      {mobileView === "list" && !hideMapToggle && (
        <ViewSwipeHandle
          side="right"
          label={t`Show map`}
          icon={<MapIcon className="text-muted-foreground size-3.5" strokeWidth={2.25} />}
          onClick={() => {
            if (!mapReady) setMapReady(true);
            setView("map");
            setFitKey((k) => k + 1);
          }}
        />
      )}

      {/* ─── Mobile: RIGHT-edge swipe ZONE on the MAP view.
           Width restricted to the bezel (w-3 = 12px) so it does not overlap
           the map control buttons (locate / zoom) which sit at right-3 inside
           the map. Tap-to-list stays available via the visible handle below. */}
      {mobileView === "map" && (
        <m.div
          className="fixed right-0 bottom-0 z-[60] w-3 md:hidden"
          style={{
            top: "var(--chrome-h)",
            touchAction: "pan-y",
          }}
          drag="x"
          dragDirectionLock
          dragConstraints={{ left: 0, right: 0 }}
          dragElastic={0.18}
          dragMomentum={false}
          onDragEnd={(_, info: PanInfo) => {
            if (info.offset.x < -60 || info.velocity.x < -500) {
              setView("list");
            }
          }}
          aria-label={t`Show list`}
        />
      )}

      {/* ─── Mobile: side swipe handle on the MAP view, anchored RIGHT ─── */}
      {mobileView === "map" && (
        <ViewSwipeHandle
          side="right"
          label={t`Show list`}
          icon={<ListIcon className="text-muted-foreground size-3.5" strokeWidth={2.25} />}
          onClick={() => setView("list")}
        />
      )}

      {/* ─── Save-search modal ─── */}
      <SaveSearchModal
        open={saveSearchOpen}
        onClose={() => setSaveSearchOpen(false)}
        defaultName={buildDefaultSaveName({
          search,
          specialties,
          topCities,
          country,
          locale: i18n.locale,
        })}
        filters={buildSavedFilters(search)}
        sortBy={search.sort}
        searchType="all"
        queryText={search.q}
        resultCount={total}
      />

      {/* Provider profile modal — opens the full <ProviderProfile> from
          @pontimed/provider-ui inside <ProviderBottomSheet>. Deep-link via
          ?provider=<id>; the modal normalizes Meili composite ids
          (`doc_<provId>_<facId>`) internally. */}
      {search.provider && (
        <ProviderProfileModal
          open={Boolean(search.provider)}
          onClose={closeProviderSheet}
          providerId={search.provider}
          scrollToBooking={search.book === "1"}
          autoOpenBooking={search.book === "1"}
          initialFacilityId={search.facilityId}
          onProfileResolved={handleProviderProfileResolved}
        />
      )}

      {/* Facility profile modal — replaces the old `/facility/$slug` route.
          Deep-link via ?facility=<id>; the modal extracts the UUID suffix
          from pretty slugs and fetches `/api/facilities/:id`. */}
      {search.facility && (
        <FacilityProfileModal
          open={Boolean(search.facility)}
          onClose={closeFacilitySheet}
          facilityId={search.facility}
          onProfileResolved={handleFacilityProfileResolved}
        />
      )}

      {/* ─── CompareBar + CompareModal — fed by useCompareSelection ─── */}
      <CompareBar
        selected={compare.selection}
        limit={compare.limit}
        onOpen={() => setCompareOpen(true)}
        onRemove={compare.remove}
        onClearAll={compare.clear}
      />
      <CompareModal
        open={compareOpen}
        onClose={() => setCompareOpen(false)}
        results={compare.selection}
        onBook={(r) => {
          setCompareOpen(false);
          handleBookClick(r.id);
        }}
        onViewProfile={(r) => {
          setCompareOpen(false);
          handleCardClick(r.id);
        }}
      />

      {/* ─── Page footer (1:1 legacy) — only shown in list view so the map
           overlay isn't covered. Sits at the bottom of the scrollable
           content container; modals/overlays are portaled and unaffected. */}
      {mobileView !== "map" && <Footer />}

      {/* ─── Mobile bottom tab bar — same as account (Home / Find / Visits / Wallet / Account) ───
           On /find, the Search tab morphs into "Adjust search" (matches the
           hero CTA + the filter modal title) so a re-tap opens the filters
           modal instead of navigating to /find with cleared params. */}
      <BottomTabBar
        className="md:hidden"
        searchTabOverride={{
          icon: SlidersHorizontal,
          label: t`Adjust search`,
          badge: activeFilterCount,
          onClick: () => setFiltersOpen(true),
        }}
      />
    </div>
  );
}

/* ────────── Sub-components ────────── */

function MapSkeleton() {
  return (
    <div className="bg-muted relative h-full min-h-[60vh] w-full overflow-hidden rounded-xl">
      <Skeleton className="absolute inset-0 h-full w-full" />
      {/* Centered spinner overlays the shimmer so the user sees an explicit
          "loading" affordance — Skeleton alone reads as static placeholder
          on slow returns from /account where the map mount is delayed. */}
      <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
        <Loader2 className="text-muted-foreground/70 size-8 animate-spin" aria-hidden />
      </div>
    </div>
  );
}

/* SidebarFilterSection / SectionPill / HeroChoiceChip extracted to
 * components/search/_find/sidebar-primitives.tsx so this orchestrator
 * file stays focused on URL-state + render glue rather than UI primitives.
 * They are pure UI — re-imported above. */

/* ────────── Desktop layout helpers ──────────
   Three-tier viewport model:
   - mobile  (< 768)         single column, list ↔ map via swipe
   - tablet  (768..1199)     filters + list (map column dropped)
   - desktop (≥ 1200)        filters + list + map with draggable split

   The list/map split is persisted to localStorage so the user's preferred
   ratio survives reloads. Filter column is fixed at FILTER_COL_PX. */

const SPLIT_STORAGE_KEY = "pontimed-find-list-map-split-v1";
const SPLIT_DEFAULT = 50;
const SPLIT_MIN = 25;
const SPLIT_MAX = 75;
const FILTER_COL_PX = 280;
const GRID_GAP_PX = 24;

type ViewportTier = "mobile" | "tablet" | "desktop";

function getViewportTier(): ViewportTier {
  if (typeof window === "undefined") return "desktop";
  if (window.innerWidth < 768) return "mobile";
  if (window.innerWidth < 1200) return "tablet";
  return "desktop";
}

function useViewportTier(): ViewportTier {
  const [tier, setTier] = useState<ViewportTier>(getViewportTier);
  useEffect(() => {
    const update = () => setTier(getViewportTier());
    const mqMd = window.matchMedia("(min-width: 768px)");
    const mqXl = window.matchMedia("(min-width: 1200px)");
    mqMd.addEventListener("change", update);
    mqXl.addEventListener("change", update);
    return () => {
      mqMd.removeEventListener("change", update);
      mqXl.removeEventListener("change", update);
    };
  }, []);
  return tier;
}

function useListMapSplit(): readonly [number, (next: number) => void] {
  const [pct, setPct] = useState<number>(() => {
    if (typeof window === "undefined") return SPLIT_DEFAULT;
    try {
      const raw = window.localStorage.getItem(SPLIT_STORAGE_KEY);
      const n = raw ? parseFloat(raw) : NaN;
      if (Number.isFinite(n)) return Math.min(SPLIT_MAX, Math.max(SPLIT_MIN, n));
    } catch {
      /* private mode / quota */
    }
    return SPLIT_DEFAULT;
  });
  const update = useCallback((next: number) => {
    const clamped = Math.min(SPLIT_MAX, Math.max(SPLIT_MIN, next));
    setPct(clamped);
    try {
      window.localStorage.setItem(SPLIT_STORAGE_KEY, String(clamped));
    } catch {
      /* private mode / quota */
    }
  }, []);
  return [pct, update] as const;
}

function SplitResizeHandle({
  containerRef,
  onChange,
  label,
}: {
  containerRef: React.RefObject<HTMLDivElement | null>;
  onChange: (next: number) => void;
  label: string;
}) {
  const onPointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      e.preventDefault();
      const handle = e.currentTarget;
      handle.setPointerCapture(e.pointerId);
      const move = (ev: PointerEvent) => {
        const container = containerRef.current;
        if (!container) return;
        const rect = container.getBoundingClientRect();
        // Two grid gaps (filter→list and list→map) and the filter column
        // come out of the available width before list/map fr units kick in.
        const usable = rect.width - FILTER_COL_PX - GRID_GAP_PX * 2;
        if (usable <= 0) return;
        const xFromList = ev.clientX - rect.left - FILTER_COL_PX - GRID_GAP_PX;
        onChange(Math.round((xFromList / usable) * 100));
      };
      const up = () => {
        try {
          handle.releasePointerCapture(e.pointerId);
        } catch {
          /* already released */
        }
        window.removeEventListener("pointermove", move);
        window.removeEventListener("pointerup", up);
        document.body.style.cursor = "";
        document.body.style.userSelect = "";
      };
      window.addEventListener("pointermove", move);
      window.addEventListener("pointerup", up);
      document.body.style.cursor = "col-resize";
      document.body.style.userSelect = "none";
    },
    [containerRef, onChange],
  );
  return (
    <div
      role="separator"
      aria-orientation="vertical"
      aria-label={label}
      onPointerDown={onPointerDown}
      className="group absolute top-0 bottom-0 z-10 flex w-6 -translate-x-full cursor-col-resize touch-none items-center justify-center"
      style={{ left: 0 }}
    >
      <div className="bg-border-strong group-hover:bg-primary group-active:bg-primary h-12 w-1 rounded-full transition-colors" />
    </div>
  );
}

/* ────────── Active filter pill helpers ────────── */

interface ActivePillsArgs {
  params: SearchSearchParams;
  values: SearchFiltersState;
  specOptions: { value: string; label: string }[];
  serviceOptions: { value: string; label: string }[];
  topCities: CityOption[];
  /** Country catalog from `useCountriesQuery()` — used to render country
   *  pills in the active locale (e.g. "България" instead of "Bulgaria"
   *  when locale=bg). Falls back to the static English COUNTRY_NAMES_EN
   *  when a country isn't in the catalog yet. */
  countryCatalog?: ReadonlyArray<{ code: string; name: Record<string, string> }>;
}

/**
 * Active-filter pills row — 1:1 with legacy search-client.tsx
 * `activeFilterPills` (lines 1140-1380). Every modal toggle shows a pill in
 * the header so the user knows what's active without re-opening the modal.
 *
 * Each pill carries a `remove` updates dict that the parent applies via
 * `updateParams` on click (clearing the underlying URL key, plus any
 * coupled keys — e.g. consultationType=video also clears hasVideo+mode).
 */
function useActivePills({
  params,
  values,
  specOptions,
  serviceOptions,
  topCities,
  countryCatalog,
}: ActivePillsArgs): ActiveFilterPill[] {
  const { t, i18n } = useLingui();

  // Locale-aware country display. Country catalog is the canonical source;
  // COUNTRY_NAMES_EN is a static English fallback for any country missing
  // from the live API response.
  const localizedCountryName = (code: string): string => {
    const upper = code.toUpperCase();
    const entry = countryCatalog?.find((c) => c.code.toUpperCase() === upper);
    if (entry) {
      return (
        entry.name[i18n.locale] || entry.name.en || COUNTRY_NAMES_EN[upper.toLowerCase()] || upper
      );
    }
    return COUNTRY_NAMES_EN[upper.toLowerCase()] ?? upper;
  };
  const verticalPillLabels: Record<NonNullable<SearchFiltersState["vertical"]>, string> = {
    provider: t`Specialist doctor`,
    mental_health: t`Mental health`,
    dental: t`Dentist`,
    physio: t`Physiotherapist`,
    midwife: t`Midwife`,
    nutrition: t`Nutritionist`,
    hospital: t`Hospitals`,
    clinic: t`Clinics`,
    polyclinic: t`Polyclinics`,
    dental_clinic: t`Dental offices`,
    pharmacy: t`Pharmacy`,
    lab: t`Lab`,
    imaging: t`Imaging`,
    optical: t`Optical`,
    homecare: t`Home care`,
    medical_transport: t`Medical transport`,
    equipment: t`Equipment`,
  };
  const pills: ActiveFilterPill[] = [];

  // ── Free-text query (q) ──
  // Surface the active text search as a pill so users see *what* they're
  // searching for after the input is cleared on dropdown close. Removed →
  // wipes `q` only (other filters stay intact). When the system can match
  // a typed query to a specialty (executeSmartSearch / preprocessQuery),
  // it sets `specialties=…` and clears `q` itself — so this pill only
  // appears for free-text queries that didn't resolve to a specialty
  // ("daca nu e specialitate").
  if (params.q && params.q.trim()) {
    pills.push({
      id: `q:${params.q}`,
      label: `"${params.q.trim()}"`,
      remove: { q: null },
    });
  }

  // ── Country & cross-border scope ──
  // Geographical context comes first: current country, then the cross-border
  // scope (per-neighbor pills when scope=cross, single "All countries" pill
  // when scope=global). Removing the country pill resets to DEFAULT_COUNTRY
  // and drops cross-border state. Removing a neighbor pill peels just that
  // one out of the `countries` CSV (and clears scope when the CSV is empty).
  {
    const currentCountry = (params.country ?? DEFAULT_COUNTRY).toUpperCase();
    const lower = currentCountry.toLowerCase();
    const flag = COUNTRY_FLAGS[lower] ?? "";
    const name = localizedCountryName(currentCountry);
    pills.push({
      id: `country:${currentCountry}`,
      label: flag ? `${flag} ${name}` : name,
      remove: {
        country: DEFAULT_COUNTRY.toUpperCase(),
        scope: null,
        countries: null,
      },
    });
  }
  if (params.scope === "global") {
    pills.push({
      id: "scope:global",
      label: t`All countries`,
      remove: { scope: null, countries: null },
    });
  } else if (params.scope === "cross") {
    const selected = (params.countries ?? "")
      .split(",")
      .map((s) => s.trim().toUpperCase())
      .filter(Boolean);
    for (const code of selected) {
      const lower = code.toLowerCase();
      const cFlag = COUNTRY_FLAGS[lower] ?? "";
      const cName = localizedCountryName(code);
      const remaining = selected.filter((c) => c !== code);
      pills.push({
        id: `cross:${code}`,
        label: cFlag ? `${cFlag} ${cName}` : cName,
        remove: {
          countries: remaining.length > 0 ? remaining.join(",") : null,
          scope: remaining.length > 0 ? "cross" : null,
        },
      });
    }
  }

  // ── Vertical (provider type) ──
  // dental → STO, mental_health → PSH (cluster), physio → FIZ, midwife → MOA,
  // nutrition → NUT. Removing the pill also strips the auto-seeded specialty.
  const verticalSpecialtyMap: Partial<Record<NonNullable<SearchFiltersState["vertical"]>, string>> =
    {
      dental: "STO",
      mental_health: "PSH",
      physio: "FIZ",
      midwife: "MOA",
      nutrition: "NUT",
    };
  const linkedSpecialty = values.vertical ? verticalSpecialtyMap[values.vertical] : undefined;
  if (values.vertical) {
    const remove: Record<string, string | null> = {
      tab: null,
      mode: null,
      category: null,
      kind: null,
      includeEmpty: null,
    };
    if (linkedSpecialty) {
      const remaining = values.specialties.filter((c) => c !== linkedSpecialty);
      remove.specialties = remaining.length > 0 ? remaining.join(",") : null;
      remove.specialty = null;
    }
    pills.push({
      id: `vertical:${values.vertical}`,
      label: verticalPillLabels[values.vertical],
      remove,
    });
  }

  // ── Specialties (one pill per code) ──
  // Previously the pill that matched the vertical's linked specialty was
  // skipped to avoid duplicate-conveyance with the vertical pill. Mobile
  // hides the vertical pill (the ModeSwitch + quickbar already show the
  // service type), so that dedup ended up hiding a real user-selected
  // specialty (STO/FIZ/MOA/NUT/PSH). Emit them all; desktop bottom row
  // was removed in a prior pass, so no double-render risk there either.
  for (const code of values.specialties) {
    const match = specOptions.find((o) => o.value === code);
    const remaining = values.specialties.filter((c) => c !== code);
    pills.push({
      id: `specialty:${code}`,
      label: match?.label || code,
      remove: {
        specialties: remaining.length > 0 ? remaining.join(",") : null,
        specialty: null,
      },
    });
  }

  // ── City ──
  if (params.city) {
    const cityId = parseCitySlug(params.city);
    const match = topCities.find((c) => c.id === cityId || String(c.geonameId) === cityId);
    pills.push({
      id: `city:${params.city}`,
      // Localized name from the catalog when known (so BG users see
      // "Пловдив" instead of "Plovdiv"); otherwise titlecase the slug tail
      // so the pill never reveals the raw `id__slug` form.
      label: match ? getCityDisplayName(match, i18n.locale) : cityNameFromSlug(params.city),
      remove: { city: null, nearMe: null, lat: null, lng: null, distance: null },
    });
  }

  // ── Services (one pill per code) ──
  for (const code of values.services) {
    const match = serviceOptions.find((o) => o.value === code);
    const remaining = values.services.filter((c) => c !== code);
    pills.push({
      id: `service:${code}`,
      label: match?.label || code,
      remove: {
        services: remaining.length > 0 ? remaining.join(",") : null,
        service: null,
      },
    });
  }

  // ── Consultation type — accept both "in_person" (modal) and "in_office"
  //    (legacy/Meili) as aliases of the same option. ──
  if (params.consultationType) {
    const labels: Record<NonNullable<SearchSearchParams["consultationType"]>, string> = {
      in_office: t`In person`,
      in_person: t`In person`,
      video: t`Video`,
      home_visit: t`Home visit`,
    };
    const consultRemove: Record<string, string | null> = { consultationType: null };
    if (params.consultationType === "video") {
      consultRemove.hasVideo = null;
    }
    let consultLabel: string = labels[params.consultationType];
    // Home-visit address (set by the modal's home-visit picker) must come off
    // with the consultation pill — orphan `hasHomeVisit/lat/lng/homeCity` URL
    // params after a remove are confusing and survive in shareable links.
    // Show the address inline when present so the pill carries enough info.
    if (params.consultationType === "home_visit") {
      consultRemove.hasHomeVisit = null;
      consultRemove.lat = null;
      consultRemove.lng = null;
      consultRemove.homeCity = null;
      consultRemove.homeVisitCity = null;
      const homeLabel = params.homeCity ?? params.homeVisitCity;
      if (homeLabel) consultLabel = `${consultLabel} · ${homeLabel}`;
    }
    pills.push({
      id: `consultationType:${params.consultationType}`,
      label: consultLabel,
      remove: consultRemove,
    });
  }

  // ── When (time-of-day quick pick + custom range) ──
  // URL is the source of truth; the pill format mirrors what the sidebar /
  // modal show in their collapsed-section summary so users see the same
  // label everywhere.
  if (params.when) {
    const whenLabels: Record<NonNullable<SearchSearchParams["when"]>, string> = {
      now: t`Now`,
      today: t`Today`,
      tomorrow: t`Tomorrow`,
      this_week: t`This week`,
      custom: t`Pick a date`,
    };
    let whenLabel: string = whenLabels[params.when];
    if (params.when === "custom" && params.dateFrom && params.dateTo) {
      try {
        const fmt = new Intl.DateTimeFormat(i18n.locale, {
          day: "numeric",
          month: "short",
        });
        whenLabel = `${fmt.format(new Date(params.dateFrom))} – ${fmt.format(new Date(params.dateTo))}`;
      } catch {
        /* malformed dates — fall back to the static "Pick a date" label */
      }
    }
    pills.push({
      id: `when:${params.when}`,
      label: whenLabel,
      remove: { when: null, dateFrom: null, dateTo: null },
    });
  }

  // ── Insurance ──
  if (params.insurance && params.insurance !== "all") {
    const labels: Record<InsuranceFilter, string> = {
      all: "",
      cas: t`Public (CAS)`,
      private: t`Private insurance`,
      direct: t`Direct payment`,
    };
    pills.push({
      id: `insurance:${params.insurance}`,
      label: labels[params.insurance],
      remove: { insurance: null },
    });
  }

  // ── Near me (geolocation + radius) ──
  if (params.nearMe === "true") {
    const distance = params.distance;
    pills.push({
      id: "nearby",
      label: distance ? t`Near me (${distance} km)` : t`Near me`,
      remove: { nearMe: null, lat: null, lng: null, distance: null },
    });
  }

  // ── Rating min ──
  if (params.ratingMin) {
    pills.push({
      id: `ratingMin:${params.ratingMin}`,
      label: `${params.ratingMin}+ ★`,
      remove: { ratingMin: null },
    });
  }

  // ── Verified ──
  if (params.isVerified === "true") {
    pills.push({
      id: "isVerified",
      label: t`Verified`,
      remove: { isVerified: null },
    });
  }

  // ── Gender ──
  if (params.gender) {
    pills.push({
      id: `gender:${params.gender}`,
      label: params.gender === "F" ? t`Female` : t`Male`,
      remove: { gender: null },
    });
  }

  // ── Languages (one pill per code) ──
  if (values.languages.length > 0) {
    // Use the active app locale (Lingui) — not the browser default — so the
    // language names follow the locale switcher, matching the rest of the UI.
    let displayName: (code: string) => string = (code) => code.toUpperCase();
    try {
      const dn = new Intl.DisplayNames([i18n.locale], { type: "language" });
      displayName = (code) => dn.of(code) ?? code.toUpperCase();
    } catch {
      // fall back
    }
    for (const lang of values.languages) {
      pills.push({
        id: `lang:${lang}`,
        label: displayName(lang),
        remove: {
          languages: values.languages.filter((l) => l !== lang).join(",") || null,
        },
      });
    }
  }

  // ── Audience tags ──
  if (values.audience.length > 0) {
    const audienceLabels: Record<string, string> = {
      children: t`Children`,
      adults: t`Adults`,
      elderly: t`Elderly`,
      pregnant: t`Pregnancy`,
      lgbtq_friendly: t`LGBTQ+ friendly`,
    };
    for (const tag of values.audience) {
      pills.push({
        id: `audience:${tag}`,
        label: audienceLabels[tag] ?? tag,
        remove: {
          audience: values.audience.filter((a) => a !== tag).join(",") || null,
        },
      });
    }
  }

  // ── Price range (combined min/max into one pill) ──
  if (values.minPrice != null || values.maxPrice != null) {
    const lo = values.minPrice ?? 0;
    const hi = values.maxPrice ?? "∞";
    pills.push({
      id: "price",
      label: `${lo}–${hi} RON`,
      remove: { minPrice: null, maxPrice: null },
    });
  }

  // ── Facility amenities (mirror of modal's accessibility + comfort sections) ──
  const amenityPills: Array<{ param: keyof SearchSearchParams; label: string }> = [
    { param: "hasWheelchairAccess", label: t`Wheelchair` },
    { param: "hasElevator", label: t`Elevator` },
    { param: "hasAccessibleRestroom", label: t`Accessible restroom` },
    { param: "hasParking", label: t`Parking` },
    { param: "hasPublicTransport", label: t`Transit nearby` },
    { param: "hasChildrenArea", label: t`Kids area` },
    { param: "hasBreastfeedingArea", label: t`Breastfeeding area` },
    { param: "hasAirConditioning", label: t`AC` },
    { param: "hasWifi", label: t`Wi-Fi` },
  ];
  for (const { param, label } of amenityPills) {
    if (params[param] === "true") {
      pills.push({
        id: `amenity:${String(param)}`,
        label,
        remove: { [param]: null },
      });
    }
  }

  // ── Business-vertical toggles ──
  const businessPills: Array<{ param: keyof SearchSearchParams; label: string }> = [
    { param: "isOpenNow", label: t`Open now` },
    { param: "is24h", label: t`24h` },
    { param: "hasDelivery", label: t`Delivery` },
    { param: "hasHomeCollection", label: t`Home sample collection` },
  ];
  for (const { param, label } of businessPills) {
    if (params[param] === "true") {
      pills.push({
        id: `business:${String(param)}`,
        label,
        remove: { [param]: null },
      });
    }
  }

  // Defensive: drop any pill whose label resolved to falsy/whitespace —
  // prevents "ghost" X-only pills from rendering when a filter value goes
  // through validateSearch into the URL but has no matching label entry
  // (e.g. an unknown consultationType value).
  return pills.filter((p) => typeof p.label === "string" && p.label.trim().length > 0);
}

function buildSavedFilters(search: SearchSearchParams): Record<string, unknown> {
  const filters: Record<string, unknown> = {
    country: (search.country ?? DEFAULT_COUNTRY).toUpperCase(),
  };
  if (search.specialty) filters.specialty = search.specialty;
  if (search.service) filters.service = search.service;
  if (search.city) filters.city = search.city;
  if (search.insurance && search.insurance !== "all") filters.insurance = search.insurance;
  if (search.consultationType) filters.consultationType = search.consultationType;
  if (search.ratingMin) filters.ratingMin = search.ratingMin;
  return filters;
}

interface BuildDefaultNameArgs {
  search: SearchSearchParams;
  specialties: SpecialtyOption[];
  topCities: CityOption[];
  country: string;
  locale: string;
}

function buildDefaultSaveName({
  search,
  specialties,
  topCities,
  country,
  locale,
}: BuildDefaultNameArgs): string {
  const parts: string[] = [];
  if (search.specialty) {
    const match = specialties.find((s) => s.code === search.specialty);
    parts.push(match ? getSpecialtyDisplayName(match, locale) : search.specialty);
  }
  if (search.city) {
    const cityId = parseCitySlug(search.city);
    const city = topCities.find((c) => c.id === cityId || String(c.geonameId) === cityId);
    if (city) parts.push(city.asciiName);
  }
  if (parts.length === 0) parts.push(country);
  return parts.join(" — ");
}
